/* eslint-disable @typescript-eslint/no-explicit-any */
import * as THREE from 'three';
import {
  Canvas3D,
  type Canvas3DOptions,
  type CanvasSection,
  type CanvasTunnel,
} from './canvas3d';

/**
 * Phase 6c1 — AHU board GEOMETRY, first half. Faithful port of the draw
 * pipeline of django/ahu/static/ahu/mvc/view/canvas/canvas.view3d.js (the
 * G-classes below) plus the render layer of view/canvas/tunnel.js that feeds
 * it. Every ported block cites its legacy line numbers.
 *
 * G-classes in this file (PHASE6_AHUBOARD_INVENTORY.md Part 5 §41):
 *  - G1  section cube + edge wireframe   (drawSection :1297 → drawCube :1426
 *                                         → drawSectionComplete :2426)
 *  - G2  section name labels             (createTextLabel :3922,
 *                                         calculateSectionName :4857,
 *                                         updateSectionName :4798)
 *  - G3  interior casing cube            (drawCasing :1375 — ported here
 *                                         because drawCube embeds it; its
 *                                         accessory-offset consumers are 6c2)
 *  - G4  tunnel under-insulation casing  (drawTunnelCasing :1358)
 *  - G5  tunnel wireframe                (drawTunnelWireframe :4657)
 *  - G6  tunnel base assembly            (drawTunnelBase :3275 → createBase
 *                                         :5272, tube :5302 / c_channel :5335)
 *  - G22 wireframe dash rules            (makeLineDashed :4943 — consumers
 *                                         arrive with accessories, 6c2)
 *  - G23 dimension primitives            (drawLines :4003 … createGroup :4038,
 *                                         remove_dimensions :4688)
 *  - G24 tunnel width dimension          (drawTunnelWidth :4165)
 *  - G25 tunnel height dimension         (drawTunnelHeight :4217)
 *  - G26 tunnel base-height dimension    (drawTunnelBaseDimensions :4385)
 *  - G27 AHU overall-height dimension    (drawAHUHeight :4600)
 *  - G28 tunnel + per-section lengths    (drawLength :4278, orchestrated by
 *                                         drawTunnelDimensions :4574)
 *  - G30 per-view dimension/label router (getTextPosition :4967)
 *  - G33 airflow arrows + stream labels  (drawAirflowDirectionChangingArrow
 *                                         :5474, tube :5537, head :5621,
 *                                         createArrow :5692, position :5402,
 *                                         data :5425, orientation :5378)
 *  - G37 calculateCubePositions :3679 (incl. grid Y §40)
 *  - G38 setSectionPosition :3614
 *  - G39 disposal subset (destroySection :2991, removeSectionObjFromScene
 *         :3062, disposeAndRemoveTunnelBase :2984, disposeDetails :3570)
 *  - §40 global X recentering (drawAllTunnelsWidthAndHeight :4148)
 *
 * Phase 6c2 — GEOMETRY second half, now IN this file (the 6c1 markers are
 * replaced by the real calls):
 *  - G7-G9  lug styles                  (drawLiftingLug :1549, drawWeldOnLug
 *                                        :1630, drawFivePlateLug :1756 +
 *                                        plates :1839/:1885/:1923)
 *  - G10    lug positioning/selection   (drawLug :3207, positioners
 *                                        :3334/:3401/:3483, updateLugStyles
 *                                        :3141, adjoining-base :1782/:1805,
 *                                        redrawTunnelLug(s) :1528/:1533,
 *                                        drawTunnelBase replay :3322-3331)
 *  - G11    internal walls              (drawInternalWall :1984, positioners
 *                                        :2035/:2048, redrawIntWalls :2064,
 *                                        getDistanceAfterRotation :507)
 *  - G12-G21 accessories                (drawAccessory dispatch :1324, maps
 *                                        :269-323, rectangles :1491/:2619,
 *                                        viewports :2071/:2096, cylinders
 *                                        :1962/:2671, cubes :2170/:2701,
 *                                        blades :2798/:2815, cube casing
 *                                        :2850, VFD rects :2109, dog-house
 *                                        base :5261, hoods :2292/:2765 +
 *                                        PrismGeometry :422, drain pans
 *                                        :2363/:2870, openings :2228/:2243,
 *                                        position tables :3731-3920,
 *                                        destroyAccessory :3046, the
 *                                        staging/PREVIEW merge :2566-2585)
 *  - G31/G32 coil/evap piping           (drawCoilConnections :5077 + pipes
 *                                        :5130-5212, drawEvapPackConnections
 *                                        :5231, caches + replay :2587-2594)
 *  - G34    tint state machine          (selectDeselectSection :1181,
 *                                        updateSectionStyles :1205,
 *                                        deselectAllActiveSections :1222,
 *                                        hover_scene_section :4700,
 *                                        changeAccessoryColor family
 *                                        :4727-4788, getAccessoryColors :5726)
 *  - R16/D4 accessory-preview mini-canvas factory
 *                                        (fn['create.view.canvas.for.accessory']
 *                                        fn/map.js:1137-1151 → exported
 *                                        createAccessoryPreviewCanvas, now in
 *                                        canvas3dInteraction.ts)
 *
 * Hover/select INPUT wiring (raycast picking, onSectionAction :876) stays 6d;
 * the G34 machine here is driven by the selection slice + the bus seams the
 * 6d picker will feed. Error-tint INPUT (§22.5 error-log maps) is 6e — the
 * `accessoriesWithError`/`sectionsWithError` consumers are live with the
 * documented payload shapes.
 *
 * r110 pin is LOAD-BEARING (§42): BoxBufferGeometry / PlaneBufferGeometry /
 * TextBufferGeometry / Font / Geometry-class ExtrudeGeometry +
 * BufferGeometry.fromGeometry are used exactly as censused. Do NOT modernize.
 *
 * Backbone-model surface: the ported math reads `model.get('x')`, `.id`,
 * `.attributes`, mutable stamps (`order`, `before`, `after`, `reverse`,
 * `dual_section_component`) and per-tunnel section collections. The
 * CanvasTunnelModel / CanvasSectionModel adapters below reproduce that
 * surface over the plain REST rows so the ported blocks stay line-for-line
 * comparable with the legacy file.
 */

// ---------------------------------------------------------------------------
// Module constants (canvas.view3d.js module scope — §3.2.1 tables, verbatim).
// ---------------------------------------------------------------------------
/**
 * Legacy drawSection/drawCube default `color` arg — dead THERE (drawCube
 * overwrites it from getSectionColors :1432-1435), but LIVE as the accessory
 * fill fallback (`accessoriesColors[name] || color`, :1496/:1964/:2201), the
 * int-wall metal fill (:1987) and changeAccessoryColor's last-resort passive
 * (:4743).
 */
const sectionMaterialColor = 0xdadada;
const sectionMaterialColorHovered = 0x9bb3d7; // :42 — lug hover tint
const sectionMaterialColorActive = 0x3867af; // :43 — lug/section select tint
const errorColor = 0xd6a0a0; // :39 — int-wall fill w/o metal sheet
const errorColorSelected = 0xa72121; // :40 — int-wall wireframe w/o metal sheet
const wireframeMaterialColorAccessory = 0x000000;
const wireframeMaterialColor = 0x9f9e9e;
const wireframeMaterialColorActive = 0x3867af;
const wireframeMaterialColorHovered = 0xb0bcd0; // :47 — onSectionAction hover wireframe (6d picker)
void wireframeMaterialColorHovered; // referenced by the 6d picking port (onSectionAction :886)
const tunnelMaterialColor = 0xbdad7e;
const dimensionsColor = 0x757575;
const dimensionsNumberColor = 0x365736;
const wallColor = 0xbd9d7e;
const lugThickness = 0.25; // :55 — tri_plane/hat_channel plate thickness (inches)
/** §3.2.1 accessoriesColors (:60-73) — DAMPER/LOUVER intentionally absent (commented in legacy). */
const accessoriesColors: Record<string, number> = {
  OPENING: 0xf0f0f0,
  ACCESS_DOOR: 0xbdad7e,
  LIGHT: 0xffbf00,
  LIGHT_LINEAR: 0xffbf00,
  VFD: 0x828282,
  ADP_GAGE: 0x828282,
  JBOX: 0x828282,
  DISCONNECT: 0x828282,
  ENCLOSURE: 0x828282,
  DOG_HOUSE: 0xdadada,
};
/** §3.2.1 `colors` table — section fills. [NEG]#2: only `default` is reachable. */
const colors = {
  default: { passive: 0xdadada, hover: 0x9bb3d7, selected: 0x3867af },
  warning: { passive: 0xe6b78d, hover: 0xffbf80, selected: 0xd68b47 },
  error: { passive: 0xd6a0a0, hover: 0xd57272, selected: 0xa72121 },
};
/**
 * §3.2.1 `acc_colors` (:91-106) — accessory tint palettes (G34, LIVE unlike
 * the section table). `default` has no `passive`: changeAccessoryColor falls
 * back to accessoriesColors[name] || sectionMaterialColor (:4743).
 */
const acc_colors: Record<string, { passive?: number; hover: number; selected: number }> = {
  default: { hover: 0x3867af, selected: 0x3867af },
  warning: { passive: 0xe6b78d, hover: 0xffbf80, selected: 0xd68b47 },
  error: { passive: 0xd6a0a0, hover: 0xd57272, selected: 0xa72121 },
};

// ---------------------------------------------------------------------------
// Backbone-model adapters (tunnel.js reads `tunnels.models` / `sections[id]`
// AMD globals; the React canvas rebuilds these wrappers from the plain rows
// at the start of every render pass — syncCollections()).
// ---------------------------------------------------------------------------

export class CanvasSectionModel {
  row: CanvasSection;
  tunnel: CanvasTunnelModel;
  /** Transient render-layer stamps (tunnel.js:495-502). */
  order = 0;
  before = 0;
  after = 0;
  dual_section_component: any = null;
  /** api.js create-flow stamp — null on boot (see CanvasSection doc). */
  name_on_canvas: string | null;
  /** Legacy fan model stamp (canvas.view3d:5397) — no consumer in 6c1. */
  need_redraw = false;
  /**
   * G29 stamps — re-home of tunnel.js section_dmount (:319-335): the legacy
   * derives these from each section's DMount collection on sync and persists
   * them on the Backbone model; the wrappers re-derive from the §20.4 #38
   * rows on every rebuild (same steady-state result). `horizontal_dmounts`
   * stays null while the rows are in flight (legacy pre-listener state — the
   * drawSectionComplete replay is gated on truthiness, :2558).
   */
  dmounts: any[] = [];
  horizontal_dmounts: any[] | null = null;

  constructor(row: CanvasSection, tunnel: CanvasTunnelModel) {
    this.row = row;
    this.tunnel = tunnel;
    this.name_on_canvas = row.name_on_canvas;
    if (row.dmounts != null) {
      const vertical: any[] = [];
      const horizontal: any[] = [];
      row.dmounts.forEach((dmount) => {
        if (!dmount.plane_id) return; // tunnel.js:323 — plane-less rows skipped
        const item = {
          dmount_id: dmount.id,
          dist_from: dmount.dist_from,
          casing_insulation_thick: row.casing_insulation_thick || 0,
        };
        if (dmount.plane_id == 2) horizontal.push(item);
        else vertical.push(item);
      });
      this.dmounts = vertical.filter((el) => el).sort((a, b) => a.dist_from - b.dist_from);
      this.horizontal_dmounts = horizontal;
    }
  }

  get id(): number {
    return this.row.id;
  }

  /** Backbone `.attributes` alias used by drawSection (:1301) and naming. */
  get attributes(): CanvasSection {
    return this.row;
  }

  /**
   * Legacy section models carry `tunnel_id` as a stamped instance property
   * (section/model/section.js) — drawAccessory's floor-construction Y lift
   * reads it (canvas.view3d:1330).
   */
  get tunnel_id(): number {
    return this.tunnel.id;
  }

  get(key: string): any {
    if (key === 'id') return this.row.id;
    return (this.row as any)[key];
  }
}

export class CanvasTunnelModel {
  row: CanvasTunnel;
  sections: CanvasSectionModel[];
  /** drawTunnelDimensions stamp (:4575) — stacked upper tunnel mirror flag. */
  reverse = false;

  constructor(row: CanvasTunnel) {
    this.row = row;
    this.sections = row.sections.map((s) => new CanvasSectionModel(s, this));
  }

  get id(): number {
    return this.row.id;
  }

  get attributes(): any {
    return this.row;
  }

  get current_index_values(): [number, number] {
    return this.row.current_index_values;
  }

  get(key: string): any {
    if (key === 'id') return this.row.id;
    return (this.row as any)[key];
  }

  /**
   * Legacy tunnel models hold a fetched Floor model (`this.floor = new
   * Floor({id: floor_id})`, model/tunnel.js:80). The single canvas read is
   * `tunnel.floor.get('under_insulation_label')` in isCube (:1480) — re-homed
   * to the bootstrap floor fetch (CanvasTunnel.floor_under_insulation_label).
   */
  get floor(): { get(key: string): any } {
    return {
      get: (key: string) =>
        key === 'under_insulation_label'
          ? (this.row as any).floor_under_insulation_label
          : undefined,
    };
  }

  /** tunnel.toJSON() consumer: drawTunnelWireframe (tunnel.js:523). */
  toJSON(): { id: number; width: number; height: number; length: number; base: any } {
    return {
      id: this.row.id,
      width: this.row.width,
      height: this.row.height,
      length: this.row.length,
      base: this.row.base,
    };
  }
}

// ---------------------------------------------------------------------------
// Options — the §3.5 DOM couplings of the 6c1 surfaces, re-homed as hooks.
// ---------------------------------------------------------------------------
export interface Canvas3DGeometryOptions extends Canvas3DOptions {
  /**
   * viewdata `selected_sections` membership read (tunnel.js:562/577) —
   * selection slice re-home. Drives the `active` cube tint at draw time.
   */
  getSelectedSectionIds(): number[];
  /**
   * `ahu.constructionClass.find(id == construction_class_id)
   *   ?.floor_construction_id` (tunnel.js:492) — bootstrap data re-home (R15).
   */
  getFloorConstructionId(): number | null | undefined;
  /**
   * dispatcher.trigger re-home for canvas-published events:
   * `select:section` (first-section auto-select, canvas.view3d:1430) and
   * `change:casing:section_<id>` (isCasingWidthChanged :2424).
   */
  emitBus(event: string, ...args: unknown[]): void;
  /**
   * 360-availability re-home (6b verifier finding 1). Legacy keeps TWO
   * separate jQuery classes on `.auto_360.li_auto_360.with_window` /
   * `.views_list_li.auto_360_li`:
   *   - `disable_rotate` — cleared by EVERY drawTunnelBase path
   *     (canvas.view3d:3281/:3328/:3330);
   *   - `disable` — cleared by the change:tunnel:dimensions handler
   *     (tunnel.js:228) and destroySection (canvas.view3d:3017).
   * [NEG] dedrift (documented): nothing in the canvas ever ADDS either class
   * (the adders live in panel views outside the 6c scope), so the observable
   * canvas-side behavior is "any draw pass makes 360 available". React folds
   * both clears into one rotateEnabled=true signal; the `kind` is reported so
   * the panel port (6e) can split them again if its adders need to.
   */
  onRotateAvailabilityClear(kind: 'disable' | 'disable_rotate'): void;
  /**
   * G7-G10 — LugCollection re-home (`LugCollection.getModelByTunnelId(id)`,
   * collection/tunnel.lugs.js): the per-tunnel lug placement model
   * (`positions[]` + the pane-owned `hide_lugs` flag). null = not fetched /
   * no model (legacy boot state until tab.lifting.lug's per-tunnel fetches
   * land — fn map `current.ahu.tunnel.lugs` starts EMPTY).
   */
  getTunnelLugs(tunnelId: number): { positions: any[]; hide_lugs?: boolean } | null;
  /**
   * viewdata `current_section` read (deselectAllActiveSections,
   * canvas.view3d:3041/:1223) — selection slice re-home.
   */
  getCurrentSectionId(): number | null;
  /**
   * G34 changeAccessoryColor DOM probes (:4749-4751), §3.5 re-home — the
   * legacy syncs `selectedAccessory` from the left panel:
   *  - isAccessoryRowCollapsed ⇔ `li#forms_accessory_<id> input:not(:checked)
   *    + label i.accessory-expand` matched (row exists, collapsed);
   *  - isAccessoriesTabActive ⇔ `div#forms_accessories_<sec>.active` matched.
   * Until the 6e accessories tab lands both default to false (panel absent ⇒
   * neither selector matches in legacy either ⇒ selectedAccessory untouched).
   */
  isAccessoryRowCollapsed?(accessoryId: number): boolean;
  isAccessoriesTabActive?(sectionId: number): boolean;
  /**
   * updateAccessoriesColor's `#massage_error-item[data-id=<sec>]` probe
   * (:4777, §3.5) — true while the error log still shows a row for the
   * section. Defaults false (no error log yet ⇒ row vanished ⇒ release the
   * error tint) — 6e wires it to the validation slice.
   */
  hasErrorLogRowForSection?(sectionId: number): boolean;
  /**
   * selectDeselectSection's accessory-radio click-through (:1196-1203 —
   * `li#forms_accessory_<id> input[name="accessory_activity"]).click()`):
   * collapses the open accessory row when its section is deselected. 6e
   * panel re-home; no-op default.
   */
  onAccessoryActivityToggle?(accessoryId: number): void;
}

/**
 * Canvas3D + the 6c1 geometry pipeline. Construct THIS class on the board
 * (AhuCanvas); the 6b base remains the bootstrap/camera contract.
 */
export class Canvas3DGeometry extends Canvas3D {
  getSelectedSectionIds: () => number[];
  getFloorConstructionId: () => number | null | undefined;
  emitBus: (event: string, ...args: unknown[]) => void;
  onRotateAvailabilityClear: (kind: 'disable' | 'disable_rotate') => void;
  getTunnelLugs: (tunnelId: number) => { positions: any[]; hide_lugs?: boolean } | null;
  getCurrentSectionId: () => number | null;
  isAccessoryRowCollapsed: (accessoryId: number) => boolean;
  isAccessoriesTabActive: (sectionId: number) => boolean;
  hasErrorLogRowForSection: (sectionId: number) => boolean;
  onAccessoryActivityToggle: (accessoryId: number) => void;

  // --- G12-G21 dispatch maps (canvas.view3d:269-312) ---
  accessoryShapesType: Record<string, string> = {
    OPENING: 'rectangle',
    ACCESS_DOOR: 'rectangle',
    LIGHT: 'cylinder',
    LIGHT_LINEAR: 'cube',
    ADP_GAGE: 'cylinder',
    DAMPER: 'cube',
    LOUVER: 'cube',
    VFD: 'vfd', // dev-sync: dedicated bank/enclosure handler (canvas.view3d delta @274)
    ENCLOSURE: 'cube',
    DISCONNECT: 'cube',
    JBOX: 'cube',
    HOOD: 'hood',
    DRAIN_PAN: 'drain_pan',
    DOG_HOUSE: 'cube',
  };
  shapesFunctionality!: Record<string, { update: any; draw: any; complete: any }>;
  /** G7-G10 style dispatch maps (canvas.view3d:314-323). */
  lugStyles!: Record<string, (details: any, cb: (r: any) => void) => void>;
  lugsPositioning!: Record<string, (shape: any, cube: any, height: any, options: any) => void>;

  // --- G34 tint state (canvas.view3d instance fields) ---
  hover_section: any = null;
  hoveredLug: any = null;
  hovered_accessory: number | string | null = null;
  selectedAccessory: number | string | null = null;
  section_with_error: number | string | null = null;
  erroeMessages: any = null; // sic — legacy field name (§22.5), load-bearing for grep parity.

  // --- R16 preview staging channel (written by the accessory selector's
  // _updateCanvasPreview, Part 4 §33.8; read by the replay merge :2566-2585
  // and the draw:accessory handler tunnel.js:255-272) ---
  previewAccessoryStagingData: Record<string, any> | null = null;
  previewMounting: Record<string, any> | null = null;

  /** Mirrors of the legacy AMD collection globals (`tunnels` / `sections`). */
  tunnelModels: CanvasTunnelModel[] = [];
  sectionsByTunnel: Record<string, CanvasSectionModel[]> = {};

  /** tunnel.js's OWN sectionCanvasObjects bookkeeping list (:580/:590). */
  tunnelViewSectionObjects: Array<{ section: CanvasSectionModel; tunnel: CanvasTunnelModel }> = [];
  /** tunnel.js dmount cache (G29 — section_dmount :336-339, refilled per sync). */
  sections_dmounts: Record<number, any> = {};
  /** tunnel.js dmount debounce timers (:94 — 300 ms per tunnel/section). */
  dmount_timer: Record<string | number, ReturnType<typeof setTimeout>> = {};
  /** drawDmountLength short-segment stagger memory (canvas.view3d:4498-4500). */
  dmount_order: number | null = null;

  // --- draw-pass scratch fields (canvas.view3d instance fields) ---
  /** First-section auto-select latch (drawCube :1427-1431; armed by 6d). */
  need_select_first = false;
  selectFirstSection = false;
  /** drawLength short-label stagger memory (:4298-4300). */
  section_order: number | null = null;
  /** drawTunnelWidth cascade memory (:4207-4215). */
  tunnel_max_length: number | null = null;
  /** drawAllTunnelsWidthAndHeight stamp (:4153). */
  max_x = 0;
  /** calculateCubePositions stamp (:3698) — no 6c1 consumer, kept for parity. */
  firstSectionLength = 0;
  /** True once the boot render ran — gates the React data-change redraws. */
  hasBooted = false;

  constructor(options: Canvas3DGeometryOptions) {
    super(options);
    this.getSelectedSectionIds = options.getSelectedSectionIds;
    this.getFloorConstructionId = options.getFloorConstructionId;
    this.emitBus = options.emitBus;
    this.onRotateAvailabilityClear = options.onRotateAvailabilityClear;
    this.getTunnelLugs = options.getTunnelLugs;
    this.getCurrentSectionId = options.getCurrentSectionId;
    this.isAccessoryRowCollapsed = options.isAccessoryRowCollapsed ?? (() => false);
    this.isAccessoriesTabActive = options.isAccessoriesTabActive ?? (() => false);
    this.hasErrorLogRowForSection = options.hasErrorLogRowForSection ?? (() => false);
    this.onAccessoryActivityToggle = options.onAccessoryActivityToggle ?? (() => undefined);

    // shapesFunctionality (canvas.view3d:286-312). The legacy binds rectangle/
    // cube/hood/drain_pan handlers but leaves cylinder's unbound (called
    // through `?.draw(...)` so `this` still resolves via the call site —
    // drawAccessory invokes `.call(this, …)`-equivalents); here every entry is
    // bound for uniform safety (behavior identical).
    this.shapesFunctionality = {
      rectangle: {
        update: this.updateRectangle.bind(this),
        draw: this.drawRectangle.bind(this),
        complete: this.drawRectangleComplete.bind(this),
      },
      cylinder: {
        update: this.updateCylinder.bind(this),
        draw: this.drawCylinder.bind(this),
        complete: this.drawCylinderComplete.bind(this),
      },
      cube: {
        update: this.updateAccessoriesCube.bind(this),
        draw: this.drawAccessoriesCube.bind(this),
        complete: this.drawAccessoriesCubeComplete.bind(this),
      },
      hood: {
        update: this.updateAccessoriesHood.bind(this),
        draw: this.drawAccessoriesHood.bind(this),
        complete: this.drawAccessoriesHoodComplete.bind(this),
      },
      drain_pan: {
        update: this.updateDrainPan.bind(this),
        draw: this.drawDrainPan.bind(this),
        complete: this.drawDrainPanComplete.bind(this),
      },
      // dev-sync (canvas.view3d delta @309): VFD bank drawing — the cube draw
      // builds the first box, drawVFDComplete places the bank + enclosure.
      vfd: {
        update: this.updateVFD.bind(this),
        draw: this.drawAccessoriesCube.bind(this),
        complete: this.drawVFDComplete.bind(this),
      },
    };
    // lugStyles / lugsPositioning (canvas.view3d:314-323).
    this.lugStyles = {
      single_plate: this.drawLiftingLug.bind(this),
      tri_plane: this.drawWeldOnLug.bind(this),
      hat_channel: this.drawFivePlateLug.bind(this),
    };
    this.lugsPositioning = {
      single_plate: this.calculateLugPosition.bind(this),
      tri_plane: this.calculateWeldPosition.bind(this),
      hat_channel: this.calculateFivePlateLugPosition.bind(this),
    };
  }

  /**
   * Rebuild the collection mirrors from the live rows. Called at the start of
   * every render pass — the legacy equivalents are persistent Backbone
   * collections; id-keyed registry comparisons make fresh wrappers safe.
   */
  syncCollections(): void {
    this.tunnelModels = this.getTunnels().map((row) => new CanvasTunnelModel(row));
    this.sectionsByTunnel = {};
    this.tunnelModels.forEach((t) => {
      this.sectionsByTunnel[t.id] = t.sections;
      // G29 cache re-fill (tunnel.js section_dmount :336-339): only sections
      // whose dmount rows have LOADED get an entry — get_dmounts_position then
      // sees the legacy pre-listener state (single full segment → no dims)
      // for the rest.
      t.sections.forEach((s) => {
        if (s.horizontal_dmounts != null) {
          this.sections_dmounts[s.id] = { horizontal: s.horizontal_dmounts, dmounts: s.dmounts };
        }
      });
    });
  }

  /**
   * tunnel.js dmount re-schedule idiom (:138-139/:201-202/:212-213/:223-224/
   * :248-249/:599-600/:643-644): clear + 300 ms debounce per tunnel. The
   * timer re-resolves the tunnel wrapper at fire time — the legacy closures
   * capture persistent Backbone models; ours are rebuilt per render pass.
   */
  scheduleDmountsPosition(tunnelId: number): void {
    if (this.dmount_timer[tunnelId]) clearTimeout(this.dmount_timer[tunnelId]);
    this.dmount_timer[tunnelId] = setTimeout(() => {
      const tunnel = this.tunnelModels.find((t) => t.id === tunnelId);
      if (tunnel) this.get_dmounts_position(tunnel);
    }, 300);
  }

  // =========================================================================
  // tunnel.js RENDER LAYER (§3.1)
  // =========================================================================

  /**
   * render_canvas (tunnel.js:448-471). The jQuery chrome toggles (`.empty`,
   * #pallete-wrapper / #canvas_toolbar / #canvas-current-view hide/show,
   * #kinetix_bot_button classes) are re-homed to React state derived from the
   * same `!tunnels[0]?.length` gate (BoardContent, §3.5) — only the geometry
   * work lives here.
   */
  render_canvas(): void {
    this.syncCollections();
    if (!this.tunnelModels[0]?.get('length')) {
      this.hasBooted = true;
      return; // empty board — React renders the onboarding card (parity 16).
    }
    const { max_length, max_width } = this.getAhuSizes();
    this.tunnelModels.forEach((tunnel, index) => {
      this.render_canvas_tunnel(tunnel, false, index + 1, max_length, max_width);
      // G29 boot pass — re-home of the tunnel.js init dmountListener cascade
      // (:96-98 → section_dmount :340-343): every section listener lands on
      // the same per-tunnel 300 ms get_dmounts_position debounce.
      this.scheduleDmountsPosition(tunnel.id);
    });
    this.tunnels_length = this.tunnelModels.length;
    // $('#canvas_section canvas')?.focus() (:468) — renderer.domElement IS
    // that canvas.
    this.renderer.domElement.focus();
    this.checkTunnelsLength(true);
    this.hasBooted = true;
  }

  /**
   * render_canvas_tunnel (tunnel.js:472-527): section list reversed for
   * airflow 2, dual-section bookkeeping, floor-construction Y base, display
   * naming, per-section render + first-section tunnel wireframe + base.
   */
  render_canvas_tunnel(
    tunnel: CanvasTunnelModel,
    reorder = false,
    tunnel_index?: number,
    max_length?: number,
    max_width?: number,
    active_id?: number
  ): void {
    const models = this.sectionsByTunnel[tunnel.id] || [];
    const list = tunnel.get('airflow_direction_id') === 2 ? [...models].reverse() : models;
    const dual_index = list.findIndex((el) => el.get('sitting_on_id'));
    let before = 0;
    let after = 0;
    let before_dual = 0;
    let dual_section_component: any = null;
    if (dual_index > -1) {
      for (let i = 0; i < dual_index; i++) {
        before_dual += list[i].get('length');
      }
      dual_section_component = {
        sitting_on_id: list[dual_index].get('sitting_on_id'),
        length: (list[dual_index].get('length') * this.coef) / 2,
        index: dual_index + 1,
        before_dual: before_dual * this.coef,
        id: list[dual_index].get('id'),
      };
    }
    // tunnel.js:492-493 — floor_construction_id == 2 raises accessory/
    // int-wall/dmount Y by the under-insulation thickness (parity 55).
    const floor_construction_id = this.getFloorConstructionId();
    this.tunnelStartYByFloorConstr[tunnel.id] =
      floor_construction_id == 2 && tunnel.get('under_insulation_thick')
        ? tunnel.get('under_insulation_thick')
        : 0;

    list.forEach((section, index) => {
      section.after = after * this.coef;
      if (index < dual_index) before += section.get('length');
      else if (index != dual_index) after += section.get('length');
      section.before = before * this.coef;
      section.order =
        tunnel.get('airflow_direction_id') === 2
          ? (this.sectionsByTunnel[tunnel.id]?.length || 0) - section.get('order_rate') + 1
          : section.get('order_rate');
      let name = section.name_on_canvas?.replace('_', ' ');
      section.dual_section_component = dual_section_component;
      if (section.name_on_canvas === 'plate_heat_exchanger') {
        name = section.attributes.sitting_on_id ? 'HEATEX' : 'PLATE';
      } else if (section.name_on_canvas === 'er_wheel') {
        name = section.attributes.sitting_on_id ? 'WHEEL' : 'HEAT';
      } else if (section.name_on_canvas === 'user_defined_component') name = 'USER DEFINED';
      this.render_canvas_section({
        tunnel,
        section,
        width: tunnel.get('width'),
        height: tunnel.get('height'),
        length: section.get('length'),
        name: (name || section.get('name') || 'plenum').toUpperCase(),
        reorder,
        last: list.length === index + 1,
        tunnel_index,
        max_length,
        max_width,
        // [NEG]#13: legacy also ORs `section.id === self.auto_select_id`
        // where `self` is window → never matches; and render_canvas_section
        // overwrites `active` from viewdata anyway — kept for the signature.
        active: active_id === section.id,
        index,
      });
      if (!index) this.drawTunnelWireframe(tunnel.toJSON(), section);
    });
    this.render_canvas_tunnel_base(tunnel, max_length);
  }

  /** render_canvas_tunnel_base (tunnel.js:533-541). */
  render_canvas_tunnel_base(tunnel: CanvasTunnelModel, max_length?: number): void {
    this.drawTunnelBase({
      tunnel,
      width: tunnel.get('width'),
      height: tunnel.get('base')?.height,
      length: tunnel.get('length'),
      max_length,
    });
  }

  /**
   * render_canvas_section (tunnel.js:543-584): shortNames map + the
   * PLATE/ROTARY HEATEX re-mapping by sitting_on_id; `active` comes from
   * viewdata selected_sections (the destructured `active` param is unused by
   * the legacy too — it always recomputes membership, :577).
   */
  render_canvas_section({
    tunnel,
    section,
    width,
    height,
    length,
    name,
    loading = false,
    reorder,
    last = false,
    max_length,
    max_width,
    tunnel_index,
    index,
  }: any): void {
    const shortNames: Record<string, string> = {
      'USER DEFINED': 'UDS',
      ELIMINATOR: 'EL',
      FILTER: 'FL',
      'GAS HEATER': 'GH',
      'EVAP PACK': 'EVAP',
      SILENCER: 'SIL',
      PLENUM: 'PL',
      'SPLIT-PLENUM': 'S-PL',
      HEATEX: 'HTX',
    };
    if (name === 'PLATE HEATEX') {
      name = section?.attributes?.sitting_on_id ? 'HEATEX' : 'PLATE';
    } else if (name === 'ROTARY HEATEX' || name === 'HEAT WHEEL') {
      name = section?.attributes?.sitting_on_id ? 'WHEEL' : 'HEAT';
    }
    const shortName = shortNames[name] || name;
    // stateStorage viewdata selected_sections (:562) → selection slice.
    const selected_sections = this.getSelectedSectionIds();
    this.drawSection({
      section,
      tunnel,
      width,
      height,
      length,
      name,
      order: index + 1,
      loading,
      reorder,
      last,
      max_length,
      max_width,
      tunnel_index,
      active: selected_sections.includes(section.id),
      shortName,
    });
    this.tunnelViewSectionObjects.push({ section, tunnel });
  }

  /**
   * render_canvas_section_destroy (tunnel.js:585-610). The cross-list splice
   * quirk is verbatim: the index found in CANVAS registries is used to splice
   * the VIEW list (:586-590). Empty-state chrome classes re-homed (§3.5).
   * 6c2: dmount timers (:599-600).
   */
  render_canvas_section_destroy(sectionId: number): void {
    this.dropDestroyedSectionRegistries(sectionId);
    this.renderAfterSectionDestroy();
  }

  /**
   * The registry half of `render_canvas_section_destroy`, split out so a
   * batched delete can drop SEVERAL sections and rebuild once
   * (canvasRedrawScheduler) instead of running a full pass per section.
   */
  dropDestroyedSectionRegistries(sectionId: number): void {
    const sectionIndex = this.sectionCanvasObjects.findIndex(
      (obj: any) => obj.section.get('id') === sectionId || obj.section.get('section_id') === sectionId
    );
    this.destroySection(sectionId);
    // Guarded: legacy splices unconditionally (:600) because its registry always
    // still held the section. Ours may have been rebuilt without it already, and
    // splice(-1, 1) would drop an unrelated (the last) entry.
    if (sectionIndex !== -1) this.tunnelViewSectionObjects.splice(sectionIndex, 1);
    delete this.sections_dmounts[sectionId];
  }

  /** The re-render half (tunnel.js:596-610). */
  renderAfterSectionDestroy(): void {
    this.syncCollections();
    const { max_length, max_width } = this.getAhuSizes();
    this.sectionCanvasObjects.map((_: any, idx: number) => this.removeSectionObjFromScene(idx));
    this.sectionCanvasObjects = [];
    this.accessoryCanvasObjectsObj = {};
    this.tunnelsDimensions = {};
    this.tunnelModels.forEach((el, idx) => {
      this.render_canvas_tunnel(el, true, idx + 1, max_length, max_width);
      this.scheduleDmountsPosition(el.id); // tunnel.js:599-600 (G29)
    });
    this.pruneOrphanTunnelObjects();
    this.checkTunnelsLength();
  }

  /**
   * redrawCanvas (tunnel.js:622-646) — 6b's registry wipe + the real tunnel
   * re-render. `fromAhu` (= `redraw:canvas` bus path) keeps the cameras;
   * resize resets both frusta + zoom. simulateZoomApp lives in useBodyZoom.
   */
  redrawCanvas(fromAhu: boolean): void {
    this.need_update = true;
    if (!fromAhu) {
      this.updateCameraProps(this.camera, this.gridHelper);
      this.updateCameraProps(this.cameraForGrid, this.fixedGridHelper);
    }
    this.syncCollections();
    const { max_length, max_width } = this.getAhuSizes();
    this.renderer.setSize(this.el.offsetWidth, this.el.offsetHeight);
    this.renderer.setPixelRatio(Math.min(Math.max(1.5, window.devicePixelRatio), 2));
    this.sectionCanvasObjects.map((_: any, idx: number) => this.removeSectionObjFromScene(idx));
    this.sectionCanvasObjects = [];
    this.accessoryCanvasObjectsObj = {};
    this.tunnelsDimensions = {};
    this.tunnels_lugs = {};
    this.tunnels_lugs_on_canvas = {};
    this.activeLugs = [];
    this.tunnelModels.forEach((el, idx) => {
      this.render_canvas_tunnel(el, true, idx + 1, max_length, max_width);
      this.scheduleDmountsPosition(el.id); // tunnel.js:643-644 (G29)
    });
    this.pruneOrphanTunnelObjects();
  }

  /** redrawCanvasDependsOnAHU (tunnel.js:647-649) — `redraw:canvas` receiver. */
  redrawCanvasDependsOnAHU(): void {
    this.redrawCanvas(true);
  }

  // =========================================================================
  // tunnel.js DISPATCHER HANDLER PORTS (§3.4 rows 2/6/7 — wired by AhuCanvas)
  // =========================================================================

  /**
   * `show:on:canvas` (tunnel.js:136-157): full re-render of all tunnels with
   * the new section active. Chrome un-hide + `.added-section-note` re-homed
   * (§3.5). 6c2: dmount re-schedules.
   */
  onShowOnCanvas(component: any): void {
    this.syncCollections();
    const { max_length, max_width } = this.getAhuSizes();
    this.sectionCanvasObjects.map((_: any, idx: number) => this.removeSectionObjFromScene(idx));
    this.sectionCanvasObjects = [];
    this.accessoryCanvasObjectsObj = {};
    this.need_update = true;
    this.tunnelsDimensions = {};
    this.tunnelModels.forEach((tunnel, idx) => {
      this.render_canvas_tunnel(tunnel, true, idx + 1, max_length, max_width, component?.id);
      // tunnel.js:147-148 (G29; the :138-139 pre-schedule for component.tunnel
      // is subsumed — this loop re-debounces every tunnel including it).
      this.scheduleDmountsPosition(tunnel.id);
    });
    this.pruneOrphanTunnelObjects();
    this.checkTunnelsLength();
  }

  /**
   * `change:tunnel:dimensions` (tunnel.js:179-230): one-tunnel re-render;
   * `render:true` → wipe all tunnel wireframes + full re-render; longest-
   * tunnel change → re-render the OTHERS too. The `model` payload (a just-
   * saved tunnel not yet in the collection) has no 6c1 producer — resolved
   * from the live rows; `check:true` chrome un-hide re-homed (§3.5). Ends by
   * clearing `disable` from the 360 controls (:228 — see options doc).
   */
  onChangeTunnelDimensions({ tunnel_id, render }: { tunnel_id: number; render?: boolean; check?: boolean }): void {
    this.syncCollections();
    let index: number | undefined;
    this.need_update = true;
    const currentTunnel = this.tunnelModels.find((tunnel, idx) => {
      if (tunnel.get('id') === tunnel_id) index = idx + 1;
      return tunnel.get('id') === tunnel_id;
    });
    const tunnel = currentTunnel;
    if (!tunnel) return;
    const { max_length, max_width } = this.getAhuSizes();
    if (!render) {
      this.render_canvas_tunnel(tunnel, true, index, max_length, max_width);
      this.scheduleDmountsPosition(tunnel.id); // tunnel.js:201-202 (G29)
    }
    if (render) {
      Object.keys(this.tunnelsWireframes).forEach((id) => this.removeTunnelWireframe(id));
      this.sectionCanvasObjects.map((_: any, idx: number) => this.removeSectionObjFromScene(idx));
      this.sectionCanvasObjects = [];
      this.accessoryCanvasObjectsObj = {};
      this.tunnelModels.forEach((t, idx) => {
        this.render_canvas_tunnel(t, true, idx + 1, max_length, max_width);
        this.scheduleDmountsPosition(t.id); // tunnel.js:212-213 (G29)
      });
    } else if (max_length === tunnel.get('length')) {
      this.sectionCanvasObjects.map((_: any, idx: number) => this.removeSectionObjFromScene(idx));
      this.sectionCanvasObjects = [];
      this.tunnelsDimensions = {};
      this.tunnelModels.forEach((t, idx) => {
        if (tunnel_id !== t.id) {
          this.render_canvas_tunnel(t, true, idx + 1, max_length, max_width);
          this.scheduleDmountsPosition(t.id); // tunnel.js:223-224 (G29)
        }
      });
    }
    this.onRotateAvailabilityClear('disable'); // $(".auto_360…").removeClass('disable') (:228)
  }

  /** `sections:reordered` (tunnel.js:232-251): one-tunnel rebuild in place. */
  onSectionsReordered(tunnelId: number): void {
    this.syncCollections();
    let index: number | undefined;
    this.need_update = true;
    const currentTunnel = this.tunnelModels.find((tunnel, idx) => {
      if (tunnel.get('id') === tunnelId) index = idx + 1;
      return tunnel.get('id') === tunnelId;
    });
    if (!currentTunnel) return;
    const { max_length, max_width } = this.getAhuSizes();
    // Verbatim filter-with-side-effect (:243-246).
    this.sectionCanvasObjects = this.sectionCanvasObjects.filter((el: any, i: number) => {
      if (el.tunnel.id === tunnelId) this.removeSectionObjFromScene(i);
      if (el.tunnel.id !== tunnelId) return el;
      return undefined;
    });
    this.render_canvas_tunnel(currentTunnel, true, index, max_length, max_width);
    this.scheduleDmountsPosition(currentTunnel.id); // tunnel.js:248-249 (G29)
  }

  // =========================================================================
  // G1 — SECTION CUBE + EDGE WIREFRAME
  // =========================================================================

  /** drawSection (canvas.view3d:1297-1323). [NEG]#104: the bare `this.resetData;`
   *  expression (:1302) is a no-op — dropped (resetData itself has no other
   *  live caller and is not ported). */
  drawSection({
    section,
    tunnel,
    width,
    height,
    length,
    name,
    shortName,
    order = this.sectionCanvasObjects?.length || 0,
    loading,
    reorder,
    last,
    max_length,
    tunnel_index,
    max_width,
    active,
  }: any): void {
    width = this.coef * width;
    height = this.coef * height;
    length = this.coef * length;
    const tag = section?.attributes?.tag;
    this.drawCube(
      {
        width,
        height,
        length,
        name,
        shortName,
        order,
        section,
        tunnel,
        loading,
        reorder,
        last,
        max_length,
        max_width,
        tunnel_index,
        active,
        tag,
      },
      this.drawSectionComplete
    );
  }

  /** isSectionHasCasing (canvas.view3d:1355-1357). */
  isSectionHasCasing({ section }: any): number | false {
    return section.get('casing_insulation_thick') * this.coef || false;
  }

  /**
   * G4 drawTunnelCasing (canvas.view3d:1358-1374) — under-insulation casing
   * wireframe inside the tunnel-base cube; skipped when thickness 0.
   * OBSERVABLE-BEHAVIOR NOTE: the base wrapper cube it parents into is never
   * scene-attached (only the part-set `group` is added — :3308; verified by
   * grep, no other attach site), so this wireframe does not actually render
   * in legacy. Ported verbatim anyway — the cube/casing become visible the
   * moment a future fix attaches the wrapper, exactly like legacy would.
   */
  drawTunnelCasing({ tunnel, shape, height: base_height }: any): void {
    const width = tunnel.get('width') * this.coef;
    const height = tunnel.get('under_insulation_thick') * this.coef;
    const length = tunnel.get('length') * this.coef;
    if (!height) return;
    const y =
      (base_height - height) / 2 + (this.tunnelStartYByFloorConstr[tunnel.id] ? height : 0);
    const cubeGeometry = new THREE.BoxBufferGeometry(length, height, width);
    const edges = new THREE.EdgesGeometry(cubeGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({ color: wallColor });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.renderOrder = 0;
    wireframe.position.y = y;
    shape.add(wireframe);
  }

  /**
   * G3 drawCasing (canvas.view3d:1375-1425) — interior casing: invisible fill
   * + wallColor wireframe; end-trim/recenter for first/last sections;
   * left/right/casing_thick flags stamped for the 6c2 accessory math.
   */
  drawCasing({ section, shape, last, order, hasCasing }: any): any {
    const width = Math.round(section.get('aw_width')) * this.coef;
    const height = Math.round(section.get('aw_height')) * this.coef;
    let length = section.get('length') * this.coef;
    let x = 0;
    const y = hasCasing;
    const z = hasCasing;

    if (last) length -= z;
    if (order === 1) length -= z;
    if (last || order == 1) x = z / 2;

    const cubeGeometry = new THREE.BoxBufferGeometry(length, height, width);
    const cubeMaterial = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });
    const cube: any = new THREE.Mesh(cubeGeometry, cubeMaterial);

    if (section.tunnel.get('airflow_direction_id') == 2) {
      if (last) cube.left = true;
      if (order === 1) cube.right = true;
    } else {
      if (last) cube.right = true;
      if (order === 1) cube.left = true;
    }
    cube.casing_thick = z;

    const edges = new THREE.EdgesGeometry(cubeGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: wallColor,
      opacity: 0.8,
      transparent: true,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.renderOrder = 0;
    wireframe.position.y = -y / 2;
    cube.position.y = -y / 2;
    if (!last) {
      wireframe.position.x = x;
      cube.position.x = x;
    }
    if (order != 1) {
      wireframe.position.x = -x;
      cube.position.x = -x;
    }
    shape.add(wireframe);
    shape.add(cube);
    return { cube, wireframe };
  }

  /**
   * drawCube (canvas.view3d:1426-1473): BB(length,height,width) fill (opacity
   * 0.6 sections / 0.4 bases unless an explicit `opacity` arg — createBase
   * passes 0) + EdgesGeometry wireframe renderOrder 1; section branch builds
   * both name labels + casing; first-section auto-select fires once per fresh
   * boot (need_select_first latch — armed by the 6d refresh-restore).
   */
  drawCube(
    {
      section,
      tunnel,
      width,
      height,
      length,
      name,
      shortName,
      tag,
      order,
      loading,
      reorder,
      last,
      max_length,
      tunnel_index,
      max_width,
      active,
      opacity,
    }: any,
    callback?: (response: any) => void
  ): { cube: any; wireframe: any } {
    if (section && !this.selectFirstSection && this.need_select_first) {
      active = true;
      this.selectFirstSection = true;
      this.emitBus('select:section', { section, tab: 'ahu', selected_sections: [section] });
    }
    const sectionColors: any = section ? this.getSectionColors(section.id) : {};
    // Verbatim (:1434-1435): for bases (no section) both branches read
    // undefined → MeshBasicMaterial default white; invisible at opacity 0.
    const color = active ? sectionColors.selected : sectionColors.passive;

    const cubeGeometry = new THREE.BoxBufferGeometry(length, height, width);
    const cubeMaterial = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: typeof opacity === 'number' ? opacity : section ? 0.6 : 0.4,
    });
    const cube: any = new THREE.Mesh(cubeGeometry, cubeMaterial);

    const edges = new THREE.EdgesGeometry(cubeGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: !section
        ? wireframeMaterialColorAccessory
        : active
        ? wireframeMaterialColorActive
        : wireframeMaterialColor,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.renderOrder = 1;
    let response: any = {};
    if (section) {
      cube.name = name;
      const nameColor = this.getSectionNameColor(section);
      const textLabel = this.calculateSectionName(
        cube.geometry.parameters.depth,
        name,
        shortName,
        tag,
        nameColor
      );
      const textLabelRotated = this.calculateSectionName(
        cube.geometry.parameters.height,
        name,
        shortName,
        tag,
        nameColor
      );
      textLabel.rotation.y += Math.PI / 2;
      textLabelRotated.rotation.x += Math.PI / 2;
      let casing = null;
      const hasCasing = this.isSectionHasCasing({ section });
      if (hasCasing) casing = this.drawCasing({ section, shape: cube, last, order, hasCasing });
      response = {
        section,
        tunnel,
        cube,
        wireframe,
        textLabel,
        textLabelRotated,
        order,
        height,
        loading,
        reorder,
        last,
        max_length,
        tunnel_index,
        max_width,
        active,
        name,
        shortName,
        casing,
      };
    } else {
      response = { cube, wireframe };
    }
    if (callback) {
      callback.call(this, response);
    }
    return { cube, wireframe };
  }

  /**
   * isCasingWidthChanged (canvas.view3d:2417-2425) — fires the dynamic
   * `change:casing:section_<id>` event when the exposed-end flags flip
   * (§3.4 #34; consumers are the 6e accessory forms).
   */
  isCasingWidthChanged(section_id: number, cube: any): void {
    const casing = this.sections_cassing[section_id];
    if (!casing) return;
    const old_cube = casing.cube;
    if (!old_cube || !cube) return;
    const right = old_cube?.right != cube?.right;
    const left = old_cube?.left != cube?.left;
    if (left || right) this.emitBus(`change:casing:section_${section_id}`, { update_position: true });
  }

  /**
   * drawSectionComplete (canvas.view3d:2426-2618): start_x chain, registry
   * replace/push, position pipeline (G37/G38), the three dual-section X
   * overrides, dimensions orchestration, tunnel-isolation visibility and the
   * airflow arrows. Deferred blocks marked 6c2: int-wall replay (:2557),
   * horizontal dmounts (:2558), accessory replay + preview merge (:2566-2585),
   * coil/evap replay (:2587-2594).
   */
  drawSectionComplete({
    name,
    shortName,
    section,
    tunnel,
    cube,
    wireframe,
    textLabel,
    textLabelRotated,
    order,
    loading,
    reorder,
    last,
    max_length,
    tunnel_index,
    max_width,
    active,
    casing,
  }: any): void {
    cube.layers.set(0);
    cube.tunnel_index = tunnel_index;
    let start_x;
    if (!this.tunnelsLengths[tunnel_index - 1]) {
      start_x =
        this.longestTunnelDirection === 2 ? (max_length - tunnel.get('length')) * this.coef : 0;
    } else {
      start_x =
        tunnel.get('airflow_direction_id') === 1
          ? this.tunnelsLengths[tunnel_index - 1].start_x
          : (this.tunnelsLengths[tunnel_index - 1].length - tunnel.get('length')) * this.coef +
            this.tunnelsLengths[tunnel_index - 1].start_x;
    }
    const sectionIndex = this.sectionCanvasObjects.findIndex(
      (obj: any) =>
        obj.section.get('id') === section.id || obj.section.get('section_id') === section.id
    );
    this.sectionCanvasObjectsObj[section.id] = {
      section,
      tunnel,
      cube,
      casing,
      last,
    };
    this.isCasingWidthChanged(section.id, casing?.cube);
    this.sections_cassing[section.id] = casing || {};
    if (reorder && sectionIndex > -1) {
      this.removeSectionObjFromScene(sectionIndex);
      this.sectionCanvasObjects[sectionIndex] = {
        section,
        tunnel,
        cube,
        wireframe,
        textLabel,
        textLabelRotated,
        order,
        active,
        tunnel_index,
        start_x,
        name,
        shortName,
        casing,
        last,
      };
    } else {
      this.sectionCanvasObjects.push({
        section,
        tunnel,
        cube,
        wireframe,
        textLabel,
        textLabelRotated,
        order,
        active,
        tunnel_index,
        start_x,
        name,
        shortName,
        casing,
        last,
      });
    }
    this.tunnelsLengths[tunnel_index] = { length: tunnel.get('length'), start_x };
    const tunnelBaseHeight = tunnel.get('base')?.height * this.coef || 0;
    const fullHeight = tunnel.get('height') + (tunnel.get('base')?.height || 0);
    const positions = this.calculateCubePositions(
      fullHeight * this.coef,
      tunnelBaseHeight,
      tunnel.get('id'),
      start_x,
      max_length,
      max_width,
      tunnel_index === 1 ? tunnel.get('id') : null
    );
    this.setSectionPosition(
      tunnel,
      positions[tunnel.get('id')],
      order,
      cube,
      wireframe,
      textLabel,
      textLabelRotated,
      tunnelBaseHeight
    );
    // Dual-section X overrides (:2490-2548) — three branches (parity 252).
    if (section.get('sitting_on_id') && this.sectionCanvasObjectsObj[section.get('sitting_on_id')]) {
      let x = this.sectionCanvasObjectsObj[section.get('sitting_on_id')].cube.position.x;
      const diff =
        (this.sectionCanvasObjectsObj[section.get('sitting_on_id')].cube.geometry.parameters.width -
          cube.geometry.parameters.width) /
        2;
      if (tunnel.get('airflow_direction_id') == 2) x += diff;
      else x -= diff;
      cube.position.x = x;
      wireframe.position.x = x;
    } else if (
      section.dual_section_component &&
      this.sectionCanvasObjectsObj[section.dual_section_component.sitting_on_id]
    ) {
      const hostCube = this.sectionCanvasObjectsObj[section.dual_section_component.sitting_on_id].cube;
      const dualModel = (this.sectionsByTunnel[tunnel.id] || []).find(
        (el) => el.id == section.dual_section_component.id
      );
      const diff =
        (hostCube.geometry.parameters.width - (dualModel ? dualModel.get('length') * this.coef : 0)) / 2;
      let new_x = hostCube.position.x;
      if (tunnel.get('airflow_direction_id') == 2) new_x += diff;
      else new_x -= diff;
      if (order < section.dual_section_component.index)
        new_x -=
          section.dual_section_component.length +
          cube.geometry.parameters.width / 2 +
          section.dual_section_component.before_dual -
          section.before;
      else
        new_x +=
          section.dual_section_component.length + cube.geometry.parameters.width / 2 + section.after;
      cube.position.x = new_x;
      wireframe.position.x = new_x;
    } else if (tunnel_index > 0 && this.sectionCanvasObjects.find((s: any) => s.section.get('sitting_on_id'))) {
      const airflowDirection = tunnel.get('airflow_direction_id');

      const prevSections = this.sectionCanvasObjects.filter(
        (s: any) => s.tunnel_index === tunnel_index - 1
      );
      const currentSections = this.sectionCanvasObjects.filter(
        (s: any) => s.tunnel_index === tunnel_index
      );

      const prevIndex = airflowDirection === 1 ? 0 : prevSections.length - 1;
      const currentIndex = airflowDirection === 2 ? 0 : order - 2;

      const prevTunnelCube = prevSections?.[prevIndex]?.cube;
      const currentTunnelCube = currentSections?.[currentIndex]?.cube;
      const prevWidth = prevTunnelCube?.geometry.parameters.width;
      const currentWidth = currentTunnelCube?.geometry.parameters.width;
      const cubeWidth = cube.geometry.parameters.width;

      const newX =
        airflowDirection === 1
          ? prevTunnelCube?.position?.x - prevWidth / 2 + cubeWidth / 2
          : prevTunnelCube?.position?.x + prevWidth / 2 - cubeWidth / 2;

      let val = 0;
      const tunnelSections = this.sectionsByTunnel[tunnel.id] || [];
      if (airflowDirection === 1) {
        val = order === 1 ? newX : currentTunnelCube?.position.x + currentWidth / 2 + cubeWidth / 2;
      } else {
        if (order === tunnelSections.length) val = newX;
        else {
          let width_befor = 0;
          tunnelSections.forEach((s, i) => {
            if (i < tunnelSections.length - order) width_befor += s.get('length');
          });
          val = newX - width_befor * this.coef;
        }
      }
      if (val) {
        cube.position.x = val;
        wireframe.position.x = val;
      }
    }
    if (loading) {
      this.normalizeTunnelBase(tunnel);
    }
    this.group.add(cube);
    this.group.add(wireframe);
    cube.add(textLabel);
    cube.add(textLabelRotated);
    this.redrawIntWalls(section); // :2557 (G11)
    // G29 horizontal-dmount replay (:2558) — stamp derived from the §20.4 #38
    // rows in the wrapper ctor (null while in flight = legacy pre-listener).
    if (section.horizontal_dmounts) this.drawHorizontalDmounts(section, section.horizontal_dmounts);
    this.drawTunnelDimensions({ tunnel, section, last, cube, max_length, max_width, tunnel_index, start_x });
    if (
      this.activeTunnelIndex &&
      // legacy: `sections[activeTunnelIndex]?.models` — collection EXISTENCE (truthy
      // even when empty), not section count.
      this.sectionsByTunnel[this.activeTunnelIndex] !== undefined &&
      tunnel.get('id') != this.activeTunnelIndex
    ) {
      cube.visible = false;
      wireframe.visible = false;
    }
    this.scene.updateMatrixWorld(true);
    // Accessory replay + PREVIEW merge (:2566-2585, G12-G21 + R16): when the
    // LAST section of the LAST tunnel completes, every cached entry re-draws
    // with the width/height fallback chain; the preview accessory overlays
    // previewAccessoryStagingData and swaps mounting for previewMounting
    // (written directly by the accessory selector's _updateCanvasPreview).
    if (last && tunnel_index === this.tunnelModels.length) {
      this.accessoryCanvasObjects.forEach(
        ({ accessory, section: accSection, mounting, int_wall_model, viewport }: any) => {
          const isPreview =
            this.previewAccessoryId != null && accessory.id === this.previewAccessoryId;
          const acc =
            isPreview && this.previewAccessoryStagingData
              ? { ...accessory, ...this.previewAccessoryStagingData }
              : accessory;
          const mnt = isPreview && this.previewMounting ? this.previewMounting : mounting;
          this.drawAccessory({
            accessory: acc,
            section: accSection,
            width: acc.width || acc.overall_width || acc.width_calc,
            height: acc.height || acc.overall_depth || acc.height_calc,
            name: acc.name,
            mounting: mnt,
            isAlredyRendered: false,
            depth: acc.depth,
            int_wall_model,
            viewport,
          });
        }
      );
    }
    // Coil/evap connection replay (:2587-2594, G31/G32).
    if (this.coilConnections[section.id]) {
      const { coil, duty, qty, h_from_floor, angle } = this.coilConnections[section.id];
      this.drawCoilConnections(coil, section.id, qty, duty, h_from_floor, angle);
    }
    if (this.evapPackConnections[section.id]) {
      const { data } = this.evapPackConnections[section.id];
      this.drawEvapPackConnections(data, section.id);
    }

    // G33 airflow arrows (:2595-2617) — drawn at order-1 (airflow 1) or last
    // (airflow 2), the fan-anchored re-position + the wheel end-arrow offset.
    if (tunnel.get('airflow_direction_id') == 2 && last) {
      this.drawAirflowDirectionChangingArrow(tunnel.get('id'), tunnel.get('airflow_direction_id'), section.id);
      const arrow = this.tunnelsAirflowIcons[tunnel.get('id')];
      const section_cube = this.sectionCanvasObjectsObj[arrow?.section_id]?.cube;
      if (arrow) this.getArrowPosition(arrow.orientation_id, arrow, section_cube, 2);
      const arrowEnd2 = this.tunnelsAirflowIcons[tunnel.get('id') + '_end'];
      if (arrowEnd2 && section_cube) {
        this.getArrowPosition(arrowEnd2.orientation_id, arrowEnd2, section_cube, 2);
        arrowEnd2.position.x -= tunnel.get('length') * this.coef + 2 * 50 * this.coef;
      }
    }
    if (order === 1 && tunnel.get('airflow_direction_id') == 1) {
      this.drawAirflowDirectionChangingArrow(tunnel.get('id'), tunnel.get('airflow_direction_id'), section.id);
    }
    const arrow = this.tunnelsAirflowIcons[tunnel.get('id')];
    if (tunnel.get('airflow_direction_id') == 1 && arrow?.section_id === section.id) {
      this.getArrowPosition(arrow.orientation_id, arrow, cube, tunnel.get('airflow_direction_id'));
      const arrowEnd1 = this.tunnelsAirflowIcons[tunnel.get('id') + '_end'];
      if (arrowEnd1) {
        this.getArrowPosition(arrowEnd1.orientation_id, arrowEnd1, cube, tunnel.get('airflow_direction_id'));
        arrowEnd1.position.x += tunnel.get('length') * this.coef + 2 * 50 * this.coef;
      }
    }
  }

  // =========================================================================
  // G37/G38 — POSITION PIPELINE
  // =========================================================================

  /**
   * calculateCubePositions (canvas.view3d:3679-3730): per-tunnel order sort,
   * X chaining, Y by next_to/stacked, Z centering, grid Y (§40), tunnelsStartX.
   */
  calculateCubePositions(
    height: number,
    baseHeight: number,
    _initialTunnelId: any,
    start_x: number,
    max_length: number,
    max_width: number,
    firstTunnel: any
  ): any {
    const positions: any = {};
    const tunnels: Record<string, any[]> = {};
    this.sectionCanvasObjects.map((obj: any) => {
      if (!tunnels[obj.tunnel.get('id')]) {
        tunnels[obj.tunnel.get('id')] = [];
      }
      tunnels[obj.tunnel.get('id')].push(obj);
      tunnels[obj.tunnel.get('id')].sort((a, b) => {
        const orderA = a.section.order || a.section.get('order_rate');
        const orderB = b.section.order || b.section.get('order_rate');
        return orderA < orderB ? -1 : 1;
      });
    });
    const extra_height = this.next_to ? (this.max_height * this.coef - height + baseHeight) / 2 : 0;
    for (const tunnelId in tunnels) {
      for (let i = 0; i < tunnels[tunnelId].length; i++) {
        if (!i && this.firstTunnel == (tunnelId as any)) {
          this.firstSectionLength = tunnels[tunnelId][i].cube.geometry.parameters.width;
          // :3699 — the legacy assigns an implicit global `unit` here that is
          // only consumed by a commented-out term ([NEG]#102): scoped + unused.
        }
        // :3701 precedence quirk (G37): `A + !next_to ? B : 0` parses as
        // `(A + !next_to) ? B : 0` — replicate the RESULT, not the expression.
        const zCond =
          (this.ahu_width && firstTunnel ? this.ahu_length : 0) + (this.next_to ? 0 : 1);
        const z = zCond
          ? (max_width * this.coef - tunnels[tunnelId][i].cube.geometry.parameters.depth) / 2
          : 0;
        const x = i
          ? positions[tunnelId][i - 1].x +
            tunnels[tunnelId][i - 1].cube.geometry.parameters.width / 2 +
            tunnels[tunnelId][i].cube.geometry.parameters.width / 2
          : tunnels[tunnelId][i].cube.geometry.parameters.width / 2 +
            start_x -
            (max_length * this.coef) / 2;
        const y = this.next_to
          ? -this.max_base_height * this.coef +
            baseHeight +
            (this.firstTunnelHeight * this.coef) / 4 -
            extra_height
          : -extra_height + (height - this.ahu_height) / 2 + this.firstTunnelBaseHeight * this.coef;
        if (!i) this.tunnelsStartX[tunnelId] = start_x;
        const position = new THREE.Vector3(x, y, z);
        if (!positions[tunnelId]) {
          positions[tunnelId] = [];
        }
        positions[tunnelId].push(position);
      }
      // :3716-3720 dead branch dropped: it references the loop-scoped `z`
      // after the loop (latent ReferenceError) and is unreachable — the
      // caller pushes into sectionCanvasObjects before calling this.
      // Grid Y (§40) — both grids move with the AHU envelope.
      if (!this.next_to) {
        this.gridHelper.position.y =
          -this.ahu_height / 2 + this.firstTunnelBaseHeight * this.coef - extra_height - 50;
        this.fixedGridHelper.position.y =
          -this.ahu_height / 2 + this.firstTunnelBaseHeight * this.coef - extra_height - 50;
      } else {
        this.gridHelper.position.y =
          -((this.firstTunnelHeight * this.coef) / 4) - (this.max_height * this.coef) / 2 - 50;
        this.fixedGridHelper.position.y =
          -((this.firstTunnelHeight * this.coef) / 4) - (this.max_height * this.coef) / 2 - 50;
      }
    }
    return positions;
  }

  /**
   * setSectionPosition (canvas.view3d:3614-3677): tunnelsByIndexes stacking
   * (side-by-side / stacked / single-row), label placement + view-dependent
   * orientation, plan/elevation visibility (the `.views_list_li.active` DOM
   * reads re-homed to activePreset, §3.5).
   */
  setSectionPosition(
    tunnel: any,
    positions: any[],
    order: number,
    cube: any,
    wireframe: any,
    textLabel: any,
    textLabelRotated: any,
    tunnelBaseHeight: number
  ): void {
    const index1 = tunnel.current_index_values[0];
    const index2 = tunnel.current_index_values[1];
    const extra_width = this.next_to ? this.ahu_width - this.firstTunnelWidth * this.coef : 0;
    order = order - 1;
    if (index1 > index2) {
      const index = `${index1 - 1}:${index2}`;
      positions[order].z +=
        this.tunnelsByIndexes[index].z -
        this.tunnelsByIndexes[index].depth / 2 -
        (tunnel.get('width') * this.coef) / 2;
    } else if (index1 < index2) {
      const index = `${index1}:${index2 - 1}`;
      positions[order].y =
        tunnelBaseHeight +
        this.tunnelsByIndexes[index].y +
        this.tunnelsByIndexes[index].height / 2 +
        (tunnel.get('height') * this.coef) / 2;
    } else {
      positions[order].z += extra_width / 2;
    }
    cube.position.copy(positions[order]);
    wireframe.position.copy(positions[order]);
    textLabel.geometry.computeBoundingBox();
    textLabelRotated.geometry.computeBoundingBox();
    const unit =
      this.text_view_position === 'back_left' || this.text_view_position === 'back_right' ? -1 : 1;
    const textBoundingBox = textLabel.geometry.boundingBox;
    const textBoundingBoxRotated = textLabelRotated.geometry.boundingBox;
    const textWidth = textBoundingBox.max.x - textBoundingBox.min.x;
    const textWidthRotated = textBoundingBoxRotated.max.x - textBoundingBoxRotated.min.x;
    textLabel.position.x = (unit * (textBoundingBox.max.z - textBoundingBox.min.z)) / 2;
    textLabel.position.y += cube.geometry.parameters.height / 2;
    textLabel.position.z = (unit * textWidth) / 2;
    textLabel.rotation.y = (unit * Math.PI) / 2;
    textLabelRotated.rotation.y = (unit * Math.PI) / 2;
    textLabelRotated.position.y = unit * (-textWidthRotated / 2);
    textLabelRotated.position.z += cube.geometry.parameters.depth / 2;
    textLabelRotated.position.x +=
      (unit * (textBoundingBoxRotated.max.z - textBoundingBoxRotated.min.z)) / 2;
    this.getTextPosition(this.text_view_position, textLabelRotated, cube, textWidthRotated);
    // :3652-3658 — `.views_list_li.active[data-value=…]` → activePreset (§3.5).
    if (this.activePreset === 'plan') {
      textLabelRotated.visible = false;
    } else if (this.activePreset === 'elevation_near_side') {
      textLabel.visible = false;
    } else if (this.text_position === 'top') {
      textLabelRotated.visible = false;
    } else textLabel.visible = false;
    const { x, y, z } = cube.position;

    if (!this.tunnelsByIndexes[`${index1}:${index2}`]?.x) {
      this.tunnelsByIndexes[`${index1}:${index2}`] = {
        x,
        y,
        z,
        width: tunnel.get('length') * this.coef,
        height: tunnel.get('height') * this.coef,
        depth: tunnel.get('width') * this.coef,
      };
    } else {
      this.tunnelsByIndexes[`${index1}:${index2}`].width = tunnel.get('length') * this.coef;
      this.tunnelsByIndexes[`${index1}:${index2}`].height = tunnel.get('height') * this.coef;
      this.tunnelsByIndexes[`${index1}:${index2}`].depth = tunnel.get('width') * this.coef;
      this.tunnelsByIndexes[`${index1}:${index2}`].z = z;
      this.tunnelsByIndexes[`${index1}:${index2}`].y = y;
    }
  }

  // =========================================================================
  // G5 — TUNNEL WIREFRAME
  // =========================================================================

  /**
   * drawTunnelWireframe (canvas.view3d:4657-4679): +0.1" padded box per
   * tunnel, drawn with the tunnel's FIRST section, parent tunnelsWireframeGroup.
   * [NEG]#103: legacy uses MeshPhysicalMaterial(0x757575) on LineSegments in a
   * light-less scene → renders BLACK; ported as LineBasicMaterial matched to
   * the RENDERED color (screenshot-verify in 6f). [NEG]#97: the object-level
   * `depthFunc = AlwaysDepth` no-op and the inert `castShadow` are dropped.
   */
  drawTunnelWireframe(
    { id, width, height, length }: { id: number; width: number; height: number; length: number; base?: any },
    section: any
  ): void {
    this.removeTunnelWireframe(id);
    const unit = 0.1;
    const cubeGeometry = new THREE.BoxBufferGeometry(
      (length + unit) * this.coef,
      (height + unit) * this.coef,
      (width + unit) * this.coef
    );
    const edges = new THREE.EdgesGeometry(cubeGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({ color: 0x000000 });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.position.copy(this.sectionCanvasObjectsObj[section.id].cube.position);
    wireframe.computeLineDistances();
    const diff =
      cubeGeometry.parameters.width -
      unit * this.coef -
      this.sectionCanvasObjectsObj[section.id].cube.geometry.parameters.width;
    wireframe.position.x += diff / 2;
    wireframe.renderOrder = 50;
    this.tunnelsWireframes[id] = wireframe;
    this.tunnelsWireframeGroup.add(wireframe);
    if (
      this.activeTunnelIndex &&
      // legacy: `sections[activeTunnelIndex]?.models` — collection EXISTENCE (truthy
      // even when empty), not section count.
      this.sectionsByTunnel[this.activeTunnelIndex] !== undefined &&
      section.tunnel.get('id') != this.activeTunnelIndex
    ) {
      wireframe.visible = false;
    }
  }

  /** removeTunnelWireframe (canvas.view3d:4680-4687). */
  removeTunnelWireframe(id: number | string): void {
    if (this.tunnelsWireframes[id]) {
      this.disposeChilds(this.tunnelsWireframes[id]);
      this.tunnelsWireframeGroup.remove(this.tunnelsWireframes[id]);
      delete this.tunnelsWireframes[id];
    }
  }

  /**
   * Drop the tunnel-level geometry of any tunnel a rebuild pass will not draw.
   *
   * `render_canvas_tunnel` reaches `drawTunnelWireframe` / `drawTunnelBase`
   * only THROUGH a tunnel's sections (`if (!index)` above, :655), so a tunnel
   * that lost its last section — or whose row is gone from the tunnel list
   * entirely — draws nothing, and the wireframe + base it drew last time stay
   * parented in the scene until the page is reloaded.
   *
   * No legacy counterpart because legacy never got into this state: tunnel.js
   * only rebuilt AFTER `destroySection` (:599) had removed that tunnel's
   * wireframe and base by id, off a Backbone collection that still held the
   * deleted section. Here the rebuild is driven by the tunnels prop, which
   * changes the moment RTK Query drops the row — so it runs BEFORE the 500 ms
   * `validate:tunnel:after:section:destroy` handler, and `destroySection` then
   * finds no section to resolve a tunnel id from (sectionIndex === -1) and
   * removes nothing. Whether the redraw wins that race is what made the
   * leftover frame intermittent.
   */
  pruneOrphanTunnelObjects(): void {
    const drawn = new Set(
      this.tunnelModels.filter((t) => (this.sectionsByTunnel[t.id] || []).length > 0).map((t) => String(t.id))
    );
    Object.keys(this.tunnelsWireframes).forEach((id) => {
      if (!drawn.has(String(id))) this.removeTunnelWireframe(id);
    });
    Object.keys(this.tunnelBases).forEach((id) => {
      if (!drawn.has(String(id))) this.disposeAndRemoveTunnelBase(id);
    });
  }

  // =========================================================================
  // G6 — TUNNEL BASE ASSEMBLY
  // =========================================================================

  /**
   * drawTunnelBase (canvas.view3d:3275-3332): height==null removes the base +
   * re-enables 360; invisible wrapper cube + visible part group (tube /
   * c_channel), under-insulation casing, isolation visibility. NB only the
   * part `group` is added to the scene (:3308) — the wrapper cube/wireframe
   * stay orphaned (positioning/measure source for lugs G10 + normalize) and
   * the visibility toggles on them are inert; replicated verbatim. The
   * `.removeClass('disable_rotate')` jQuery clears (:3281/:3328/:3330) are the
   * rotate-availability hook (see Canvas3DGeometryOptions). 6c2: the lug
   * replay from LugCollection (:3322-3327, G7-G10).
   */
  drawTunnelBase({ tunnel, width, height, length }: any): void {
    const tunnelId = tunnel.get('id');
    if (height == null) {
      if (this.tunnelBases[tunnelId]) {
        this.disposeAndRemoveTunnelBase(tunnelId);
      }
      this.onRotateAvailabilityClear('disable_rotate');
      return;
    }
    width = this.coef * width;
    height = this.coef * height;
    length = this.coef * length;

    if (this.tunnelBases[tunnelId]) {
      const { group } = this.tunnelBases[tunnelId];
      [group].forEach((obj) => {
        if (obj) {
          this.disposeChilds(obj);
          this.group.remove(obj);
        }
      });
    }
    const baseType = tunnel?.attributes?.base?.type;

    const baseData = this.createBase({ tunnel, width, height, length, baseType });
    if (!baseData) return;

    const { cube, wireframe, group } = baseData;

    this.calculateTunnelBasePosition(cube, wireframe, tunnel, length);
    this.drawTunnelCasing({ tunnel, shape: cube, height });

    this.group.add(group);
    group.position.copy(cube.position);

    this.tunnelBases[tunnelId] = { cube, wireframe, group };

    // :3313-3321 — verbatim quirk: `tunnelId !== String(activeTunnelIndex)`
    // compares number to string → ALWAYS true, so a redraw during tunnel
    // isolation hides EVERY base (incl. the isolated tunnel's) until the next
    // pill interaction. Replicate, do not fix.
    if (
      this.activeTunnelIndex &&
      // legacy: `sections[activeTunnelIndex]?.models` — collection EXISTENCE (truthy
      // even when empty), not section count.
      this.sectionsByTunnel[this.activeTunnelIndex] !== undefined &&
      (tunnelId as any) !== String(this.activeTunnelIndex)
    ) {
      cube.visible = false;
      wireframe.visible = false;
      group.visible = false;
    }
    // Lug replay from the LugCollection re-home (:3322-3331, G7-G10) — lugs
    // are children of `group`. NB the legacy early-returns BEFORE the
    // 360-enable when the lug model is missing/hidden (`return` inside the
    // tunnel.get('lug') branch :3324) — replicated.
    if (tunnel.get('lug')) {
      const lugs = this.getTunnelLugs(tunnel.id);
      if (!lugs || lugs.hide_lugs) return;
      (lugs.positions || []).forEach((lug: any) => {
        this.drawLug(lug, tunnel, tunnel.id);
      });
      this.onRotateAvailabilityClear('disable_rotate');
    } else {
      this.onRotateAvailabilityClear('disable_rotate');
    }
  }

  /**
   * createBase (canvas.view3d:5272-5301): invisible wrapper cube (opacity 0,
   * black wireframe via the no-section drawCube branch) + the visible part
   * group per base type.
   */
  createBase({ tunnel, width, height, length, baseType }: any): any {
    const { cube, wireframe } = this.drawCube({
      width,
      height,
      length,
      color: tunnelMaterialColor,
      tunnel,
      opacity: 0,
    });

    const group = new THREE.Group();
    const cubeParams = cube?.geometry?.parameters;

    if (!cubeParams?.width) {
      return null;
    }

    const along_length = true;
    const along_width = false;

    if (baseType === 'tube') {
      this.createTubeBaseParts(cubeParams, group);
    } else if (baseType === 'c_channel') {
      this.createCChannelBaseParts(cubeParams, group, along_length, along_width);
    } else {
      console.warn(`Unknown base type: ${baseType}`);
    }

    return { cube, wireframe, group };
  }

  /** drawTabularSteel (canvas.view3d:3076-3098) — tube-base bar (fill 0.4 + black edges). */
  drawTabularSteel(params: any, callback?: (r: { shape: any; wireframe: any }) => void): void {
    const { width, height, depth, color } = params;

    const geometry = new THREE.BoxBufferGeometry(width, height, depth);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.4,
    });
    const mesh = new THREE.Mesh(geometry, material);
    const wireframe = new THREE.LineSegments(
      new THREE.EdgesGeometry(geometry),
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    if (callback) callback({ shape: mesh, wireframe });
  }

  /**
   * drawCChannel (canvas.view3d:3100-3139) — extruded C-profile. The Shape
   * outline thickness 0.2 is RAW (not coef-scaled) — quirk, keep (G6).
   */
  drawCChannel(params: any, callback?: (r: { shape: any; wireframe: any }) => void): void {
    const { width, height, thickness, depth, color } = params;

    const shape: any = new THREE.Shape();
    shape.renderOrder = 1111111; // verbatim (:3108) — meaningless on a Shape, kept.
    shape.moveTo(0, 0);
    shape.lineTo(width, 0);
    shape.lineTo(width, thickness);
    shape.lineTo(thickness, thickness);
    shape.lineTo(thickness, height - thickness);
    shape.lineTo(width, height - thickness);
    shape.lineTo(width, height);
    shape.lineTo(0, height);
    shape.lineTo(0, 0);

    const extrudeSettings = {
      depth,
      bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.4,
    });
    const mesh = new THREE.Mesh(geometry, material);
    const wireframe = new THREE.LineSegments(
      new THREE.EdgesGeometry(geometry as any),
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    if (callback) callback({ shape: mesh, wireframe });
  }

  /** createTubeBaseParts (canvas.view3d:5302-5333): 4 bars (2 ends, 2 sides). */
  createTubeBaseParts(cubeParams: any, group: THREE.Group): void {
    const barWidth = this.coef * 2;
    const barThickness = 0.2 * this.coef;
    const color = tunnelMaterialColor;

    const positions = [
      { width: barWidth, height: cubeParams.height, depth: cubeParams.depth, x: -cubeParams.width / 2 + barWidth / 2, y: 0, z: 0 },
      { width: barWidth, height: cubeParams.height, depth: cubeParams.depth, x: cubeParams.width / 2 - barWidth / 2, y: 0, z: 0 },
      { width: cubeParams.width, height: cubeParams.height, depth: barWidth, x: 0, y: 0, z: -cubeParams.depth / 2 + barWidth / 2 },
      { width: cubeParams.width, height: cubeParams.height, depth: barWidth, x: 0, y: 0, z: cubeParams.depth / 2 - barWidth / 2 },
    ];

    positions.forEach(({ width, height, depth, x, y = 0, z = 0 }) => {
      this.drawTabularSteel(
        {
          width,
          height,
          thickness: barThickness,
          depth,
          color,
        },
        ({ shape, wireframe }) => {
          shape.position.set(x, y, z);
          wireframe.position.copy(shape.position);
          // legacy `rotate` config key is never set on these 4 bars (:5325).
          group.add(shape, wireframe);
        }
      );
    });
  }

  /** createCChannelBaseParts (canvas.view3d:5335-5377): 4 corner C-profiles. */
  createCChannelBaseParts(
    cubeParams: any,
    group: THREE.Group,
    along_length: boolean,
    along_width: boolean
  ): void {
    const thickness = 0.2;
    const channelWidth = 2 * thickness;
    const color = tunnelMaterialColor;

    const horizontalDepth = cubeParams.depth - 2 * channelWidth;
    const verticalDepth = cubeParams.width - 2 * channelWidth;

    const configsLength = [
      { depth: horizontalDepth, pos: { x: cubeParams.width / 2 - channelWidth, y: -cubeParams.height / 2, z: -cubeParams.depth / 2 + channelWidth }, rot: { y: 0, z: 0 } },
      { depth: cubeParams.width, pos: { x: -cubeParams.width / 2, y: -cubeParams.height / 2, z: -cubeParams.depth / 2 + channelWidth }, rot: { y: Math.PI / 2, z: 0 } },
      { depth: horizontalDepth, pos: { x: -cubeParams.width / 2 + channelWidth, y: -cubeParams.height / 2, z: cubeParams.depth / 2 - channelWidth }, rot: { y: Math.PI, z: 0 } },
      { depth: cubeParams.width, pos: { x: cubeParams.width / 2, y: -cubeParams.height / 2, z: cubeParams.depth / 2 - channelWidth }, rot: { y: -Math.PI / 2, z: 0 } },
    ];

    const configsWidth = [
      { depth: cubeParams.depth, pos: { x: cubeParams.width / 2 - channelWidth, y: -cubeParams.height / 2, z: -cubeParams.depth / 2 }, rot: { y: 0, z: 0 } },
      { depth: verticalDepth, pos: { x: -cubeParams.width / 2 + channelWidth, y: -cubeParams.height / 2, z: -cubeParams.depth / 2 + channelWidth }, rot: { y: Math.PI / 2, z: 0 } },
      { depth: cubeParams.depth, pos: { x: -cubeParams.width / 2 + channelWidth, y: -cubeParams.height / 2, z: cubeParams.depth / 2 }, rot: { y: Math.PI, z: 0 } },
      { depth: verticalDepth, pos: { x: cubeParams.width / 2 - channelWidth, y: -cubeParams.height / 2, z: cubeParams.depth / 2 - channelWidth }, rot: { y: -Math.PI / 2, z: 0 } },
    ];

    const cChannelConfigs = along_length ? configsLength : along_width ? configsWidth : configsLength;

    cChannelConfigs.forEach((cfg) => {
      this.drawCChannel(
        {
          width: channelWidth,
          height: cubeParams.height,
          thickness,
          depth: cfg.depth,
          color,
        },
        ({ shape, wireframe }) => {
          shape.rotation.y = cfg.rot.y;
          shape.rotation.z = cfg.rot.z;
          shape.position.set(cfg.pos.x, cfg.pos.y, cfg.pos.z);

          wireframe.rotation.copy(shape.rotation);
          wireframe.position.copy(shape.position);

          group.add(shape, wireframe);
        }
      );
    });
  }

  /**
   * calculateTunnelBasePosition (canvas.view3d:3533-3546): anchored to the
   * tunnel's lowest-X section cube. NB the outer `point` re-multiplies unit
   * (`unit * x` where x already carries unit) — keep verbatim (G6). The sort
   * comparator returns a BOOLEAN (coerced 1/0 — never -1); Number() keeps the
   * engine-identical partial-sort semantics.
   */
  calculateTunnelBasePosition(cube: any, wireframe: any, tunnel: any, length: number): void {
    const el = this.sectionCanvasObjects
      .filter((s: any) => s.tunnel.id === tunnel.id)
      .sort((a: any, b: any) => Number(a.cube.position.x > b.cube.position.x))[0];
    if (!el) return;
    const unit = -2 * tunnel.get('airflow_direction_id') + 3;
    const x = (unit * (length - el.cube.geometry.parameters.width)) / 2 + unit * el.cube.position.x;
    const index = tunnel.get('index');
    const y =
      this.tunnelsByIndexes[index].y -
      this.tunnelsByIndexes[index].height / 2 -
      (tunnel.get('base')?.height * this.coef) / 2;
    const z = this.tunnelsByIndexes[index].z;
    const point = new THREE.Vector3(unit * x, y, z);
    cube.position.copy(point);
    wireframe.position.copy(point);
  }

  /**
   * normalizeTunnelBase (canvas.view3d:3548-3569): in-place wrapper-cube
   * geometry swap + reposition (the `loading` pass), immediate render.
   */
  normalizeTunnelBase(tunnel: any): void {
    if (tunnel?.get('base') && this.tunnelBases[tunnel.get('id')]) {
      const baseCube = this.tunnelBases[tunnel.get('id')].cube;
      const width = tunnel.get('length') * this.coef;
      const height = tunnel.get('base').height * this.coef;
      const depth = tunnel.get('width') * this.coef;
      const baseCubeGeometry = new THREE.BoxBufferGeometry(width, height, depth);
      this.disposeChilds(baseCube);
      baseCube.geometry.dispose();
      baseCube.geometry = baseCubeGeometry;

      const baseWireframe = this.tunnelBases[tunnel.get('id')].wireframe;
      const wireframeGeometry = new THREE.EdgesGeometry(baseCube.geometry);
      this.disposeChilds(baseWireframe);
      baseWireframe.geometry.dispose();
      baseWireframe.geometry = wireframeGeometry;

      this.calculateTunnelBasePosition(baseCube, baseWireframe, tunnel, width);
      this.renderer.render(this.scene, this.camera);
    }
  }

  /** disposeAndRemoveTunnelBase (canvas.view3d:2984-2989). */
  disposeAndRemoveTunnelBase(tunnel_id: number | string): void {
    if (!this.tunnelBases[tunnel_id]) return;
    this.disposeChilds(this.tunnelBases[tunnel_id].group);
    this.group.remove(this.tunnelBases[tunnel_id].group);
    delete this.tunnelBases[tunnel_id];
  }

  // =========================================================================
  // G39 (subset) — SECTION DESTROY / SCENE REMOVAL
  // =========================================================================

  /**
   * destroySection (canvas.view3d:2991-3039): removes cube/wireframe/labels +
   * the tunnel wireframe and base; Plate/Rotary/Heatex names also kill the
   * PARTNER tunnel's wireframe + base (`sitting_on_id ?? id + 1`); clears the
   * section's accessories from the registry; un-disables `.auto_360`; returns
   * "tunnel now empty".
   */
  destroySection(id: number): boolean {
    const sectionIndex = this.sectionCanvasObjects.findIndex(
      (obj: any) =>
        obj.section.get('id') === id ||
        obj.section.get('section_id') === id ||
        obj.section.get('sitting_on_id') === id
    );
    const tunnel = this.sectionCanvasObjects[sectionIndex]?.tunnel;

    if (this.tunnelBases[tunnel?.id]) {
      this.disposeAndRemoveTunnelBase(tunnel.id);
    }
    if (sectionIndex !== -1) {
      const name_on_canvas = this.sectionCanvasObjects[sectionIndex].section.name_on_canvas;
      const name = this.sectionCanvasObjects[sectionIndex].section.get('name');
      const sitting_on_id = this.sectionCanvasObjects[sectionIndex].section.get('sitting_on_id');
      this.removeSectionObjFromScene(sectionIndex);
      this.sectionCanvasObjects.splice(sectionIndex, 1);
      this.removeTunnelWireframe(tunnel?.id);
      if (
        name_on_canvas === 'Plate' ||
        name_on_canvas === 'Rotary' ||
        name === 'Rotary Heatex' ||
        name === 'Plate Heatex' ||
        name === 'Heat' ||
        name === 'Wheel' ||
        name === 'Heat Wheel'
      ) {
        const el_id = sitting_on_id ?? id + 1;
        const tun_id = this.sectionCanvasObjectsObj[el_id]?.tunnel?.id;
        this.removeTunnelWireframe(tun_id);
        if (this.tunnelBases[tun_id]) {
          this.disposeAndRemoveTunnelBase(tun_id);
        }
      }
    }
    this.accessoryCanvasObjects = this.accessoryCanvasObjects.filter((el: any) => el.section.id !== id);
    this.onRotateAvailabilityClear('disable'); // $(".auto_360…").removeClass('disable') (:3017)
    return !this.sectionCanvasObjects.find((obj: any) => obj.tunnel.get('id') === tunnel?.get('id'));
  }

  /**
   * removeSectionObjFromScene (canvas.view3d:3062-3074). Verbatim: only
   * cube/wireframe/textLabel get the extra disposeDetails pass (NOT
   * textLabelRotated — legacy asymmetry, keep).
   */
  removeSectionObjFromScene(sectionIndex: number): void {
    this.disposeChilds(this.sectionCanvasObjects[sectionIndex].cube);
    this.disposeChilds(this.sectionCanvasObjects[sectionIndex].wireframe);
    this.disposeChilds(this.sectionCanvasObjects[sectionIndex].textLabel);
    this.disposeChilds(this.sectionCanvasObjects[sectionIndex].textLabelRotated);
    this.group.remove(this.sectionCanvasObjects[sectionIndex].cube);
    this.group.remove(this.sectionCanvasObjects[sectionIndex].wireframe);
    this.group.remove(this.sectionCanvasObjects[sectionIndex].textLabel);
    this.group.remove(this.sectionCanvasObjects[sectionIndex].textLabelRotated);
    this.disposeDetails(this.sectionCanvasObjects[sectionIndex].cube);
    this.disposeDetails(this.sectionCanvasObjects[sectionIndex].wireframe);
    this.disposeDetails(this.sectionCanvasObjects[sectionIndex].textLabel);
  }

  /** disposeDetails (canvas.view3d:3570-3581). */
  disposeDetails(obj: any): void {
    if (obj) {
      obj.geometry.dispose();
      if (Array.isArray(obj.material)) {
        obj.material.forEach((material: any) => material.dispose());
      } else {
        obj.material.dispose();
      }
    }
  }

  // =========================================================================
  // G2 — SECTION NAME LABELS
  // =========================================================================

  /**
   * createTextLabel (canvas.view3d:3922-3941): TextBufferGeometry over the
   * loaded Font, wireframe MeshBasicMaterial (the "thin" look IS the
   * wireframe render), depthTest off + renderOrder 105 unless
   * `removedepthTest` (airflow labels G33). [NEG]#101: the 7th arg of the
   * :5509 call site is dropped (signature takes 6).
   */
  createTextLabel(
    font: THREE.Font = this.font,
    cube: { name?: string; removedepthTest?: boolean },
    color: number = 0x383838,
    rotate = true,
    size = 0.55,
    height = 0.04
  ): any {
    const textGeometry = new THREE.TextBufferGeometry(cube.name || '', {
      font,
      size,
      height,
    });
    const textMaterial = new THREE.MeshBasicMaterial({ color, wireframe: true });
    const textLabel: any = new THREE.Mesh(textGeometry, textMaterial);
    if (rotate) {
      textGeometry.rotateX(-Math.PI / 2);
    }
    textLabel.material.transparent = true;
    if (!cube.removedepthTest) textLabel.material.depthTest = false;
    textLabel.material.blending = THREE.NormalBlending;
    if (!cube.removedepthTest) textLabel.renderOrder = 105;
    return textLabel;
  }

  /**
   * calculateSectionName (canvas.view3d:4857-4874) — the 4-attempt fit
   * against cube_size with the +0.5 margin: ① tag||name ② tag?name:shortName
   * ③ name ④ shortName.
   */
  calculateSectionName(cube_size: number, name: string, shortName: string, tag: string | undefined, color: number): any {
    tag = tag?.trim();
    let textLabel: any;
    let x: number;
    textLabel = this.createTextLabel(this.font, { name: tag ? tag : name }, color);
    x = this.getTextWidth(textLabel).x + 0.5;
    if (x > cube_size) {
      textLabel = this.createTextLabel(this.font, { name: tag ? name : shortName }, color);
      x = this.getTextWidth(textLabel).x + 0.5;
    } else return textLabel;
    if (x > cube_size && tag) {
      textLabel = this.createTextLabel(this.font, { name }, color);
      x = this.getTextWidth(textLabel).x + 0.5;
    } else return textLabel;
    if (x > cube_size && tag) {
      textLabel = this.createTextLabel(this.font, { name: shortName }, color);
    }
    return textLabel;
  }

  /** getTextWidth (canvas.view3d:4789-4797). */
  getTextWidth(textLabel: any): { x: number; y: number; z: number } {
    textLabel.geometry.computeBoundingBox();
    const textBoundingBox = textLabel.geometry.boundingBox;
    return {
      x: textBoundingBox.max.x - textBoundingBox.min.x,
      y: textBoundingBox.max.y - textBoundingBox.min.y,
      z: textBoundingBox.max.z - textBoundingBox.min.z,
    };
  }

  /**
   * getSectionNameColor (canvas.view3d:5737-5741): colors-off or plenum →
   * grey; required_length truthy OR named UDC → green; else red. The UDC
   * stateStorage probe is the `udc_has_name` row field (see CanvasSection).
   *
   * DELIBERATE DEVIATION from dev, third clause only. `required_length` is
   * derived from AHUGasHeater.min_aw_length, whose first branch is
   * `if not self.master_gas_heater: return None` — and a DIRECT FIRE heater
   * never persists a master_gas_heater in either app: legacy's
   * searchGasHeaters puts `master_gas_heater_id` in the PATCH only on its
   * indirect branches (gas_heater.selector.js:2073/:2101), never on the
   * type-3 one (:2005/:2037), and selection_validations exempts direct fire
   * from needing one (models_component_gas_heater.py:1011). So dev paints a
   * fully selected direct-fire heater red, and required_length cannot tell
   * "not selected" from "selected, direct fire".
   *
   * `model` can (models_component_gas_heater.py:407): burner-assembly name
   * for direct fire, master_gas_heater.model for id_tubular/id_drum_tube.
   * That is legacy's own selected-test in the rating dialog — Print/Select
   * are gated on `!master_gas_heater_id && !model` (:2585) — so this reuses
   * the marker the old app already trusted, rather than inventing one.
   */
  getSectionNameColor(section: any): number {
    if (!this.showColor || section.get('type') === 'plenum') return 0x383838;
    if (
      section.get('required_length') ||
      (section.get('type') === 'user_defined_component' && !!section.get('udc_has_name')) ||
      (section.get('type') === 'gas_heater' && !!section.get('gh_has_model'))
    )
      return 0x006834;
    return 0xa72121;
  }

  /**
   * updateSectionName (canvas.view3d:4798-4832) — `change:section:tag:canvas`
   * receiver: rebuilds BOTH labels in place preserving positions + the
   * view-dependent deltas (parity 254).
   */
  updateSectionName(section_id: number, tag: string): void {
    this.need_update = true;
    const selected = this.sectionCanvasObjects.find((el: any) => el.section.id === section_id);
    if (!selected) return;
    const color = this.getSectionNameColor(selected.section);
    const text = this.calculateSectionName(
      selected.cube.geometry.parameters.depth,
      selected.name,
      selected.shortName,
      tag,
      color
    );
    const textLabelRotated = this.calculateSectionName(
      selected.cube.geometry.parameters.height,
      selected.name,
      selected.shortName,
      tag,
      color
    );
    text.position.copy(selected.textLabel.position);
    textLabelRotated.position.copy(selected.textLabelRotated.position);
    const unit =
      this.text_view_position === 'back_left' || this.text_view_position === 'back_right' ? -1 : 1;
    const textBoundingBox = text.geometry.boundingBox;
    text.rotation.y = (unit * Math.PI) / 2;
    text.position.z += (unit * (this.getTextWidth(text).x - this.getTextWidth(selected.textLabel).x)) / 2;
    text.position.x = (unit * (textBoundingBox.max.z - textBoundingBox.min.z)) / 2;
    this.disposeChilds(selected.textLabel);
    this.disposeChilds(selected.textLabelRotated);
    selected.cube.remove(selected.textLabel);
    selected.cube.remove(selected.textLabelRotated);
    textLabelRotated.rotation.y = (unit * Math.PI) / 2;
    textLabelRotated.rotation.x = Math.PI / 2;
    if (this.text_view_position === 'front_left' || this.text_view_position === 'back_right') {
      textLabelRotated.position.z = -selected.cube.geometry.parameters.depth / 2;
    }
    if (unit < 1)
      textLabelRotated.position.y +=
        (this.getTextWidth(textLabelRotated).x - this.getTextWidth(selected.textLabelRotated).x) / 2;
    else
      textLabelRotated.position.y -=
        (this.getTextWidth(textLabelRotated).x - this.getTextWidth(selected.textLabelRotated).x) / 2;
    const textBoundingBoxRotated = textLabelRotated.geometry.boundingBox;
    const textWidthRotated = textBoundingBoxRotated.max.x - textBoundingBoxRotated.min.x;
    this.getTextPosition(this.text_view_position, textLabelRotated, selected.cube, textWidthRotated);
    selected.textLabel = text;
    selected.textLabelRotated = textLabelRotated;
    const text_position_changed = this.text_position_changed || this.text_position;
    if (text_position_changed === 'top') textLabelRotated.visible = false;
    else text.visible = false;
    selected.cube.add(text);
    selected.cube.add(textLabelRotated);
  }

  // =========================================================================
  // G23 — DIMENSION PRIMITIVES
  // =========================================================================

  /** drawLines (canvas.view3d:4003-4012): 0x757575 line, depthTest off, renderOrder 100. */
  drawLines(points: THREE.Vector3[]): any {
    const material = new THREE.LineBasicMaterial({ color: dimensionsColor });
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const line: any = new THREE.Line(geometry, material);
    line.material.transparent = true;
    line.material.depthTest = false;
    line.material.blending = THREE.NormalBlending;
    line.renderOrder = 100;
    return line;
  }

  /** drawLinesMany (canvas.view3d:4013-4023). [NEG]#102: loop var scoped. */
  drawLinesMany(
    points_1: THREE.Vector3[],
    points_2: THREE.Vector3[],
    position_1: THREE.Vector3,
    position_2: THREE.Vector3,
    count = 1
  ): Array<{ line_1: any; line_2: any }> {
    const list: Array<{ line_1: any; line_2: any }> = [];
    for (let i = 0; i < count; i++) {
      const line_1 = this.drawLines(points_1);
      const line_2 = this.drawLines(points_2);
      line_1.position.copy(position_1);
      line_2.position.copy(position_2);
      list.push({ line_1, line_2 });
    }
    return list;
  }

  /** getPoints (canvas.view3d:4024-4030) — the dimension "L" point set. */
  getPoints(a: number, b: number): THREE.Vector3[] {
    return [new THREE.Vector3(0, 0, a), new THREE.Vector3(0, 0, 0), new THREE.Vector3(b, 0, 0)];
  }

  /** createTextLabelMany (canvas.view3d:4031-4037). [NEG]#102: loop var scoped. */
  createTextLabelMany(name: string, count = 1): any[] {
    const list: any[] = [];
    for (let i = 0; i < count; i++) {
      list.push(this.createTextLabel(this.font, { name }, dimensionsNumberColor, true, 0.55, 0.015));
    }
    return list;
  }

  /** createGroup (canvas.view3d:4038-4046) — initially invisible, child of the anchor cube. */
  createGroup(cube: any, textLable: any, line_1: any, line_2: any): THREE.Group {
    const group = new THREE.Group();
    group.add(textLable);
    group.add(line_1);
    group.add(line_2);
    group.visible = false;
    cube.add(group);
    return group;
  }

  /** remove_dimensions (canvas.view3d:4688-4699) — disposes up to 5 group variants. */
  remove_dimensions(cube: any, elements: any): void {
    this.disposeChilds(elements.group);
    this.disposeChilds(elements.group_rotated);
    this.disposeChilds(elements.group_rotated_1);
    this.disposeChilds(elements.group_rotated_2);
    this.disposeChilds(elements.group_rotated_3);
    cube.remove(elements.group);
    cube.remove(elements.group_rotated);
    cube.remove(elements.group_rotated_1);
    cube.remove(elements.group_rotated_2);
    cube.remove(elements.group_rotated_3);
  }

  // =========================================================================
  // §40 — GLOBAL X RECENTERING + G24-G27 ORCHESTRATION
  // =========================================================================

  /**
   * drawAllTunnelsWidthAndHeight (canvas.view3d:4148-4164): max_x/min_x over
   * all section cubes → group + wireframeGroup X recenter; max/min_point
   * dimension anchors; per-tunnel width/height (+ AHU height) draws.
   */
  drawAllTunnelsWidthAndHeight(): void {
    const x_positions_max = this.sectionCanvasObjects.map(
      (el: any) => el.cube.position.x + el.cube.geometry.parameters.width / 2
    );
    const x_positions_min = this.sectionCanvasObjects.map(
      (el: any) => el.cube.position.x - el.cube.geometry.parameters.width / 2
    );
    const max_x = Math.max(...x_positions_max);
    const min_x = Math.min(...x_positions_min);
    this.max_x = max_x;
    const max_point = max_x + 1.5;
    const min_point = min_x - 3;
    const global_x = max_x - min_x - this.max_length * this.coef;
    this.group.position.x = global_x;
    this.tunnelsWireframeGroup.position.x = global_x;
    Object.values(this.tunnelsDimensionsInfo).forEach(
      ({ cube, width, tunnel, max_length, section_length, start_x, height, tunnel_index, drawAHUHeight }: any) => {
        this.drawTunnelWidth({ cube, width, tunnel, max_length, section_length, start_x, height, tunnel_index: 1, max_point, min_point });
        this.drawTunnelHeight({ cube, width, tunnel, max_length, section_length, start_x, height, tunnel_index, max_point, min_point });
        if (drawAHUHeight)
          this.drawAHUHeight({
            cube,
            section_length,
            height: this.ahu_height,
            tunnel,
            max_length,
            tunnel_index,
            start_x,
            // :4162 — `(2 * base?.height || 0)`: NaN-guard via ||, verbatim.
            section_height:
              this.ahu_height - (tunnel.get('height') + (2 * tunnel.get('base')?.height || 0)) * this.coef,
            max_x,
            min_x,
          });
      }
    );
  }

  /** setTunnelWidthDimensionsPosition (canvas.view3d:4047-4062). */
  setTunnelWidthDimensionsPosition(
    positionText: THREE.Vector3,
    textLable: any,
    textLableRotated: any,
    line_2: any,
    line_1_rotated: any,
    line_2_rotated: any,
    x_for_another_view: number,
    name_height: number,
    name_width: number
  ): void {
    textLable.rotation.y = Math.PI / 2;
    textLable.position.copy(positionText);
    textLableRotated.position.copy(positionText);
    line_2.rotation.y = Math.PI / 2;

    line_1_rotated.position.x = x_for_another_view;
    line_2_rotated.position.x = x_for_another_view;
    textLableRotated.position.x = x_for_another_view - name_height / 2;
    textLableRotated.position.z = -name_width / 2;

    line_1_rotated.rotation.z = Math.PI;
    line_2_rotated.rotation.z = Math.PI;
    line_2_rotated.rotation.y = -Math.PI / 2;
    textLableRotated.rotation.y = -Math.PI / 2;
  }

  /**
   * G24 drawTunnelWidth (canvas.view3d:4165-4216). The :4207-4215 "cascade"
   * is observably inert — `tunnel_id === tunnel.id` is always true so the
   * loop body always early-returns; only `tunnel_max_length` updates.
   * Replicated, not fixed.
   */
  drawTunnelWidth({ width, cube, tunnel, max_length, tunnel_index, section_length, max_point, min_point }: any): void {
    const extra_width = -cube.position.x + max_point + tunnel_index * 1;
    const tunnel_id = tunnel.id;
    const height = (tunnel.get('height') / 2 + (tunnel.get('base')?.height || 0)) * this.coef;
    const [textLable, textLableRotated] = this.createTextLabelMany(`${tunnel.get('width').toFixed(2)}`, 2);
    const { x, z } = this.getTextWidth(textLable);
    const name_width = x;
    const name_height = z;
    const positionText = new THREE.Vector3(extra_width + name_height / 2, -height - 0.05, name_width / 2);
    const lineLength = width / 2 - name_width > 0 ? width / 2 - name_width : 0.2;
    const points_1 = this.getPoints(lineLength, -extra_width + section_length / 2 + 0.5);
    const points_2 = this.getPoints(-extra_width + section_length / 2 + 0.5, lineLength);
    const position_1 = new THREE.Vector3(extra_width, -height, (-tunnel.get('width') * this.coef) / 2);
    const position_2 = new THREE.Vector3(extra_width, -height, (tunnel.get('width') * this.coef) / 2);
    const [{ line_1, line_2 }, { line_1: line_1_rotated, line_2: line_2_rotated }] = this.drawLinesMany(
      points_1,
      points_2,
      position_1,
      position_2,
      2
    );

    const x_for_another_view = min_point - cube.position.x;
    this.setTunnelWidthDimensionsPosition(
      positionText,
      textLable,
      textLableRotated,
      line_2,
      line_1_rotated,
      line_2_rotated,
      x_for_another_view,
      name_height,
      name_width
    );

    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated);

    cube.group_width_rotated = group_rotated;
    cube.group_width = group;

    if (this.text_view_position === 'back_right' || this.text_view_position === 'back_left')
      group_rotated.visible = true;
    else group.visible = true;

    this.tunnelsDimensions[tunnel.id] = {
      cube,
      width_elements: {
        group,
        group_rotated,
      },
      tunnel,
      width,
      tunnel_index,
      tunnel_id: tunnel.id,
    };
    if (this.tunnel_max_length && this.tunnel_max_length != max_length) {
      this.tunnel_max_length = max_length;
      Object.values(this.tunnelsDimensions).forEach(() => {
        if (tunnel_id === tunnel.id) return; // always true — inert by design (:4210)
        // unreachable (verbatim legacy dead path)
      });
    }
    this.tunnel_max_length = max_length;
  }

  /** setTunnelHeightDimensionsPosition (canvas.view3d:4063-4113) — the 4-variant rotation matrix. */
  setTunnelHeightDimensionsPosition(
    x_for_another_view: number,
    positionText: THREE.Vector3,
    textLable: any,
    textLableRotated: any,
    textLableRotated_2: any,
    textLableRotated_3: any,
    line_1: any,
    line_2: any,
    line_1_rotated: any,
    line_2_rotated: any,
    line_1_rotated_2: any,
    line_2_rotated_2: any,
    line_1_rotated_3: any,
    line_2_rotated_3: any,
    name_height: number,
    name_width: number
  ): void {
    textLable.rotation.x = Math.PI / 2;
    textLable.rotation.y = Math.PI / 2;
    textLableRotated.rotation.x = -Math.PI / 2;
    textLableRotated.rotation.y = -Math.PI / 2;
    textLableRotated_2.rotation.x = Math.PI / 2;
    textLableRotated_2.rotation.y = Math.PI / 2;
    textLableRotated_2.rotation.z = Math.PI;
    textLableRotated_3.rotation.x = -Math.PI / 2;
    textLableRotated_3.rotation.y = -Math.PI / 2;
    textLableRotated_3.rotation.z = -Math.PI;

    textLable.position.copy(positionText);
    textLableRotated.position.copy(positionText);
    textLableRotated_2.position.copy(positionText);
    textLableRotated_3.position.copy(positionText);

    textLableRotated.position.x = x_for_another_view - name_height / 2;
    textLableRotated_2.position.x += name_height / 2;
    textLableRotated_2.position.y += name_width;
    textLableRotated_3.position.y += name_width;
    textLableRotated_3.position.x = x_for_another_view - name_height / 2;

    line_2.rotation.x = Math.PI / 2;
    line_2.rotation.y = Math.PI / 2;
    line_1.rotation.x = Math.PI / 2;

    line_2_rotated.rotation.x = Math.PI / 2;
    line_2_rotated.rotation.y = -Math.PI / 2;
    line_1_rotated.rotation.x = Math.PI / 2;
    line_1_rotated.rotation.z = Math.PI;
    line_2_rotated.rotation.z = Math.PI;

    line_2_rotated_2.rotation.x = Math.PI / 2;
    line_2_rotated_2.rotation.y = Math.PI / 2;
    line_1_rotated_2.rotation.x = Math.PI / 2;

    line_2_rotated_3.rotation.x = Math.PI / 2;
    line_2_rotated_3.rotation.y = -Math.PI / 2;
    line_1_rotated_3.rotation.x = Math.PI / 2;
    line_1_rotated_3.rotation.z = Math.PI;
    line_2_rotated_3.rotation.z = Math.PI;

    line_1_rotated.position.x = x_for_another_view;
    line_2_rotated.position.x = x_for_another_view;

    line_1_rotated_3.position.x = x_for_another_view;
    line_2_rotated_3.position.x = x_for_another_view;
  }

  /**
   * G25 drawTunnelHeight (canvas.view3d:4217-4277): 4 view variants, legs at
   * x = 1 + extra_width; chains the base-height dim (G26) when based.
   */
  drawTunnelHeight({ height, cube, tunnel, max_length, section_length, start_x, max_point, min_point }: any): void {
    const base = (tunnel.get('base')?.height || 0) * this.coef;
    if (this.tunnelsHeightDimensions[tunnel.id])
      this.remove_dimensions(
        this.tunnelsHeightDimensions[tunnel.id].cube,
        this.tunnelsHeightDimensions[tunnel.id].height_elements
      );
    const extra_width = -cube.position.x + max_point;
    const [textLable, textLableRotated, textLableRotated_2, textLableRotated_3] = this.createTextLabelMany(
      `${(height / this.coef).toFixed(2)}`,
      4
    );
    const { x, y, z } = this.getTextWidth(textLable);
    const name_width = x;
    const name_height = z;
    const positionText = new THREE.Vector3(
      0.5 + extra_width + name_height,
      -name_width / 2,
      (tunnel.get('width') / 2) * this.coef - y / 2
    );
    const position_1 = new THREE.Vector3(1 + extra_width, height / 2, (tunnel.get('width') * this.coef) / 2);
    const position_2 = new THREE.Vector3(1 + extra_width, -height / 2, (tunnel.get('width') * this.coef) / 2);

    const lineLength = height / 2 - name_width > 0 ? height / 2 - name_width : 0.2;
    const points_1 = this.getPoints(lineLength, -extra_width);
    const points_2 = this.getPoints(-extra_width, lineLength);

    const [
      { line_1, line_2 },
      { line_1: line_1_rotated, line_2: line_2_rotated },
      { line_1: line_1_rotated_2, line_2: line_2_rotated_2 },
      { line_1: line_1_rotated_3, line_2: line_2_rotated_3 },
    ] = this.drawLinesMany(points_1, points_2, position_1, position_2, 4);

    const x_for_another_view = min_point - cube.position.x;
    this.setTunnelHeightDimensionsPosition(
      x_for_another_view,
      positionText,
      textLable,
      textLableRotated,
      textLableRotated_2,
      textLableRotated_3,
      line_1,
      line_2,
      line_1_rotated,
      line_2_rotated,
      line_1_rotated_2,
      line_2_rotated_2,
      line_1_rotated_3,
      line_2_rotated_3,
      name_height,
      name_width
    );

    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated);
    const group_rotated_2 = this.createGroup(cube, textLableRotated_2, line_1_rotated_2, line_2_rotated_2);
    const group_rotated_3 = this.createGroup(cube, textLableRotated_3, line_1_rotated_3, line_2_rotated_3);

    if (this.text_view_position === 'back_right') group_rotated.visible = true;
    else if (this.text_view_position === 'front_left') group_rotated_2.visible = true;
    else if (this.text_view_position === 'back_left') group_rotated_3.visible = true;
    else if (this.text_view_position !== 'plan') group.visible = true;

    cube.group_height_rotated = group_rotated;
    cube.group_height_rotated_2 = group_rotated_2;
    cube.group_height_rotated_3 = group_rotated_3;
    cube.group_height = group;

    this.tunnelsHeightDimensions[tunnel.id] = {
      cube,
      height_elements: {
        group,
        group_rotated,
        group_rotated_2,
        group_rotated_3,
      },
      tunnel_id: tunnel.id,
    };
    if (base) {
      this.drawTunnelBaseDimensions({ height: height + base, cube, tunnel, max_length, section_length, start_x, max_point, min_point });
    }
  }

  /**
   * G26 drawTunnelBaseDimensions (canvas.view3d:4385-4444): same 4-variant
   * structure for `base.height.toFixed(2)`, hung below the tunnel; rotated
   * variants shifted x −= 1.
   */
  drawTunnelBaseDimensions({ height, cube, tunnel, max_point, min_point }: any): void {
    const height_for_draw = tunnel.get('base')?.height;
    const base = height_for_draw * this.coef;
    if (this.tunnelsBaseDimensions[tunnel.id])
      this.remove_dimensions(
        this.tunnelsBaseDimensions[tunnel.id].cube,
        this.tunnelsBaseDimensions[tunnel.id].height_elements
      );
    const extra_width = -cube.position.x + max_point;
    const [textLable, textLableRotated, textLableRotated_2, textLableRotated_3] = this.createTextLabelMany(
      `${height_for_draw.toFixed(2)}`,
      4
    );
    const { x, y, z } = this.getTextWidth(textLable);
    const name_width = x;
    const name_height = z;
    const positionText = new THREE.Vector3(
      0.5 + extra_width + name_height + 1,
      -base / 2 - height / 2 + 0.1,
      (tunnel.get('width') / 2) * this.coef - y / 2
    );
    const position_1 = new THREE.Vector3(1 + extra_width, base / 2 - height / 2, (tunnel.get('width') * this.coef) / 2);
    const position_2 = new THREE.Vector3(1 + extra_width, -height / 2 - base / 2, (tunnel.get('width') * this.coef) / 2);

    const lineLength = base / 2 - name_width > 0 ? base / 2 - name_width : 0.2;
    const points_1 = this.getPoints(lineLength, -extra_width);
    const points_2 = this.getPoints(-extra_width, lineLength);

    const [
      { line_1, line_2 },
      { line_1: line_1_rotated, line_2: line_2_rotated },
      { line_1: line_1_rotated_2, line_2: line_2_rotated_2 },
      { line_1: line_1_rotated_3, line_2: line_2_rotated_3 },
    ] = this.drawLinesMany(points_1, points_2, position_1, position_2, 4);

    const x_for_another_view = min_point - cube.position.x;
    this.setTunnelHeightDimensionsPosition(
      x_for_another_view,
      positionText,
      textLable,
      textLableRotated,
      textLableRotated_2,
      textLableRotated_3,
      line_1,
      line_2,
      line_1_rotated,
      line_2_rotated,
      line_1_rotated_2,
      line_2_rotated_2,
      line_1_rotated_3,
      line_2_rotated_3,
      name_height,
      name_width
    );
    textLableRotated.position.x -= 1;
    textLableRotated_3.position.x -= 1;
    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated);
    const group_rotated_2 = this.createGroup(cube, textLableRotated_2, line_1_rotated_2, line_2_rotated_2);
    const group_rotated_3 = this.createGroup(cube, textLableRotated_3, line_1_rotated_3, line_2_rotated_3);

    if (this.text_view_position === 'back_right') group_rotated.visible = true;
    else if (this.text_view_position === 'front_left') group_rotated_2.visible = true;
    else if (this.text_view_position === 'back_left') group_rotated_3.visible = true;
    else if (this.text_view_position !== 'plan') group.visible = true;

    cube.group_base_height_rotated = group_rotated;
    cube.group_base_height_rotated_2 = group_rotated_2;
    cube.group_base_height_rotated_3 = group_rotated_3;
    cube.group_base_height = group;

    this.tunnelsBaseDimensions[tunnel.id] = {
      cube,
      height_elements: {
        group,
        group_rotated,
        group_rotated_2,
        group_rotated_3,
      },
      tunnel_id: tunnel.id,
    };
  }

  /**
   * G27 drawAHUHeight (canvas.view3d:4600-4656): single instance keyed
   * `tunnelsHeightDimensions[1]` (the render-loop + isolation gates read that
   * key — Part 1 §3.2.5), anchored max_x + 4 / min_x − 6.
   */
  drawAHUHeight({ height, cube, tunnel, section_height, max_x, min_x }: any): void {
    if (this.tunnelsHeightDimensions[1])
      this.remove_dimensions(this.tunnelsHeightDimensions[1].cube, this.tunnelsHeightDimensions[1].height_elements);
    const extra_width = -cube.position.x + max_x + 4;
    const [textLable, textLableRotated, textLableRotated_2, textLableRotated_3] = this.createTextLabelMany(
      `${(height / this.coef).toFixed(2)}`,
      4
    );
    const { x, y, z } = this.getTextWidth(textLable);
    const name_width = x;
    const name_height = z;
    const positionText = new THREE.Vector3(
      0.5 + extra_width + name_height,
      section_height / 2 - name_width / 2,
      (tunnel.get('width') / 2) * this.coef - y / 2
    );
    const position_1 = new THREE.Vector3(
      1 + extra_width,
      height / 2 + section_height / 2,
      (tunnel.get('width') * this.coef) / 2
    );
    const position_2 = new THREE.Vector3(
      1 + extra_width,
      -height / 2 + section_height / 2,
      (tunnel.get('width') * this.coef) / 2
    );

    const lineLength = height / 2 - name_width > 0 ? height / 2 - name_width : 0.2;
    const points_1 = this.getPoints(lineLength, -extra_width);
    const points_2 = this.getPoints(-extra_width, lineLength);

    const [
      { line_1, line_2 },
      { line_1: line_1_rotated, line_2: line_2_rotated },
      { line_1: line_1_rotated_2, line_2: line_2_rotated_2 },
      { line_1: line_1_rotated_3, line_2: line_2_rotated_3 },
    ] = this.drawLinesMany(points_1, points_2, position_1, position_2, 4);

    const x_for_another_view = min_x - cube.position.x - 6;
    this.setTunnelHeightDimensionsPosition(
      x_for_another_view,
      positionText,
      textLable,
      textLableRotated,
      textLableRotated_2,
      textLableRotated_3,
      line_1,
      line_2,
      line_1_rotated,
      line_2_rotated,
      line_1_rotated_2,
      line_2_rotated_2,
      line_1_rotated_3,
      line_2_rotated_3,
      name_height,
      name_width
    );

    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated);
    const group_rotated_2 = this.createGroup(cube, textLableRotated_2, line_1_rotated_2, line_2_rotated_2);
    const group_rotated_3 = this.createGroup(cube, textLableRotated_3, line_1_rotated_3, line_2_rotated_3);

    if (this.text_view_position === 'back_right') group_rotated.visible = true;
    else if (this.text_view_position === 'front_left') group_rotated_2.visible = true;
    else if (this.text_view_position === 'back_left') group_rotated_3.visible = true;
    else if (this.text_view_position !== 'plan') group.visible = true;

    cube.group_ahu_height_rotated = group_rotated;
    cube.group_ahu_height_rotated_2 = group_rotated_2;
    cube.group_ahu_height_rotated_3 = group_rotated_3;
    cube.group_ahu_height = group;

    this.tunnelsHeightDimensions[1] = {
      cube,
      height_elements: {
        group,
        group_rotated,
        group_rotated_2,
        group_rotated_3,
      },
      tunnel_id: 1,
    };
  }

  /** setLengthDimensionsPosition (canvas.view3d:4114-4133) — back/front-left variant, next_to early exit. */
  setLengthDimensionsPosition(
    positionText: THREE.Vector3,
    textLableRotated: any,
    line_1_rotated: any,
    line_2_rotated: any,
    x_for_another_view: number,
    name_width: number,
    name_height: number,
    extra_space: number,
    position_1: THREE.Vector3,
    position_2: THREE.Vector3
  ): void {
    textLableRotated.position.copy(positionText);
    line_2_rotated.rotation.z = 2 * Math.PI;
    line_1_rotated.rotation.y = -Math.PI / 2;
    line_1_rotated.position.copy(position_1);
    line_2_rotated.position.copy(position_2);
    textLableRotated.position.x += name_width;
    if (this.next_to) {
      textLableRotated.rotation.y = -Math.PI;
      textLableRotated.position.z -= name_height;
      return;
    }
    line_1_rotated.rotation.x = Math.PI;
    line_2_rotated.rotation.x = Math.PI;
    line_1_rotated.position.z = x_for_another_view;
    line_2_rotated.position.z = x_for_another_view;
    textLableRotated.position.z = x_for_another_view - extra_space;
    textLableRotated.rotation.y = -Math.PI;
    textLableRotated.position.z -= name_height / 2;
  }

  /** setLengthDimensionsPositionShort (canvas.view3d:4134-4147) — the plan `_rotated_1` variant. */
  setLengthDimensionsPositionShort(
    positionText: THREE.Vector3,
    textLable: any,
    line_1: any,
    line_2: any,
    position_1: THREE.Vector3,
    position_2: THREE.Vector3,
    unit: number,
    tunnel_index: number,
    space: number
  ): void {
    textLable.position.copy(positionText);
    line_1.position.copy(position_1);
    line_2.position.copy(position_2);
    line_2.rotation.z = 2 * Math.PI;
    line_1.rotation.y = -Math.PI / 2;
    textLable.position.z -= space;
    line_1.position.z -= space;
    line_2.position.z -= space;
    if (tunnel_index === 1) return;
    textLable.position.z += 2 * unit;
    line_1.position.z += 2 * unit;
    line_2.position.z += 2 * unit;
  }

  /**
   * G28 drawLength (canvas.view3d:4278-4384): count protocol (1 per-section /
   * 1.5 tunnel total / 3 re-draw with dmounts — G29 hook), coefCount band
   * offsets, reverse-tunnel mirroring, 3-tunnel next_to width subtraction,
   * the short-label stagger memory (0.8/1.65), three view variants.
   */
  drawLength(
    tunnel: any,
    tunnel_index: number,
    section_id: number,
    width: number,
    height: number,
    length: number,
    cube: any,
    count: number,
    section_length = 0,
    tunnel_id?: any,
    order = 0
  ): void {
    const base = (tunnel.get('base')?.height || 0) * this.coef;
    const unit = this.next_to ? 2 : tunnel_index + 1;
    const start_space = 0;
    const coefCount = (count === 1 ? 0.5 : count * 1.8) + start_space;
    const extra_width_1 = unit === 2 || tunnel.reverse ? unit : unit * 3;
    const extra_width = unit;
    const section_width = width;
    width += extra_width;
    const calc_height = -height / 2 - base;
    let calc_z = !tunnel.reverse
      ? width / 2 + coefCount + extra_width / 2 + 0.8
      : -width / 2 - extra_width / 2 - coefCount;
    const tunnelsList = this.tunnelModels;
    if (this.next_to && tunnelsList.length === 3 && tunnel_index > 1) {
      calc_z -= tunnelsList[tunnelsList.length - 1].get('width') * this.coef;
    }
    if (this.tunnelsLengthDimensions[tunnel_id] && (count === 3 || count === 1.5))
      this.remove_dimensions(
        this.tunnelsLengthDimensions[tunnel_id].cube,
        this.tunnelsLengthDimensions[tunnel_id].length_elements
      );
    const [textLable, textLableRotated, textLableRotated_1] = this.createTextLabelMany(
      `${(length / this.coef).toFixed(2)}`,
      this.next_to ? 2 : 3
    );
    const { x, y, z } = this.getTextWidth(textLable);
    const checkPrev = this.section_order && this.section_order == tunnel_index + (order - 1) ? 1.65 : 0.8;
    const extra_space = count === 1 && cube.geometry.parameters.width < x && order ? checkPrev : 0;
    if (extra_space == 0.8) this.section_order = tunnel_index + order;
    const name_width = x;
    const name_height = z;
    const textPosition = new THREE.Vector3(
      -name_width / 2 + section_length / 2 - length / 2,
      calc_height - y / 2,
      calc_z + name_height / 2 + extra_space
    );
    textLable.position.copy(textPosition);
    const position_length =
      length / 2 - name_width / 2 - 1 > 0 ? length / 2 - name_width / 2 - 0.5 : 0.2;
    let position_width = !tunnel.reverse ? -coefCount - extra_width : -coefCount - extra_width + 0.5;
    const position_width_1 = !tunnel.reverse
      ? -coefCount - extra_width_1 + start_space
      : -coefCount - extra_width_1 + 0.5 + start_space;
    if (this.next_to && tunnelsList.length === 3 && tunnel_index > 1) {
      position_width -= tunnelsList[tunnelsList.length - 1].get('width') * this.coef;
    }
    const points_1 = this.getPoints(position_length, position_width);
    const points_2 = this.getPoints(position_width, position_length);
    const points_1_1 = this.getPoints(position_length, position_width_1);
    const points_1_2 = this.getPoints(position_width_1, position_length);
    const obj: any = {
      '1': new THREE.Vector3(-length / 2, calc_height, calc_z),
      '1.5': new THREE.Vector3(section_length / 2 - length, calc_height, calc_z),
      '3': new THREE.Vector3(section_length / 2 - length, calc_height, calc_z),
    };
    const position_1 = new THREE.Vector3(section_length / 2, calc_height, calc_z);
    const position_2 = obj[count];
    const [{ line_1, line_2 }, { line_1: line_1_rotated, line_2: line_2_rotated } = {} as any] =
      this.drawLinesMany(points_1, points_2, position_1, position_2, 2);
    const [{ line_1: line_1_rotated_1, line_2: line_2_rotated_1 }] = this.drawLinesMany(
      points_1_1,
      points_1_2,
      position_1,
      position_2,
      1
    );
    line_1.position.copy(position_1);
    line_2.position.copy(position_2);

    line_2.rotation.z = 2 * Math.PI;
    line_1.rotation.y = -Math.PI / 2;

    if (tunnel.reverse) {
      line_1.rotation.x = Math.PI;
      line_2.rotation.x = Math.PI;
      line_1_rotated.rotation.x = Math.PI;
      line_2_rotated.rotation.x = Math.PI;
    }
    const x_for_another_view =
      calc_z - 2 * (calc_z - section_width / 2) - this.max_width * this.coef;
    this.setLengthDimensionsPosition(
      textPosition,
      textLableRotated,
      line_1_rotated,
      line_2_rotated,
      x_for_another_view,
      name_width,
      name_height,
      extra_space,
      position_1,
      position_2
    );
    if (textLableRotated_1) {
      this.setLengthDimensionsPositionShort(
        textPosition,
        textLableRotated_1,
        line_1_rotated_1,
        line_2_rotated_1,
        position_1,
        position_2,
        unit,
        tunnel_index,
        start_space
      );
    }
    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = textLableRotated
      ? this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated)
      : ({} as any);
    const group_rotated_1 = textLableRotated_1
      ? this.createGroup(cube, textLableRotated_1, line_1_rotated_1, line_2_rotated_1)
      : ({} as any);

    cube[`group${count === 1 ? '' : 's'}_length`] = group;
    cube[`group${count === 1 ? '' : 's'}_length_rotated`] = group_rotated;
    cube[`group${count === 1 ? '' : 's'}_length_rotated_1`] = group_rotated_1;
    if (this.text_view_position === 'back_right' || this.text_view_position === 'front_left')
      group_rotated.visible = true;
    else if (this.text_view_position === 'plan' && !this.next_to) group_rotated_1.visible = true;
    else group.visible = true;

    if (count === 3 || count === 1.5) {
      this.tunnelsLengthDimensions[tunnel_id] = {
        cube,
        length_elements: {
          group,
          group_rotated,
          group_rotated_1,
        },
        tunnel_id: tunnel.id,
        tunnel,
        tunnel_index,
        section_id,
        width,
        height,
        length,
        count,
        section_length,
        order,
      };
      return;
    }
    this.tunnelsSectionsLengthDimensions[section_id] = {
      cube,
      length_elements: {
        group,
        group_rotated,
        group_rotated_1,
      },
      tunnel_id: tunnel.id,
    };
  }

  /**
   * drawTunnelDimensions (canvas.view3d:4574-4599): reverse stamp, the
   * tunnel-total (count 1.5) at `last`, AHU-height eligibility, the
   * all-tunnels pass trigger, per-section lengths (count 1).
   */
  drawTunnelDimensions({ tunnel, section, last, cube, max_length, tunnel_index, start_x }: any): void {
    if (tunnel_index && tunnel.current_index_values[0] > tunnel.current_index_values[1]) tunnel.reverse = true;
    else tunnel.reverse = false;
    const section_length = (section.get ? section.get('length') : section.length) * this.coef;
    if (last) {
      this.tunnelsDimensionsInfo[tunnel.id] = {
        cube,
        width: tunnel.get('width') * this.coef,
        tunnel,
        max_length,
        section_length,
        start_x,
        height: tunnel.get('height') * this.coef,
        tunnel_index,
        dual_section_component: section.dual_section_component,
      };
      this.drawLength(
        tunnel,
        tunnel_index,
        section.id,
        tunnel.get('width') * this.coef,
        tunnel.get('height') * this.coef,
        tunnel.get('length') * this.coef,
        cube,
        1.5,
        section_length,
        tunnel.id
      );
      if (tunnel_index === 1 && this.ahu_height) {
        if (!(this.tunnelModels.length === 1 && !tunnel.get('base')?.height))
          this.tunnelsDimensionsInfo[tunnel.id].drawAHUHeight = true;
      }
      // :4595 — `Object.values(sections).filter((el) => el.length).length`:
      // count of tunnels whose section collections are non-empty.
      const full_length = Object.values(this.sectionsByTunnel).filter((el) => el.length)?.length;
      if (tunnel_index === full_length) this.drawAllTunnelsWidthAndHeight();
    }
    if (!(last && section.order == 1))
      this.drawLength(
        tunnel,
        tunnel_index,
        section.id,
        tunnel.get('width') * this.coef,
        tunnel.get('height') * this.coef,
        section_length,
        cube,
        1,
        section_length,
        tunnel.id,
        section.order
      );
  }

  // =========================================================================
  // G29 — DMOUNT DIMENSIONS: SEGMENT LENGTHS + FRAMES + HORIZONTAL PLANES
  // (drawDmount :3942, drawHorizontalDmount(s) :3974/:3982, createDotted-
  //  WireFrame :4445, drawDmountFrame :4469, drawDmountLength :4478, fed by
  //  tunnel.js get_dmounts_position :650-679)
  // =========================================================================

  /**
   * get_dmounts_position (tunnel.js:650-679) — verbatim: walks the tunnel's
   * sections in airflow order, accumulates the vertical-dmount split points
   * into [{start, end, length, section_id}] segments and hands them to
   * drawDmount. Reads the sections_dmounts cache (re-filled per sync from the
   * §20.4 #38 rows).
   */
  get_dmounts_position(tunnel: CanvasTunnelModel): void {
    if (this.dmount_timer[tunnel.id]) clearTimeout(this.dmount_timer[tunnel.id]);
    this.dmount_timer = {};
    const elementsForDraw: any[] = [];
    let prev_end = 0;
    let sections_length = 0;
    const models = this.sectionsByTunnel[tunnel.id] || [];
    const list = tunnel.get('airflow_direction_id') === 2 ? [...models].reverse() : models;
    list?.forEach((section) => {
      if (!section) return;
      const section_dmounts = this.sections_dmounts[section.id]?.dmounts || [];
      const dmounts_list =
        tunnel.get('airflow_direction_id') === 2 ? [...section_dmounts]?.reverse() : section_dmounts;
      section.dmounts = section_dmounts;
      dmounts_list?.forEach((dmount: any) => {
        const from =
          tunnel.get('airflow_direction_id') === 2
            ? section.get('length') - dmount.dist_from
            : dmount.dist_from;
        const el: any = {
          start: prev_end,
          end: sections_length + from,
          section_id: section.id,
        };
        el.length = el.end - el.start;
        prev_end = el.end;
        if (!elementsForDraw.length) el.section_id = list[0].id;
        elementsForDraw.push(el);
      });
      sections_length += section.get('length');
    });
    if (tunnel.get('length') - prev_end)
      elementsForDraw.push({
        start: prev_end,
        length: tunnel.get('length') - prev_end,
        end: tunnel.get('length'),
        section_id: list?.[list.length - 1]?.id,
      });
    this.drawDmount({ tunnel, dmounts: elementsForDraw });
  }

  /**
   * drawDmount (canvas.view3d:3942-3973): wipes the prior dmount dims,
   * re-draws the tunnel-total length dim with count 3 (vs 1.5) when the
   * segment count changed (G28 count protocol), bails on a single segment
   * (no dmounts), then draws every segment dim anchored on the tunnel's
   * order-1 section cube.
   */
  drawDmount({ tunnel, dmounts }: { tunnel: any; dmounts: any[] }): void {
    const selected = this.sectionCanvasObjects.find(
      (el: any) => el.order == 1 && el.tunnel.id === tunnel.id
    );
    this.need_update = true;
    if (this.tunnelsDimountsDimensions[tunnel.id]) {
      this.tunnelsDimountsDimensions[tunnel.id].forEach((el: any) => {
        if (!el) return;
        this.remove_dimensions(el.cube, el.elements);
        this.disposeChilds(el.wireframe);
        el.cube.remove(el.wireframe);
      });
    }
    const count = dmounts.length !== 1 ? 3 : 1.5;
    if (this.tunnelsLengthDimensions[tunnel.id] && this.tunnelsLengthDimensions[tunnel.id].count != count) {
      this.drawLength(
        this.tunnelsLengthDimensions[tunnel.id].tunnel,
        this.tunnelsLengthDimensions[tunnel.id].tunnel_index,
        this.tunnelsLengthDimensions[tunnel.id].section_id,
        this.tunnelsLengthDimensions[tunnel.id].tunnel.get('width') * this.coef,
        this.tunnelsLengthDimensions[tunnel.id].tunnel.get('height') * this.coef,
        this.tunnelsLengthDimensions[tunnel.id].tunnel.get('length') * this.coef,
        this.tunnelsLengthDimensions[tunnel.id].cube,
        count,
        this.tunnelsLengthDimensions[tunnel.id].section_length,
        this.tunnelsLengthDimensions[tunnel.id].tunnel.id
      );
    }
    if (dmounts.length === 1) return;
    this.tunnelsDimountsDimensions[tunnel.id] = [];
    selected &&
      dmounts.forEach(({ length, start, section_id, end }, index) => {
        this.drawDmountLength({
          tunnel,
          length,
          cube: selected.cube,
          section: selected.section,
          start,
          end,
          tunnel_index: selected.tunnel_index,
          index,
          section_id,
        });
      });
  }

  /** drawHorizontalDmount (canvas.view3d:3974-3981) — one dotted horizontal plane. */
  drawHorizontalDmount(section: any, dmount: any): any {
    this.need_update = true;
    const y = -section.geometry.parameters.height / 2 + dmount.dist_from * this.coef;
    const geometry = new THREE.PlaneBufferGeometry(
      section.geometry.parameters.width,
      section.geometry.parameters.depth
    );
    const edges = new THREE.EdgesGeometry(geometry);
    const wireframe = this.createDottedWireFrame(edges, 0, y, false);
    return wireframe;
  }

  /**
   * drawHorizontalDmounts (canvas.view3d:3982-4002) — per-section group of
   * horizontal planes, replayed from drawSectionComplete (:2558); group Y =
   * floor-construction start.
   */
  drawHorizontalDmounts(section: any, dmounts_list: any[]): void {
    const section_on_scene = this.sectionCanvasObjects.find((el: any) => el.section.id == section.id);
    const parent = section_on_scene?.cube;
    const start_y = this.tunnelStartYByFloorConstr[section.tunnel.id] * this.coef;
    if (this.sectionsHorizontalDimounts[section.id]) {
      this.disposeChilds(this.sectionsHorizontalDimounts[section.id]);
      this.sectionsHorizontalDimounts[section.id].parent.remove(this.sectionsHorizontalDimounts[section.id]);
      delete this.sectionsHorizontalDimounts[section.id];
    }
    if (dmounts_list.length) {
      const group = new THREE.Group();
      dmounts_list.forEach((dmount) => {
        const el = this.drawHorizontalDmount(parent, dmount);
        group.add(el);
      });
      parent.add(group);
      this.sectionsHorizontalDimounts[section.id] = group;
      group.position.y = start_y;
    }
  }

  /**
   * createDottedWireFrame (canvas.view3d:4445-4468): LineDashed dash 1 /
   * gap 1.5 + a (−0.05·coef, −0.05·coef) offset clone for the double-line
   * effect, renderOrder 50; vertical → rotation.y π/2, else rotation.x π/2.
   */
  createDottedWireFrame(edges: any, x: number, y = 0, vertical = true): any {
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      scale: 1,
      dashSize: 1,
      gapSize: 1.5,
      opacity: 1,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.computeLineDistances();
    if (vertical) wireframe.rotation.y = Math.PI / 2;
    else wireframe.rotation.x = Math.PI / 2;
    wireframe.position.copy(new THREE.Vector3(-x, y, 0));
    wireframe.renderOrder = 50;
    const w = wireframe.clone();
    w.position.x = -0.05 * this.coef;
    w.position.y = -0.05 * this.coef;
    w.rotation.y = 0;
    w.rotation.x = 0;
    wireframe.add(w);
    return wireframe;
  }

  /** drawDmountFrame (canvas.view3d:4469-4477) — dotted cross-section at a split. */
  drawDmountFrame({ width, height, cube, x, start_y }: any): any {
    const unit = 0.5 * this.coef;
    const geometry = new THREE.PlaneBufferGeometry(width + unit, height + unit - start_y);
    const edges = new THREE.EdgesGeometry(geometry);
    const wireframe = this.createDottedWireFrame(edges, x, start_y / 2);
    cube.add(wireframe);
    return wireframe;
  }

  /**
   * drawDmountLength (canvas.view3d:4478-4573) — segment-length dim (same
   * 3-variant structure as G28 at coefCount 1.5·1.8), <15" stagger memory
   * (dmount_order), boundary frame for every segment except the first
   * (`!start && end → null`), registry arrays on the cube + per-tunnel list.
   */
  drawDmountLength({ tunnel, cube, length, section, start, end, tunnel_index, index }: any): void {
    const base = (tunnel.get('base')?.height || 0) * this.coef;
    const unit = this.next_to ? 2 : tunnel_index + 1;
    const extra_width_1 = unit === 2 || tunnel.reverse ? unit : unit * 3;
    const extra_width = unit;
    const start_space = 0;
    const coefCount = 1.5 * 1.8 + start_space;
    const section_length = section.get('length') * this.coef;
    const section_height = section.tunnel.get('height') * this.coef;
    const tunnel_width = section.tunnel.get('width') * this.coef + extra_width;
    const calc_height = -section_height / 2 - base;
    let calc_z = !tunnel.reverse
      ? tunnel_width / 2 + coefCount + extra_width / 2 + 1
      : -tunnel_width / 2 - extra_width / 2 - coefCount;
    const tunnelsList = this.tunnelModels;
    if (this.next_to && tunnelsList.length === 3 && tunnel_index > 1) {
      calc_z -= tunnelsList[tunnelsList.length - 1].get('width') * this.coef;
    }
    const [textLable, textLableRotated, textLableRotated_1] = this.createTextLabelMany(
      `${length.toFixed(2)}`,
      this.next_to ? 2 : 3
    );
    const { x, y, z } = this.getTextWidth(textLable);
    const checkPrev = this.dmount_order && this.dmount_order == tunnel_index + (index - 1) ? 1.65 : 0.8;
    const extra_space = length < 15 ? checkPrev : 0;
    if (extra_space == 0.8) this.dmount_order = tunnel_index + index;
    const name_width = x;
    const name_height = z;
    const positionText = new THREE.Vector3(
      -name_width / 2 - section_length / 2 + (length * this.coef) / 2 + start * this.coef,
      calc_height - y / 2,
      calc_z + name_height / 2 + extra_space
    );
    textLable.position.copy(positionText);
    const position_length =
      (length * this.coef) / 2 - name_width / 2 - 1 > 0
        ? (length * this.coef) / 2 - name_width / 2 - 0.5
        : 0.2;
    let position_width = !tunnel.reverse ? -coefCount - extra_width : -coefCount - extra_width + 0.5;
    const position_width_1 = !tunnel.reverse
      ? -coefCount - extra_width_1 + start_space
      : -coefCount - extra_width_1 + 0.5 + start_space;
    if (this.next_to && tunnelsList.length === 3 && tunnel_index > 1) {
      position_width -= tunnelsList[tunnelsList.length - 1].get('width') * this.coef;
    }
    const points_1 = this.getPoints(position_length, position_width);
    const points_2 = this.getPoints(position_width, position_length);

    const points_1_1 = this.getPoints(position_length, position_width_1);
    const points_1_2 = this.getPoints(position_width_1, position_length);

    const position_1 = new THREE.Vector3(
      -section_length / 2 + length * this.coef + start * this.coef,
      calc_height,
      calc_z
    );
    const position_2 = new THREE.Vector3(-section_length / 2 + start * this.coef, calc_height, calc_z);

    const [{ line_1, line_2 }, { line_1: line_1_rotated, line_2: line_2_rotated } = {} as any] =
      this.drawLinesMany(points_1, points_2, position_1, position_2, 2);
    const [{ line_1: line_1_rotated_1, line_2: line_2_rotated_1 } = {} as any] = this.drawLinesMany(
      points_1_1,
      points_1_2,
      position_1,
      position_2,
      1
    );

    line_2.rotation.z = 2 * Math.PI;
    line_1.rotation.y = -Math.PI / 2;
    if (tunnel.reverse) {
      line_1.rotation.x = Math.PI;
      line_2.rotation.x = Math.PI;
      line_1_rotated.rotation.x = Math.PI;
      line_2_rotated.rotation.x = Math.PI;
    }
    const x_for_another_view =
      calc_z - 2 * (calc_z - (section.tunnel.get('width') * this.coef) / 2) - this.max_width * this.coef;
    this.setLengthDimensionsPosition(
      positionText,
      textLableRotated,
      line_1_rotated,
      line_2_rotated,
      x_for_another_view,
      name_width,
      name_height,
      extra_space,
      position_1,
      position_2
    );
    if (textLableRotated_1) {
      this.setLengthDimensionsPositionShort(
        positionText,
        textLableRotated_1,
        line_1_rotated_1,
        line_2_rotated_1,
        position_1,
        position_2,
        unit,
        tunnel_index,
        start_space
      );
    }

    const group = this.createGroup(cube, textLable, line_1, line_2);
    const group_rotated = textLableRotated
      ? this.createGroup(cube, textLableRotated, line_1_rotated, line_2_rotated)
      : ({} as any);
    const group_rotated_1 = textLableRotated_1
      ? this.createGroup(cube, textLableRotated_1, line_1_rotated_1, line_2_rotated_1)
      : ({} as any);

    if (!index) {
      cube.group_dmount = [group];
      cube.group_dmount_rotated = [group_rotated];
      cube.group_dmount_rotated_1 = [group_rotated_1];
    } else {
      cube.group_dmount.push(group);
      cube.group_dmount_rotated.push(group_rotated);
      cube.group_dmount_rotated_1.push(group_rotated_1);
    }

    if (this.next_to) group.visible = true;
    else if (this.text_view_position === 'back_right' || this.text_view_position === 'front_left')
      group_rotated.visible = true;
    else if (this.text_view_position === 'plan') group_rotated_1.visible = true;
    else group.visible = true;
    const start_y = this.tunnelStartYByFloorConstr[section.tunnel.id] * this.coef;
    const wireframe =
      !start && end
        ? null
        : this.drawDmountFrame({
            cube,
            height: section_height + base,
            width: tunnel_width - extra_width,
            x: section_length / 2 - start * this.coef,
            start_y,
          });
    if (wireframe) wireframe.position.y = -base / 2;
    this.tunnelsDimountsDimensions[section.tunnel.id].push({
      cube,
      elements: {
        group,
        group_rotated,
        group_rotated_1,
      },
      wireframe,
      tunnel_id: tunnel.id,
    });
    if (this._controls?.autoRotate) {
      group_rotated.visible = false;
      group_rotated_1.visible = false;
    }
  }

  /** Dispose override: clear the G29 debounce timers, then the 6b teardown. */
  dispose(opts?: { forceContextLoss?: boolean }): void {
    Object.values(this.dmount_timer).forEach((t) => clearTimeout(t));
    this.dmount_timer = {};
    super.dispose(opts);
  }

  // =========================================================================
  // G30 — PER-VIEW DIMENSION/LABEL VISIBILITY ROUTER
  // =========================================================================

  /**
   * getTextPosition (canvas.view3d:4967-5075) — overrides the 6b no-op.
   * (a) re-orients the front text label, (b) blanket-hides every dimension
   * family on the cube, (c) re-shows exactly one variant per family per view
   * (the full :5007-5075 matrix). Dmount arrays (G29) routed too — empty
   * until 6c2.
   */
  getTextPosition(view: string, textLabelRotated: any, cube: any, textWidthRotated: number): void {
    const {
      group_width,
      group_width_rotated,
      group_height,
      group_height_rotated,
      group_height_rotated_2,
      group_height_rotated_3,
      group_ahu_height,
      group_ahu_height_rotated,
      group_ahu_height_rotated_2,
      group_ahu_height_rotated_3,
      group_base_height,
      group_base_height_rotated,
      group_base_height_rotated_2,
      group_base_height_rotated_3,
      group_length,
      groups_length,
      group_length_rotated,
      groups_length_rotated,
      group_length_rotated_1,
      groups_length_rotated_1,
      group_dmount,
      group_dmount_rotated,
      group_dmount_rotated_1,
    } = cube;
    if (group_width) {
      group_width_rotated.visible = false;
      group_width.visible = false;
      group_height_rotated.visible = false;
      group_height.visible = false;
      group_height_rotated_2.visible = false;
      group_height_rotated_3.visible = false;
      groups_length.visible = false;
      groups_length_rotated.visible = false;
      groups_length_rotated_1.visible = false;
    }
    if (group_ahu_height) {
      group_ahu_height_rotated.visible = false;
      group_ahu_height.visible = false;
      group_ahu_height_rotated_2.visible = false;
      group_ahu_height_rotated_3.visible = false;
    }
    if (group_base_height) {
      group_base_height_rotated.visible = false;
      group_base_height.visible = false;
      group_base_height_rotated_2.visible = false;
      group_base_height_rotated_3.visible = false;
    }
    if (group_length) {
      group_length.visible = false;
      group_length_rotated.visible = false;
      group_length_rotated_1.visible = false;
    }
    if (group_dmount) {
      [...group_dmount, ...group_dmount_rotated, ...group_dmount_rotated_1].forEach(
        (el: any) => (el.visible = false)
      );
    }
    if (view === 'back_right') {
      textLabelRotated.rotation.x = -Math.PI / 2;
      textLabelRotated.position.z = -cube.geometry.parameters.depth / 2;
      textLabelRotated.position.y = -textWidthRotated / 2;
      if (group_width) {
        group_width_rotated.visible = true;
        group_height_rotated.visible = true;
        groups_length_rotated.visible = true;
      }
      if (group_height_rotated) group_height_rotated.visible = true;
      if (group_base_height_rotated) group_base_height_rotated.visible = true;
      if (group_length_rotated) group_length_rotated.visible = true;
      if (group_dmount_rotated) group_dmount_rotated.forEach((el: any) => (el.visible = true));
      if (group_ahu_height_rotated) group_ahu_height_rotated.visible = true;
    } else if (view === 'back_left') {
      textLabelRotated.rotation.x = Math.PI / 2;
      textLabelRotated.position.z = cube.geometry.parameters.depth / 2;
      textLabelRotated.position.y = textWidthRotated / 2;
      if (group_width) {
        group_width_rotated.visible = true;
        group_height_rotated_3.visible = true;
        if (group_ahu_height_rotated_3) group_ahu_height_rotated_3.visible = true;
        groups_length.visible = true;
      }
      if (group_base_height_rotated_3) group_base_height_rotated_3.visible = true;
      if (group_length) group_length.visible = true;
      if (group_dmount) group_dmount.forEach((el: any) => (el.visible = true));
    } else if (view === 'front_left') {
      textLabelRotated.rotation.x = -Math.PI / 2;
      textLabelRotated.position.z = -cube.geometry.parameters.depth / 2;
      textLabelRotated.position.y = textWidthRotated / 2;
      if (group_width) {
        group_width.visible = true;
        group_height_rotated_2.visible = true;
        if (group_ahu_height_rotated_2) group_ahu_height_rotated_2.visible = true;
        groups_length_rotated.visible = true;
      }
      if (group_base_height_rotated_2) group_base_height_rotated_2.visible = true;
      if (group_length_rotated) group_length_rotated.visible = true;
      if (group_dmount_rotated) group_dmount_rotated.forEach((el: any) => (el.visible = true));
    } else {
      textLabelRotated.rotation.x = Math.PI / 2;
      textLabelRotated.position.z = cube.geometry.parameters.depth / 2;
      textLabelRotated.position.y = -textWidthRotated / 2;
      if (group_width) {
        if (view !== 'elevation_near_side') group_width.visible = true;
        if (view !== 'plan') {
          group_height.visible = true;
          if (group_ahu_height) group_ahu_height.visible = true;
        }
        if (view === 'plan') groups_length_rotated_1.visible = true;
        else if (view !== 'elevation_near_side') groups_length.visible = true;
      }
      if (group_base_height && view !== 'plan') group_base_height.visible = true;
      if (view === 'plan') {
        if (!this.next_to) {
          if (group_length_rotated_1) group_length_rotated_1.visible = true;
          if (group_dmount_rotated_1) group_dmount_rotated_1.forEach((el: any) => (el.visible = true));
        } else {
          if (group_length) group_length.visible = true;
          if (groups_length) groups_length.visible = true;
          if (group_dmount) group_dmount.forEach((el: any) => (el.visible = true));
        }
      } else if (view !== 'elevation_near_side') {
        if (group_length) group_length.visible = true;
        if (group_dmount) group_dmount.forEach((el: any) => (el.visible = true));
      }
    }
  }

  // =========================================================================
  // G22 — WIREFRAME DASH RULES
  // =========================================================================

  /** calc_x_z (canvas.view3d:4936-4942) — roof-opening X/Z-range overlap test. */
  calc_x_z(cube: any, tunnel: any, accessory: any, start_x: number, end_x: number): boolean {
    const for_z = tunnel.get('width') > accessory?.distance_to_right_wall;
    const start = cube.position.x - cube.geometry.parameters.width / 2;
    const end = start + cube.geometry.parameters.width;
    const for_x = (start <= start_x && start_x <= end) || (start <= end_x && end_x <= end);
    return for_z && for_x;
  }

  /**
   * makeLineDashed (canvas.view3d:4943-4966): the dash trigger classes —
   * int-wall planes (>6), floor (1), LIGHT/LIGHT_LINEAR, casing_mount 2,
   * roof-opening under an upper tunnel, plan-view floor mounts,
   * elevation-near-wall. [NEG]#11: the window-global `self.is_position_changed`
   * channel is the base-class instance field. Consumers (accessory wireframe
   * materials) attach in 6c2.
   */
  makeLineDashed(
    mounting: any = {},
    material: any,
    view: string,
    airflow_direction_id: number,
    shape: any,
    is_position_changed = false
  ): void {
    this.is_position_changed = is_position_changed;
    let opening_on_roof = false;
    const always_dashed =
      mounting.mounting_plane_id > 6 ||
      mounting.mounting_plane_id === 1 ||
      shape.name === 'LIGHT_LINEAR' ||
      shape.name === 'LIGHT' ||
      shape?.casing_mount_id === 2;
    const on_floor_plan_view =
      view === 'plan' && (mounting.mounting_plane_id === 1 || !mounting.mounting_plane_id);
    const elevation_near_side_view =
      (view === 'elevation_near_side' && mounting.mounting_plane_id === 5 && airflow_direction_id === 1) ||
      (view === 'elevation_near_side' && mounting.mounting_plane_id === 6 && airflow_direction_id === 2);
    if (mounting.mounting_plane_id === 2) {
      const start_x =
        shape.parent_cube.position.x -
        shape.parent_cube.geometry.parameters.width / 2 +
        shape.accessory.distance_to_downstream_wall * this.coef;
      const end_x = start_x + (shape.geometry.parameters.depth || shape.geometry.parameters.width);
      const next_tunnel = shape.parent_cube.tunnel_index + 1;
      if (
        this.sectionCanvasObjects.find(
          (el: any) =>
            el.tunnel_index === next_tunnel && this.calc_x_z(el.cube, el.tunnel, shape.accessory, start_x, end_x)
        )
      )
        opening_on_roof = true;
    }
    if (always_dashed || opening_on_roof || on_floor_plan_view || elevation_near_side_view) {
      material.gapSize = 0.2;
      material.opacity = 0.7;
      material.dashSize = 0.2;
      return;
    }
    material.gapSize = 0;
    material.opacity = 1;
    material.dashSize = 1;
    material.scale = 1;
    material.linewidth = 1;
  }

  /**
   * Accessory re-dash sweeps (setView :775-778 / updatePosition :4847-4850) —
   * real once accessoryCanvasObjects fills (6c2); the skip set is
   * DRAIN_PAN/LIGHT_LINEAR/LIGHT.
   */
  redashAccessories(view: string, isPositionChanged = false): void {
    this.accessoryCanvasObjects.forEach((el: any) => {
      if (el.accessory.name === 'DRAIN_PAN' || el.accessory.name === 'LIGHT_LINEAR' || el.accessory.name === 'LIGHT')
        return;
      this.makeLineDashed(
        el.mounting,
        el.wireframe.material,
        view,
        el.section.tunnel.get('airflow_direction_id'),
        el.shape,
        isPositionChanged
      );
      // dev-sync (canvas.view3d delta @780 + @5029 — both legacy sweeps route
      // through this one): VFD bank/enclosure extras re-dash too.
      (el.extraShapes || []).forEach(({ shape, wireframe }: any) => {
        this.makeLineDashed(
          el.mounting,
          wireframe.material,
          view,
          el.section.tunnel.get('airflow_direction_id'),
          shape,
          isPositionChanged
        );
      });
    });
  }

  // =========================================================================
  // G33 — AIRFLOW DIRECTION ARROWS + STREAM LABELS
  // =========================================================================

  /**
   * getOrientationId (canvas.view3d:5378-5401): first fan section
   * (airflow-ordered) → its fan component's orientation_id (multi-fan with
   * orientation 3 → forced 1); no fan section → 1. The stateStorage
   * `component.fan.model` probe is the `fan_orientation_id` row field —
   * undefined (component data not loaded) reproduces the legacy pre-fetch
   * state: orientation undefined → no arrow for that tunnel.
   */
  getOrientationId(id: number, airflow_direction_id: number): { orientation_id: any; fan: any } {
    const models = this.sectionsByTunnel[id] || [];
    const sections_list = airflow_direction_id === 2 ? [...models].reverse() : models;
    const fan_list = sections_list.filter((section) => section.get('type') === 'fan');
    const fan = fan_list.find((section) => section.get('type') === 'fan');
    if (!fan) return { orientation_id: 1, fan: null };
    const fanOrientation = fan.get('fan_orientation_id');
    let orientation_id;
    if (fanOrientation != null && fan_list.length > 1 && fanOrientation === 3) {
      orientation_id = 1;
    } else {
      fan.need_redraw = true;
      orientation_id = fanOrientation;
    }
    return { orientation_id, fan };
  }

  /**
   * getArrowPosition (canvas.view3d:5402-5424): anchor-cube offset
   * ±(extent/2 + 50·coef) on X (orientation 1/3) or Z (2/4) with the
   * rotation.y table; the elevation label counter-rotates against the arrow.
   */
  getArrowPosition(orientation_id: any, arrow: any, cube: any, airflow_direction_id: number): void {
    if (orientation_id == 1) {
      arrow.position.x =
        airflow_direction_id == 1
          ? -cube.geometry.parameters.width / 2 - 50 * this.coef
          : cube.geometry.parameters.width / 2 + 50 * this.coef;
      if (airflow_direction_id == 1) {
        arrow.rotation.y = -Math.PI;
      }
    } else if (orientation_id == 2) {
      arrow.position.z =
        airflow_direction_id == 1
          ? -cube.geometry.parameters.depth / 2 - 50 * this.coef
          : cube.geometry.parameters.depth / 2 + 50 * this.coef;
      arrow.rotation.y = airflow_direction_id == 1 ? Math.PI / 2 : -Math.PI / 2;
    } else if (orientation_id == 3) {
      arrow.position.x =
        airflow_direction_id == 2
          ? -cube.geometry.parameters.width / 2 - 50 * this.coef
          : cube.geometry.parameters.width / 2 + 50 * this.coef;
      if (airflow_direction_id == 2) {
        arrow.rotation.y = -Math.PI;
      }
    } else if (orientation_id == 4) {
      arrow.position.z =
        airflow_direction_id == 2
          ? -cube.geometry.parameters.depth / 2 - 50 * this.coef
          : cube.geometry.parameters.depth / 2 + 50 * this.coef;
      arrow.rotation.y = airflow_direction_id == 2 ? Math.PI / 2 : -Math.PI / 2;
    }
    cube.add(arrow);
    if (arrow.airflowLabelMeshElevation) {
      arrow.airflowLabelMeshElevation.rotation.y = -arrow.rotation.y;
    }
  }

  /**
   * getArrowData (canvas.view3d:5425-5469): wheel tunnels → SA side by
   * config_id ∈ {5,2,4,8} (inverted on the secondary), blue OA→SA / red
   * RA→EA; otherwise duty scan (cooling/evap → blue, heating/gas → red, both
   * → blue→red gradient, none → neutral 0xdddddd→0x518449). stateStorage
   * model probes re-homed to the wheel_configuration_id / coil_duty_id row
   * fields + the coilConnections cache (G31, fills in 6c2).
   */
  getArrowData(id: number): { colorStart: number; colorEnd: number; inletLabel: string; outletLabel: string } {
    const BLUE = 0x3867af;
    const RED = 0xcc2222;
    const tunnel_sections = this.sectionsByTunnel[id] || [];
    if (this.tunnelModels.length >= 2) {
      const rhe_section = tunnel_sections.find(
        (s) => s.get('type') === 'energy_wheel' || s.name_on_canvas === 'er_wheel'
      );
      if (rhe_section) {
        const is_primary = !rhe_section.get('sitting_on_id');
        const config_id = +(rhe_section.get('wheel_configuration_id') || (this.next_to ? 2 : 1));
        const primary_is_sa = [5, 2, 4, 8].includes(config_id);
        const is_sa = is_primary ? primary_is_sa : !primary_is_sa;
        return is_sa
          ? { colorStart: BLUE, colorEnd: BLUE, inletLabel: 'OA', outletLabel: 'SA' }
          : { colorStart: RED, colorEnd: RED, inletLabel: 'RA', outletLabel: 'EA' };
      }
    }

    let hasCoolingCoil = false;
    let hasHeatingCoil = false;
    let hasGasHeater = false;
    let hasEvapPack = false;
    tunnel_sections.forEach((section) => {
      const type = section.get('type');
      if (type === 'coil') {
        const duty = this.coilConnections[section.id]?.duty || section.get('coil_duty_id');
        if (duty == 1) hasCoolingCoil = true;
        else if (duty == 2) hasHeatingCoil = true;
      } else if (type === 'gas_heater') {
        hasGasHeater = true;
      } else if (type === 'evap_pack') {
        hasEvapPack = true;
      }
    });
    const hasCool = hasCoolingCoil || hasEvapPack;
    const hasHeat = hasHeatingCoil || hasGasHeater;
    if (hasCool && hasHeat) return { colorStart: BLUE, colorEnd: RED, inletLabel: '', outletLabel: '' };
    if (hasCool) return { colorStart: BLUE, colorEnd: BLUE, inletLabel: '', outletLabel: '' };
    if (hasHeat) return { colorStart: RED, colorEnd: RED, inletLabel: '', outletLabel: '' };
    return { colorStart: 0xdddddd, colorEnd: 0x518449, inletLabel: '', outletLabel: '' };
  }

  /** getAirStreamColorHex (canvas.view3d:5470-5473) — tooltip CSS side (6d). */
  getAirStreamColorHex(label: string): string {
    const labelColors: Record<string, string> = { OA: '#3DB35A', SA: '#3867AF', RA: '#CC7722', EA: '#888888' };
    return labelColors[label] || '#aaaaaa';
  }

  /**
   * drawAirflowDirectionChangingArrow (canvas.view3d:5474-5535): disposes the
   * prior `<id>` / `<id>_end` icons, builds the gradient arrow + OA/SA/RA/EA
   * labels (size 0.9 / height 0.04, removedepthTest — [NEG]#101 7th arg
   * dropped), and the wheel end-arrow for ≥2-tunnel boards. The shallow
   * legacy dispose leaks child geometries — fixed silently with disposeChilds
   * (same call the registry teardown uses).
   */
  drawAirflowDirectionChangingArrow(id: number, airflow_direction_id: number, section_id: number): void {
    const { fan, orientation_id } = this.getOrientationId(id, airflow_direction_id);
    ['', '_end'].forEach((suffix) => {
      const key = id + suffix;
      const icon = this.tunnelsAirflowIcons[key];

      if (icon && icon.parent) {
        icon.parent.remove(icon);
        this.disposeChilds(icon);
        if (icon.geometry) icon.geometry.dispose();
        if (icon.material) {
          Array.isArray(icon.material)
            ? icon.material.forEach((m: any) => m.dispose())
            : icon.material.dispose();
        }
        delete this.tunnelsAirflowIcons[key];
      }
    });
    if (orientation_id) {
      const { colorStart, colorEnd, inletLabel, outletLabel } = this.getArrowData(id);
      const arrow: any = this.createArrow(colorStart, colorEnd);
      arrow.position.y = -0.8;
      arrow.tunnel_id = id;
      arrow.renderOrder = 5;
      arrow.section_id = orientation_id === 1 ? section_id : fan.id;
      arrow.orientation_id = orientation_id;
      this.tunnelsAirflowIcons[id] = arrow;

      const tunnel_sections = this.sectionsByTunnel[id] || [];
      const hasRhePhe = tunnel_sections.find((s) => s.get('type') === 'energy_wheel');
      arrow.airflowLabel = inletLabel;
      if (inletLabel && this.font) {
        const elMesh = this.createTextLabel(
          this.font,
          { name: inletLabel, removedepthTest: true },
          colorStart,
          false,
          0.9,
          0.04
        );
        elMesh.geometry.computeBoundingBox();
        elMesh.position.y = 1.3;
        elMesh.position.x = airflow_direction_id === 1 ? 1.5 : 0;
        arrow.add(elMesh);
        arrow.airflowLabelMeshElevation = elMesh;
      }
      if (this.tunnelModels.length >= 2 && hasRhePhe) {
        const arrowEnd: any = this.createArrow(colorStart, colorEnd);
        arrowEnd.position.y = -0.8;
        arrowEnd.tunnel_id = id;
        arrowEnd.renderOrder = 5;
        arrowEnd.section_id = orientation_id === 1 ? section_id : fan.id;
        arrowEnd.orientation_id = orientation_id;
        arrowEnd.airflowLabel = outletLabel;
        if (outletLabel && this.font) {
          const endElMesh = this.createTextLabel(
            this.font,
            { name: outletLabel, removedepthTest: true },
            colorEnd,
            false,
            0.9,
            0.04
          );
          endElMesh.geometry.computeBoundingBox();
          endElMesh.position.y = 1.3;
          endElMesh.position.x = airflow_direction_id === 1 ? 1.5 : 0;
          arrowEnd.add(endElMesh);
          arrowEnd.airflowLabelMeshElevation = endElMesh;
        }
        this.tunnelsAirflowIcons[id + '_end'] = arrowEnd;
      }
    }
  }

  /**
   * createEllipticalTube (canvas.view3d:5537-5618): wavy shaft — ellipse
   * Shape extruded along a sine Curve (1000 steps — legacy perf note stands),
   * vertex-color gradient along X with the alpha² bias, opacity 0.9.
   * r110 Geometry-class ExtrudeGeometry → BufferGeometry.fromGeometry (§42 —
   * hard-port site, do not modernize).
   */
  createEllipticalTube(
    length = 5,
    radiusX = 0.3,
    radiusY = 0.3,
    colorStart: number | THREE.Color = 0xdddddd,
    colorEnd: number | THREE.Color = 0x518449,
    waveCount = 2,
    waveAmplitude = 0.4
  ): any {
    class WaveCurve extends THREE.Curve<THREE.Vector3> {
      getPoint(t: number): THREE.Vector3 {
        const x = t * length;
        const y = Math.sin(t * waveCount * Math.PI) * waveAmplitude;
        const z = 0;
        return new THREE.Vector3(x, y, z);
      }
    }
    const curve = new WaveCurve();

    const shape = new THREE.Shape();
    shape.absellipse(0, 0, radiusX, radiusY, 0, 2 * Math.PI, false, 0);

    let geometry: any = new THREE.ExtrudeGeometry(shape, {
      steps: 1000,
      extrudePath: curve,
      bevelEnabled: false,
    } as any);

    if (!geometry.attributes) {
      geometry = new THREE.BufferGeometry().fromGeometry(geometry);
    }

    const positions = geometry.attributes.position;
    const count = positions.count;
    const vertexColors = new Float32Array(count * 3);

    let minX = Infinity,
      maxX = -Infinity;
    for (let i = 0; i < count; i++) {
      const x = positions.getX(i);
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
    }
    const rangeX = maxX - minX || 1e-6;

    const cStart = colorStart instanceof THREE.Color ? colorStart : new THREE.Color(colorStart);
    const cEnd = colorEnd instanceof THREE.Color ? colorEnd : new THREE.Color(colorEnd);

    for (let i = 0; i < count; i++) {
      const x = positions.getX(i);
      const alpha = Math.pow((x - minX) / rangeX, 2); // bias: colorEnd appears sooner
      const r = cStart.r + (cEnd.r - cStart.r) * alpha;
      const g = cStart.g + (cEnd.g - cStart.g) * alpha;
      const b = cStart.b + (cEnd.b - cStart.b) * alpha;
      const idx = i * 3;
      vertexColors[idx] = r;
      vertexColors[idx + 1] = g;
      vertexColors[idx + 2] = b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));

    // Legacy passes the boolean (r110 treats any value !== NoColors as on);
    // the r110 d.ts types the enum — cast keeps the runtime value identical.
    const material = new THREE.MeshBasicMaterial({
      vertexColors: true as unknown as THREE.Colors,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9,
    });

    return new THREE.Mesh(geometry, material);
  }

  /**
   * createArrowHead (canvas.view3d:5621-5691): extruded triangle (depth 1.5),
   * vertex-color gradient across Y (tip→base, alpha² bias).
   */
  createArrowHead(
    width = 1.5,
    height = 3,
    colorStart: number | THREE.Color = 0xdddddd,
    colorEnd: number | THREE.Color = 0x518449
  ): any {
    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(-width / 2, -height);
    shape.lineTo(width / 2, -height);
    shape.lineTo(0, 0);

    let geometry: any = new THREE.ExtrudeGeometry(shape, {
      depth: 1.5,
      bevelEnabled: false,
    } as any);

    if (!geometry.attributes) {
      geometry = new THREE.BufferGeometry().fromGeometry(geometry);
    }

    const positions = geometry.attributes.position;
    const count = positions.count;
    const vertexColors = new Float32Array(count * 3);

    let minY = Infinity,
      maxY = -Infinity;
    for (let i = 0; i < count; i++) {
      const y = positions.getY(i);
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
    const rangeY = maxY - minY || 1e-6;

    const cStart = colorStart instanceof THREE.Color ? colorStart : new THREE.Color(colorStart);
    const cEnd = colorEnd instanceof THREE.Color ? colorEnd : new THREE.Color(colorEnd);

    for (let i = 0; i < count; i++) {
      const y = positions.getY(i);
      // maxY=0 (tip, left after rotation), minY=-height (base, right after rotation)
      const alpha = Math.pow((maxY - y) / rangeY, 2); // bias: colorEnd appears sooner
      const r = cStart.r + (cEnd.r - cStart.r) * alpha;
      const g = cStart.g + (cEnd.g - cStart.g) * alpha;
      const b = cStart.b + (cEnd.b - cStart.b) * alpha;
      const idx = i * 3;
      vertexColors[idx] = r;
      vertexColors[idx + 1] = g;
      vertexColors[idx + 2] = b;
    }

    geometry.setAttribute('color', new THREE.BufferAttribute(vertexColors, 3));

    const material = new THREE.MeshBasicMaterial({
      vertexColors: true as unknown as THREE.Colors,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.9,
    });

    return new THREE.Mesh(geometry, material);
  }

  /**
   * createArrow (canvas.view3d:5692-5725): shaft + head with sibling
   * cross-links (dim-both on click, 6d), centered so rotation.y preserves X,
   * shaft rotation.z π/12, group scale 0.75.
   */
  createArrow(colorStart: number = 0xdddddd, colorEnd: number = 0x518449): any {
    const arrow_width = 2;
    const arrow_height = 1.6;
    const tube_length = 2.5;
    const tube_depth = 0.5;
    const tube_width = 0.5;
    const shaft: any = this.createEllipticalTube(tube_length, tube_depth, tube_width, colorStart, colorEnd);
    const head: any = this.createArrowHead(arrow_width, arrow_height, colorStart, colorEnd);
    head.sibling = shaft;
    shaft.sibling = head;
    head.rotation.z = Math.PI / 2;
    // Center the arrow group at x=0 so that any rotation.y preserves the
    // visual x position. Total span = tube_length + (arrow_height − 0.4).
    head.position.x = -(tube_length + arrow_height - 0.4) / 2;
    shaft.position.x = (arrow_height - 0.4 - tube_length) / 2;
    head.position.y = tube_width * 0.8;
    head.position.z = -0.75;
    shaft.rotation.z = Math.PI / 12;
    const arrow: any = new THREE.Group();
    arrow.shaft = shaft;
    arrow.add(shaft);
    arrow.add(head);
    arrow.scale.x = 0.75;
    arrow.scale.y = 0.75;
    arrow.scale.z = 0.75;
    return arrow;
  }

  // =========================================================================
  // G7-G10 — LIFTING LUGS
  // =========================================================================

  /**
   * G7 drawLiftingLug (canvas.view3d:1549-1628) — single_plate: flared
   * trapezoid + twin quadratic arcs + lifting hole, extruded. [NEG]#100: the
   * black WireframeGeometry line is built and passed to the callback but
   * drawLug never adds it — no outline on single-plate lugs; replicate.
   */
  drawLiftingLug(
    { width, height, protrusion_from_base, lifting_hole_dia, color = 0x777777 }: any,
    callback: (r: any) => void
  ): void {
    const scaleInput = this.coef;
    const lugWidth = height * scaleInput * 1.25;
    const lugHeight = protrusion_from_base * scaleInput;
    const lugDepth = width * scaleInput;

    const holeRadius = (lifting_hole_dia / 2) * scaleInput;
    const topArcRadius = lugWidth * 0.25;
    const sideInset = lugWidth * 0.25;

    const baseWidthScale = 0.7;
    const baseInset = (lugWidth * (1 - baseWidthScale)) / 2;

    const flareFactor = 0.25;
    const bottomInsetFactor = 0.15;

    const bottomLeftFlaredX = baseInset - lugWidth * flareFactor;
    const bottomRightX = lugWidth - baseInset - lugWidth * bottomInsetFactor;

    const bottomY = 0;
    const cutSize = lugWidth * 0.1;

    const topRight = { x: lugWidth - sideInset, y: lugHeight - topArcRadius };
    const topCenter = { x: lugWidth / 2, y: lugHeight };
    const topLeft = {
      x: sideInset - lugWidth * flareFactor * 0.5,
      y: lugHeight - topArcRadius,
    };

    const shape = new THREE.Shape();

    shape.moveTo(bottomLeftFlaredX + cutSize, bottomY);
    shape.lineTo(topLeft.x, topLeft.y);

    shape.quadraticCurveTo(topCenter.x - topArcRadius, lugHeight, topCenter.x, lugHeight);
    shape.quadraticCurveTo(topCenter.x + topArcRadius, lugHeight, topRight.x, topRight.y);

    shape.lineTo(bottomRightX, bottomY);
    shape.lineTo(bottomLeftFlaredX + cutSize, bottomY);

    const holeCenterX = lugWidth / 2;
    const holeCenterY = lugHeight - topArcRadius * 0.8;
    const holePath = new THREE.Path();
    holePath.absarc(holeCenterX, holeCenterY, holeRadius, 0, Math.PI * 2, false);
    shape.holes.push(holePath);

    const extrudeSettings = {
      depth: lugDepth,
      bevelEnabled: false,
    };

    const geometry = new THREE.ExtrudeGeometry(shape, extrudeSettings);
    const material = new THREE.MeshBasicMaterial({ color, side: THREE.DoubleSide });
    const mesh: any = new THREE.Mesh(geometry, material);

    const wireframe = new THREE.LineSegments(
      new THREE.WireframeGeometry(geometry as any),
      new THREE.LineBasicMaterial({ color: 0x000000 })
    );
    mesh.depth = lugDepth;
    mesh.width = lugWidth;
    mesh.height = lugHeight;
    const group = new THREE.Group();
    group.add(mesh);
    callback({ shape: mesh, wireframe, height: height * scaleInput });
  }

  /**
   * G8 drawWeldOnLug (canvas.view3d:1630-1753) — tri_plane: base/front/side
   * extruded plates, front w/ round hole + ellipse slot, side w/ center hole;
   * each plate gets the open-box edge overlay (0x2f2f2f, depthTest off).
   */
  drawWeldOnLug(
    { width, protrusion_from_base, height, thickness = lugThickness, color = 0x777777 }: any,
    callback: (r: any) => void
  ): void {
    const scale = this.coef;
    const bw = protrusion_from_base * scale; // front width
    const bd = width * scale; // base depth
    const vh = height * scale;
    const th = thickness * scale;

    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: false,
      opacity: 1,
    });

    // helper: open box edges (no top, no holes) — :1648-1676
    function addOpenBoxEdges(w: number, h: number, _depth: number, edgeColor = 0x2f2f2f): THREE.LineSegments {
      const p = [
        // bottom rectangle
        0, 0, 0, w, 0, 0,
        w, 0, 0, w, h, 0,
        // vertical edges (2 walls only)
        0, 0, 0, 0, h, 0,
        w, 0, 0, w, h, 0,
        // top horizontal line (connects vertical edges)
        0, h, 0, w, h, 0,
      ];
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(p, 3));
      const line = new THREE.LineSegments(
        g,
        new THREE.LineBasicMaterial({
          color: edgeColor,
          depthTest: false,
        })
      );
      line.renderOrder = 1;
      return line;
    }

    // base mesh
    const baseShape = new THREE.Shape();
    baseShape.moveTo(0, 0);
    baseShape.lineTo(bd, 0);
    baseShape.lineTo(bd, vh);
    baseShape.lineTo(0, vh);
    baseShape.closePath();

    const baseGeom = new THREE.ExtrudeGeometry(baseShape, {
      depth: th,
      bevelEnabled: false,
    } as any);

    const baseMesh = new THREE.Mesh(baseGeom, material);
    baseMesh.add(addOpenBoxEdges(bd, vh, th));

    // front mesh
    const frontShape = new THREE.Shape();
    frontShape.moveTo(0, 0);
    frontShape.lineTo(bw, 0);
    frontShape.lineTo(bw, vh);
    frontShape.lineTo(0, vh);
    frontShape.closePath();

    const fHole1 = new THREE.Path();
    fHole1.absarc(bw * 0.25, vh * 0.75, bw * 0.08, 0, Math.PI * 2, false);
    frontShape.holes.push(fHole1);

    const fSlot = new THREE.Path();
    fSlot.absellipse(bw * 0.6, vh * 0.4, bw * 0.15, vh * 0.25, 0, Math.PI * 2, false, 0);
    frontShape.holes.push(fSlot);

    const frontGeom = new THREE.ExtrudeGeometry(frontShape, {
      depth: th,
      bevelEnabled: false,
    } as any);

    const frontMesh = new THREE.Mesh(frontGeom, material);
    frontMesh.rotation.y = Math.PI / 2;
    frontMesh.position.set(0, 0, th);
    frontMesh.add(addOpenBoxEdges(bw, vh, th));

    // side mesh
    const sideShape = new THREE.Shape();
    sideShape.moveTo(0, 0);
    sideShape.lineTo(bd, 0);
    sideShape.lineTo(bd, vh);
    sideShape.lineTo(0, vh);
    sideShape.closePath();

    const sHole = new THREE.Path();
    sHole.absarc(bd / 2, vh / 2, bd * 0.1, 0, Math.PI * 2, false);
    sideShape.holes.push(sHole);

    const sideGeom = new THREE.ExtrudeGeometry(sideShape, {
      depth: th,
      bevelEnabled: false,
    } as any);

    const sideMesh = new THREE.Mesh(sideGeom, material);
    sideMesh.rotation.x = -Math.PI / 2;
    sideMesh.add(addOpenBoxEdges(bd, vh, th));

    // group
    const group: any = new THREE.Object3D();
    group.add(baseMesh);
    group.add(frontMesh);
    group.add(sideMesh);

    group.wall_thickness = th;
    group.wall_width = bd;
    group.frontMesh = frontMesh;

    callback({ shape: group });
  }

  /**
   * G9 drawFivePlateLug (canvas.view3d:1756-1780) — hat_channel: 2 base
   * plates + 2 vertical plates + 1 big-hole plate; right-side clones offset
   * +4.0·coef. NB the right base offsets by the MODULE lugThickness, not the
   * param (verbatim :1773).
   */
  drawFivePlateLug({ width, height, thickness = lugThickness, color = 0x777777 }: any, callback: (r: any) => void): void {
    const group: any = new THREE.Group();
    const element_width = width / 4;
    this.drawBasePlate({ width, depth: element_width, height, thickness, color }, ({ shape: leftBase }: any) => {
      this.drawVerticalPlate({ width, depth: element_width, height, thickness, color }, ({ shape: leftVertical }: any) => {
        this.drawBigHolePlate({ depth: 2 * element_width, height, thickness, color }, ({ shape: topBigHole }: any) => {
          group.add(leftBase);
          group.add(leftVertical);
          group.add(topBigHole);
          topBigHole.position.x = element_width * this.coef;

          const rightVertical = leftVertical.clone();
          rightVertical.position.x += 4.0 * this.coef;
          group.add(rightVertical);

          const rightBase = leftBase.clone();
          rightBase.position.x = rightVertical.position.x - lugThickness * this.coef;
          group.add(rightBase);
          group.thickness = thickness * this.coef;
          callback({ shape: group });
        });
      });
    });
  }

  /** drawBasePlate (canvas.view3d:1839-1883) — hat-channel base plate w/ 4 corner holes, opacity 0.8. */
  drawBasePlate({ width: _width, depth, height, thickness, color = 0x777777 }: any, callback?: (r: any) => void): void {
    const scale = this.coef;
    const bw = height * scale;
    const bd = depth * scale;
    const th = thickness * scale;

    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    });

    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(bd, 0);
    shape.lineTo(bd, bw);
    shape.lineTo(0, bw);
    shape.closePath();

    const holeRadius = Math.min(bd, bw) * 0.08;
    const holePositions: Array<[number, number]> = [
      [bd * 0.25, bw * 0.25],
      [bd * 0.75, bw * 0.25],
      [bd * 0.25, bw * 0.75],
      [bd * 0.75, bw * 0.75],
    ];

    holePositions.forEach(([hx, hy]) => {
      const h = new THREE.Path();
      h.absarc(hx, hy, holeRadius, 0, Math.PI * 2, false);
      shape.holes.push(h);
    });

    const mesh = new THREE.Mesh(
      new THREE.ExtrudeGeometry(shape, { depth: th, bevelEnabled: false } as any),
      material
    );

    if (callback) callback({ shape: mesh });
  }

  /** drawVerticalPlate (canvas.view3d:1885-1921) — plateHeight 2.12, rotated z=π/2 + y=−π/2. */
  drawVerticalPlate(
    { width: _width, depth, height, thickness, plateHeight = 2.12, color = 0x777777 }: any,
    callback?: (r: any) => void
  ): void {
    const scale = this.coef;
    const bw = height * scale;
    const bd = depth * scale;
    const th = thickness * scale;

    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    });

    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(bw, 0);
    shape.lineTo(bw, plateHeight * scale);
    shape.lineTo(0, plateHeight * scale);
    shape.closePath();

    const mesh = new THREE.Mesh(
      new THREE.ExtrudeGeometry(shape, { depth: th, bevelEnabled: false } as any),
      material
    );

    mesh.rotation.z = Math.PI / 2;
    mesh.rotation.y = -Math.PI / 2;
    mesh.position.x = bd;
    mesh.position.y = 0;
    mesh.position.z = 0;

    if (callback) callback({ shape: mesh });
  }

  /** drawBigHolePlate (canvas.view3d:1923-1959) — plateLength 4.0 × height, hole r 0.8·coef, z = −2.12·coef. */
  drawBigHolePlate(
    { depth, height, thickness, plateLength = 4.0, holeRadius = 0.8, color = 0x777777 }: any,
    callback?: (r: any) => void
  ): void {
    const scale = this.coef;
    const bd = depth * scale;
    const th = thickness * scale;

    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.8,
    });

    const shape = new THREE.Shape();
    shape.moveTo(0, 0);
    shape.lineTo(plateLength * scale, 0);
    shape.lineTo(plateLength * scale, height * scale);
    shape.lineTo(0, height * scale);
    shape.closePath();

    const h = new THREE.Path();
    h.absarc((plateLength * scale) / 2, (height * scale) / 2, holeRadius * scale, 0, Math.PI * 2, false);
    shape.holes.push(h);

    const mesh = new THREE.Mesh(
      new THREE.ExtrudeGeometry(shape, { depth: th, bevelEnabled: false } as any),
      material
    );

    mesh.position.x = bd;
    mesh.position.y = 0;
    mesh.position.z = -2.12 * scale;

    if (callback) callback({ shape: mesh });
  }

  /**
   * G10 drawLug (canvas.view3d:3207-3273): re-draw removes the prior
   * instance; `removed=true` purges + drops from activeLugs; left-side /
   * dmount-pair flags; style dispatch; matrix locked after positioning;
   * registries + active-lug re-link; adjoining-base visibility test at draw
   * time. (Defensive add: legacy throws when the base is missing — drawLug is
   * only reached under a drawn base, the guard keeps React alive on the same
   * edge.)
   */
  drawLug(lug: any, tunnel: any, tunnel_id: number, removed = false): void {
    if (!this.tunnelBases[tunnel_id]) return;
    const { cube, group } = this.tunnelBases[tunnel_id];
    const baseType = tunnel?.attributes?.base?.type;
    if (this.tunnels_lugs[tunnel.id]?.[lug.id]) {
      group.remove(this.tunnels_lugs[tunnel.id][lug.id]);
    }
    if (removed) {
      this.activeLugs = this.activeLugs.filter((e: any) => e.lug_id != lug.id);
      return;
    }
    const lug_group = new THREE.Group();
    let onLeftSide = (lug.base_direction == 1 && lug.y == 0) || (lug.base_direction == 2 && lug.x > 0);
    const forDmount = lug.settings?.includes('dmount');
    let closing = false;
    let opening = false;
    if (forDmount) {
      const lugs = this.getTunnelLugs(tunnel.id);
      const next = lugs?.positions?.find((e: any) => e.id === lug.id + 1);
      if (next?.settings.includes('dmount')) closing = true;
      else {
        const prev = lugs?.positions?.find((e: any) => e.id === lug.id - 1);
        if (prev?.settings.includes('dmount')) opening = true;
      }
    }
    if (tunnel.get('airflow_direction_id') == 2) onLeftSide = !onLeftSide;
    this.lugStyles[lug.model_details.style](lug.model_details, ({ shape, height }: any) => {
      shape.model_data = lug;
      this.lugsPositioning[lug.model_details.style](shape, cube, height, {
        along_width: lug.base_direction == 2,
        along_length: lug.base_direction == 1,
        baseType,
        onLeftSide,
        lugX: lug.x * this.coef,
        lugY: lug.y * this.coef,
        airflow_direction_id: tunnel.get('airflow_direction_id'),
        dmount: {
          forDmount,
          opening,
          closing,
        },
      });
      // apply transforms into matrix, then lock it (:3249-3254)
      shape.updateMatrix();
      shape.matrixAutoUpdate = false;
      shape.updateMatrixWorld(true);
      lug_group.add(shape);
      group.add(lug_group);
      shape.shape_type = 'tunnel_lug';
      shape.tunnel_id = tunnel.id;
      shape.lug_id = lug.id;
      if (!this.tunnels_lugs[tunnel.id]) this.tunnels_lugs[tunnel.id] = {};
      this.tunnels_lugs[tunnel.id][lug.id] = lug_group;
      this.tunnels_lugs_on_canvas[lug.id] = lug_group;
      const active_lug_index = this.activeLugs.findIndex((l: any) => l.lug_id === lug.id);
      if (active_lug_index > -1) {
        this.activeLugs[active_lug_index] = shape;
        this.resetLugColor(shape, sectionMaterialColorActive);
      }
      if (!(this.tunnelModels.length < 2 || !this.next_to) && !this.activeTunnelIndex) {
        this.isLugVisibleOnAdjoiningBase(lug, tunnel, this.tunnelModels.indexOf(tunnel) + 1, this.tunnelModels.length);
      }
    });
    this.need_update = true;
  }

  /** redrawTunnelLug (canvas.view3d:1528-1531) — §3.4 #28 receiver. */
  redrawTunnelLug(tunnel_id: number, lug: any, deleted = false): void {
    const tunnel = this.tunnelModels.find((t) => t.id == tunnel_id);
    if (!tunnel) return;
    this.drawLug(lug, tunnel, tunnel.id, deleted);
  }

  /**
   * redrawTunnelLugs (canvas.view3d:1533-1546) — §3.4 #27 receiver. NB the
   * stale-entry delete uses the THREE group id, not the lug model id
   * (verbatim legacy slip — drawLug re-keys the registry right after).
   */
  redrawTunnelLugs(tunnel_id: number, _removed = false): void {
    Object.values(this.tunnels_lugs[tunnel_id] || []).forEach((lug: any) => {
      lug.parent.remove(lug);
      delete this.tunnels_lugs_on_canvas[lug.id];
    });
    delete this.tunnels_lugs[tunnel_id];
    const tunnel = this.tunnelModels.find((t) => t.id == tunnel_id);
    if (!tunnel || !tunnel.get('lug') || !tunnel.get('base_id')) return;
    const lug_models = this.getTunnelLugs(tunnel_id);
    if (!lug_models || lug_models.hide_lugs) return;
    (lug_models.positions || []).forEach((lug: any) => {
      this.drawLug(lug, tunnel, tunnel.id);
    });
  }

  /**
   * showHideLugsOnAdjoiningBase (canvas.view3d:1782-1803) — next_to
   * multi-tunnel only; isolation mode shows all. Overrides the 6b base no-op
   * (changeIndex / checkTunnelsLength call sites).
   */
  showHideLugsOnAdjoiningBase(): void {
    if (this.tunnelModels.length < 2 || !this.next_to) return;

    if (!this.activeTunnelIndex) {
      this.tunnelModels.forEach((tunnel, idx) => {
        const tunnel_index_id = idx + 1;
        const lugModels = this.getTunnelLugs(tunnel.id);
        if (!lugModels) return;

        (lugModels.positions || []).forEach((lug: any) => {
          this.isLugVisibleOnAdjoiningBase(lug, tunnel, tunnel_index_id, this.tunnelModels.length);
        });
      });
    } else {
      Object.values(this.tunnels_lugs_on_canvas).forEach((e: any) => (e.visible = true));
    }
  }

  /** isLugVisibleOnAdjoiningBase (canvas.view3d:1805-1836). */
  isLugVisibleOnAdjoiningBase(lug: any, tunnel: any, tunnelIndexId: number, totalTunnels: number): void {
    const lug_group = this.tunnels_lugs_on_canvas[lug.id];
    if (!lug_group) return;
    const airflow_direction_id = tunnel.get('airflow_direction_id');
    const lug_base_direction = lug.base_direction;
    const lug_y = lug.y;

    // Default visible
    let visible = true;

    if (tunnelIndexId === 1) {
      // FIRST TUNNEL LOGIC
      if (lug_base_direction === 1 && (airflow_direction_id == 1 ? lug_y > 0 : lug_y === 0)) {
        visible = false;
      }
    } else {
      // OTHER TUNNELS LOGIC
      if (lug_base_direction === 1) {
        const isNotLastTunnel = totalTunnels !== tunnelIndexId;
        const airflowCondition = airflow_direction_id == 2 ? lug_y > 0 : lug_y === 0;

        if (isNotLastTunnel || airflowCondition) {
          visible = false;
        }
      }
    }

    lug_group.visible = visible;
  }

  /**
   * calculateLugPosition (canvas.view3d:3483-3531) — single_plate
   * positioner: base rotation (−π/2, π/2, π), along_width extra rotateX,
   * airflow-aware X/Z from half-extents ± lug offsets, c_channel Z inset,
   * right-side flip.
   */
  calculateLugPosition(lug: any, cube: any, height: any, options: any = {}): void {
    const { onLeftSide, lugX, lugY, along_width, along_length, baseType, airflow_direction_id } = options;
    const cubeBox = new THREE.Box3().setFromObject(cube);
    const cubeCenter = new THREE.Vector3();
    cubeBox.getCenter(cubeCenter);
    const cubeSize = new THREE.Vector3();
    cubeBox.getSize(cubeSize);

    lug.rotation.set(-Math.PI / 2, Math.PI / 2, Math.PI);

    if (along_width) {
      lug.rotateX(Math.PI / 2);
    }

    lug.updateMatrixWorld(true);

    const lugBox = new THREE.Box3().setFromObject(lug);
    const lugSize = new THREE.Vector3();
    lugBox.getSize(lugSize);

    const unit_airflow_direction_id = airflow_direction_id == 1 ? 1 : -1;
    let x = 0;
    let z = 0;
    const xSign = -1;
    const zSign = onLeftSide ? 1 : -1;

    if (along_length) {
      x =
        unit_airflow_direction_id *
        ((xSign * cube.geometry.parameters.width) / 2 + lugX - (!onLeftSide ? -lug.depth : 0));
      z = (zSign * cube.geometry.parameters.depth) / 2;
      if (baseType === 'c_channel') z -= zSign * this.coef;
    } else if (along_width) {
      z =
        unit_airflow_direction_id *
        ((-xSign * cube.geometry.parameters.depth) / 2 - lugY - (airflow_direction_id == 2 ? lug.depth : 0));
      x = (zSign * cube.geometry.parameters.width) / 2;
    }
    if (!onLeftSide) {
      lug.rotateX(Math.PI);
      if (along_width) z -= lug.depth;
    }
    const y = height / 2;
    lug.position.set(x, -y, z);
  }

  /**
   * calculateWeldPosition (canvas.view3d:3401-3481) — tri_plane positioner
   * incl. the dmount opening/closing flip table and the past-midline mirror
   * (`scale.x/z *= −1`).
   */
  calculateWeldPosition(lug: any, cube: any, _height: any, options: any = {}): void {
    const { onLeftSide, lugX, lugY, along_width, along_length, airflow_direction_id, dmount } = options;

    const wallThickness = lug.wall_thickness;
    const cubeBox = new THREE.Box3().setFromObject(cube);
    const cubeSize = new THREE.Vector3();
    cubeBox.getSize(cubeSize);

    // Direction signs
    const xSign = onLeftSide ? -1 : 1;
    const zSign = onLeftSide ? 1 : -1;

    let x = 0,
      y = 0,
      z = 0;
    let rotationY = 0;

    // CASE 1: Lug along length
    if (along_length) {
      const isForward = airflow_direction_id === 1;
      const sideOffset = onLeftSide ? 0 : lug.wall_width;
      const oppositeSideOffset = !onLeftSide ? 0 : lug.wall_width;

      x = isForward ? -(cubeSize.x / 2) + sideOffset + lugX : cubeSize.x / 2 - oppositeSideOffset - lugX;
      z = zSign * (cubeSize.z / 2 + wallThickness);

      if (onLeftSide) rotationY = xSign * Math.PI;
      const isAirflowForward = airflow_direction_id === 1;
      let shouldFlip = false;
      if (dmount?.forDmount) {
        const rotate = (
          {
            true: { true: dmount.closing, false: dmount.opening },
            false: { true: dmount.opening, false: dmount.closing },
          } as any
        )[String(isAirflowForward)][String(onLeftSide)];
        shouldFlip = rotate ? false : true;
      } else
        shouldFlip =
          (onLeftSide && ((isAirflowForward && lugX < cubeSize.x / 2) || (!isAirflowForward && lugX > cubeSize.x / 2))) ||
          (!onLeftSide && ((isAirflowForward && lugX > cubeSize.x / 2) || (!isAirflowForward && lugX < cubeSize.x / 2)));
      // If lug offset exceeds cube side → flip
      if (shouldFlip) {
        lug.scale.x *= -1;
      } else {
        x -= xSign * lug.wall_width;
      }
    }

    // CASE 2: Lug along width
    else if (along_width) {
      rotationY = (xSign * Math.PI) / 2;
      // Base X position for width mounting
      x = -xSign * (cubeSize.x / 2 + wallThickness);
      // Base Z position considering airflow
      z = (onLeftSide ? -lug.wall_width : 0) + cubeSize.z / 2 - lugY;
      if ((!onLeftSide && lugY > cubeSize.z / 2) || (onLeftSide && lugY < cubeSize.z / 2)) {
        // Mirrored effect: flip along Z axis
        lug.scale.z *= -1;
        rotationY -= Math.PI; // rotate 180° for mirrored orientation
        z += zSign * lug.wall_width;
      }
    }

    // Vertical (Y) position — always bottom
    y = -(cubeSize.y / 2);

    // Apply result
    lug.position.set(x, y, z);
    lug.rotation.y = rotationY;
  }

  /**
   * calculateFivePlateLugPosition (canvas.view3d:3334-3399) — hat_channel
   * positioner: y at base bottom + 0.625·coef, per-side end offsets by
   * airflow, rotationY ∈ {0, π, ±π/2}.
   */
  calculateFivePlateLugPosition(lugGroup: any, cube: any, _height: any, options: any = {}): void {
    const { along_length, along_width, onLeftSide, lugX, lugY, airflow_direction_id } = options;

    const coef = this.coef;
    const heightFromBottom = 0.625 * coef;
    const thickness = lugGroup.thickness;

    const airflowUnit = airflow_direction_id === 1 ? 1 : -1;

    // Cube dimensions
    const cubeBox = new THREE.Box3().setFromObject(cube);
    const cubeSize = new THREE.Vector3();
    cubeBox.getSize(cubeSize);

    // Lug dimensions
    lugGroup.updateMatrixWorld(true);
    const lugBox = new THREE.Box3().setFromObject(lugGroup);
    const lugSize = new THREE.Vector3();
    lugBox.getSize(lugSize);

    // Default transform
    let x = 0;
    const y = -(cubeSize.y / 2) + heightFromBottom;
    let z = 0;
    let rotationY = 0;

    if (along_length) {
      const lugOffset = airflow_direction_id === 1 ? lugSize.x : 0;

      if (onLeftSide) {
        x = airflowUnit * (-cubeSize.x / 2 + lugX) + lugOffset;
        z = cubeSize.z / 2 + thickness;
        rotationY = Math.PI;
      } else {
        const altOffset = airflow_direction_id === 2 ? lugSize.x : 0;
        x = airflowUnit * (-cubeSize.x / 2 + lugX) + altOffset;
        z = -cubeSize.z / 2 - thickness;
        rotationY = 0;
      }
    } else if (along_width) {
      if (onLeftSide) {
        const zOffset = airflow_direction_id === 2 ? lugSize.x : 0;
        x = -cubeSize.x / 2 - thickness;
        z = airflowUnit * (cubeSize.z / 2 - lugY) + zOffset;
        rotationY = Math.PI / 2;
      } else {
        const zOffset = airflow_direction_id === 1 ? lugSize.x : 0;
        x = cubeSize.x / 2 + thickness;
        z = airflowUnit * (cubeSize.z / 2 - lugY) - zOffset;
        rotationY = -Math.PI / 2;
      }
    }

    // Apply transforms
    lugGroup.position.set(x, y, z);
    lugGroup.rotation.y = rotationY;
  }

  /**
   * G10 updateLugStyles (canvas.view3d:3141-3193) — the lug selection state
   * machine: hover tint (unless active), click-outside clear, ctrl-click
   * same-tunnel/same-direction filter with FIFO cap 2, select tint +
   * `change:active:lugs` broadcast. The `$('#canvas_section canvas')`
   * cursor-pointer toggle is the renderer's own canvas (§3.5). INPUT (the
   * raycast intersectsLug) arrives with the 6d picker.
   */
  updateLugStyles(intersectsLug: any, event: any, isCtrlPressed?: boolean): void {
    const hasIntersection = Boolean(intersectsLug);

    this.renderer.domElement.classList.toggle('cursor-pointer', hasIntersection);

    /* ---------- HOVER ---------- */
    if (event === 'hover') {
      if (!hasIntersection) return;

      const isAlreadyActive = this.activeLugs.some((lug: any) => lug.lug_id === intersectsLug.lug_id);

      if (!isAlreadyActive) {
        this.changeLugColor(intersectsLug, event);
        this.hoveredLug = intersectsLug;
      }
      return;
    }
    /* ---------- CLICK OUTSIDE ---------- */
    if (!hasIntersection) {
      this.activeLugs.forEach((lug: any) => this.resetLugColor(lug));
      this.activeLugs = [];
      this.emitBus('change:active:lugs');
      return;
    }
    /* ---------- SELECTION LOGIC ---------- */
    if (isCtrlPressed) {
      const filtered_lugs: any[] = [];
      this.activeLugs.forEach((e: any) => {
        const keep =
          e?.model_data?.tunnel === intersectsLug?.model_data?.tunnel &&
          e?.model_data?.base_direction === intersectsLug?.model_data?.base_direction;
        if (!keep) this.resetLugColor(e);
        else filtered_lugs.push(e);
      });
      this.activeLugs = filtered_lugs;
      // limit max selected lugs
      if (this.activeLugs.length === this.max_selected_lug_count) {
        const removedLug = this.activeLugs.shift();
        this.resetLugColor(removedLug);
      }
    } else {
      // single selection mode
      this.activeLugs.forEach((lug: any) => this.resetLugColor(lug));
      this.activeLugs = [];
    }
    /* ---------- ADD NEW ACTIVE LUG ---------- */
    this.activeLugs.push(intersectsLug);
    this.changeLugColor(intersectsLug, event);

    this.emitBus('change:active:lugs', this.activeLugs);
  }

  /**
   * Select drawn lugs by id — updateLugStyles' selection half without a
   * raycast, for callers that just created a lug and know which one it is.
   * Same shape as a click: previous selection reset, the FIFO cap honoured,
   * one `change:active:lugs` broadcast so the pane's Data Bar fills in.
   * Ids with nothing drawn (never rendered, or hidden on an adjoining base)
   * are skipped — activeLugs must only ever hold real shapes, resetLugColor
   * walks their children.
   */
  selectLugsById(lugIds: Array<number | string>): void {
    this.activeLugs.forEach((lug: any) => this.resetLugColor(lug));
    this.activeLugs = [];
    lugIds.slice(-this.max_selected_lug_count).forEach((id) => {
      const shape = this.tunnels_lugs_on_canvas[id as any]?.children?.[0];
      if (!shape) return;
      this.activeLugs.push(shape);
      this.changeLugColor(shape, 'click');
    });
    this.need_update = true;
    this.emitBus('change:active:lugs', this.activeLugs);
  }

  /** changeLugColor (canvas.view3d:3195-3200). */
  changeLugColor(shape: any, event: any): void {
    if (!shape) return;
    const color = event === 'hover' ? sectionMaterialColorHovered : sectionMaterialColorActive;
    shape.children.forEach((element: any) => element.material.color.set(color));
    shape.material?.color.set(color);
  }

  /** resetLugColor (canvas.view3d:3202-3205). */
  resetLugColor(lug: any, color: number = 0x777777): void {
    lug?.material?.color.set(color);
    lug?.children.forEach((element: any) => element.material.color.set(color));
  }

  // =========================================================================
  // G11 — INTERNAL WALLS
  // =========================================================================

  /**
   * drawInternalWall (canvas.view3d:1984-2021): PB fill (metal → grey /
   * missing sheet → error red) + wireframe, child of the section's casing
   * cube (preferred) or section cube; first draw re-parents + redraws any
   * accessories mounted on this wall id (:2003-2019). Defensive add: bail
   * when the host section is not on canvas yet (legacy's storage-poll timing
   * guarantees it; the bus replay keeps that, the guard covers the create
   * window).
   */
  drawInternalWall(id: number | string, model: any, redraw = false): void {
    if (this.int_wall[model.plane as 'vertical' | 'horizontal'][id] && !redraw) return;
    if (!this.sectionCanvasObjectsObj[model.section_id]) return;
    this.need_update = true;
    const color = model.metal_sheet_id ? sectionMaterialColor : errorColor;
    const width =
      model.plane === 'horizontal'
        ? model.width
        : model.end_height_flom_flr - model.start_height_flom_flr || model.width;
    const rectangleGeometry = new THREE.PlaneBufferGeometry(model.length * this.coef, width * this.coef);
    const rectangleMaterial = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.6,
    });
    const rectangle = new THREE.Mesh(rectangleGeometry, rectangleMaterial);
    const edges = new THREE.EdgesGeometry(rectangleGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: model.metal_sheet_id ? wireframeMaterialColorAccessory : errorColorSelected,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    const cube =
      this.sectionCanvasObjectsObj[model.section_id].casing?.cube ||
      this.sectionCanvasObjectsObj[model.section_id].cube;
    const tunnel = this.sectionCanvasObjectsObj[model.section_id].tunnel;
    const start_y = this.tunnelStartYByFloorConstr[tunnel.id] * this.coef;
    wireframe.scale.set(1.001, 1.001, 1.001);
    cube.add(wireframe);
    cube.add(rectangle);
    (this as any)[`${model.plane}IntWallPosition`](model, rectangle, wireframe, cube, tunnel, start_y);
    this.int_wall[model.plane as 'vertical' | 'horizontal'][id] = {
      model,
      rectangle,
      wireframe,
      cube,
      type: `${model.plane}_internal_wall`,
    };
    if (!redraw) {
      const accessories = this.accessoryCanvasObjects.filter(
        (el: any) => el.int_wall?.model?.id === id || el.int_wall_model?.id === id
      );
      const up = this.accessoryCanvasObjects.filter(
        (el: any) => el.int_wall?.model?.id !== id && el.int_wall_model?.id !== id
      );
      this.accessoryCanvasObjects = up;
      accessories.forEach(({ accessory, section, mounting, int_wall_model }: any) => {
        this.drawAccessory({
          accessory,
          section,
          width: accessory.width || accessory.overall_width || accessory.width_calc,
          height: accessory.height || accessory.overall_depth || accessory.height_calc,
          name: accessory.name,
          mounting,
          isAlredyRendered: false,
          depth: accessory.depth,
          int_wall_model,
        });
      });
    }
  }

  /** updateInternallWall (canvas.view3d:2022-2025) — §3.4 #18 receiver (destroy + redraw). */
  updateInternallWall(model: any): void {
    this.destroyInternallWall(model);
    this.drawInternalWall(model.id, model);
  }

  /** destroyInternallWall (canvas.view3d:2026-2034) — §3.4 #19 receiver. */
  destroyInternallWall(model: any): void {
    this.need_update = true;
    const wall = this.int_wall[model.plane as 'vertical' | 'horizontal'][model.id];
    if (!wall) return; // defensive — remove for a never-drawn wall (legacy throws here)
    this.disposeChilds(wall.rectangle);
    this.disposeChilds(wall.wireframe);
    wall.cube.remove(wall.rectangle);
    wall.cube.remove(wall.wireframe);
    delete this.int_wall[model.plane as 'vertical' | 'horizontal'][model.id];
  }

  /** verticalIntWallPosition (canvas.view3d:2035-2047). */
  verticalIntWallPosition(model: any, rectangle: any, wireframe: any, cube: any, tunnel: any, start_y: number): void {
    const angle = this.getRotationAngle(model.angle_at_left_wall);
    rectangle.rotation.y = Math.PI / 2 + angle;
    wireframe.rotation.y = Math.PI / 2 + angle;
    const diff = this.getDistanceAfterRotation(
      rectangle.geometry.parameters.width / 2,
      angle,
      model.angle_at_left_wall
    );
    const position = new THREE.Vector3(0, 0, 0);
    const unit = -2 * tunnel.get('airflow_direction_id') + 3;
    position.x =
      unit *
      (-0.01 - cube.geometry.parameters.width / 2 + model.dist_from_ups_edge_on_left_wall * this.coef + diff.y);
    position.z =
      unit *
      (-(cube.geometry.parameters.depth - rectangle.geometry.parameters.width) / 2 +
        model.dist_from_left_wall * this.coef -
        diff.x);
    position.y =
      start_y +
      (-(cube.geometry.parameters.height - rectangle.geometry.parameters.height) / 2 +
        model.start_height_flom_flr * this.coef);
    rectangle.position.copy(position);
    wireframe.position.copy(position);
  }

  /** horizontalIntWallPosition (canvas.view3d:2048-2063). */
  horizontalIntWallPosition(model: any, rectangle: any, wireframe: any, cube: any, tunnel: any, start_y: number): void {
    const angle = this.getRotationAngle(model.angle_from_floor);
    const unit = -2 * tunnel.get('airflow_direction_id') + 3;
    rectangle.rotation.x = Math.PI / 2;
    wireframe.rotation.x = Math.PI / 2;
    rectangle.rotateY(unit * angle);
    wireframe.rotateY(unit * angle);
    const diff = this.getDistanceAfterRotation(
      rectangle.geometry.parameters.width / 2,
      angle,
      model.angle_from_floor
    );
    const position = new THREE.Vector3(0, 0, 0);
    position.y =
      start_y +
      (-cube.geometry.parameters.height / 2 + model.height_floor_flr_at_ups_edge * this.coef + diff.y);
    position.z =
      unit *
      (-(cube.geometry.parameters.depth - rectangle.geometry.parameters.height) / 2 +
        model.dist_from_left_wall * this.coef);
    position.x = unit * (-diff.x - (cube.geometry.parameters.width - rectangle.geometry.parameters.width) / 2);
    rectangle.position.copy(position);
    wireframe.position.copy(position);
  }

  /** redrawIntWalls (canvas.view3d:2064-2070) — replays both planes for a rebuilt section (drawSectionComplete :2557). */
  redrawIntWalls(section: any): void {
    const int_walls: Record<string, any> = {
      ...this.int_wall.vertical,
      ...this.int_wall.horizontal,
    };
    Object.entries(int_walls).forEach(
      (el) => el[1].model.section_id === section.id && this.drawInternalWall(el[0], el[1].model, true)
    );
  }

  // =========================================================================
  // SHARED TRIG HELPERS (G11/G31 inputs)
  // =========================================================================

  /** getRotationAngle (canvas.view3d:504-506) — degrees → radians. */
  getRotationAngle(angle: any): number {
    return (angle * Math.PI) / 180;
  }

  /**
   * getDistanceAfterRotation (canvas.view3d:507-523) — right-triangle split
   * of a half-width `c` rotated by `angle`, with the quadrant sign table
   * keyed off the RAW degree value `angle_at` (G11 positioners).
   */
  getDistanceAfterRotation(c: number, angle: number, angle_at: number): { x: number; y: number; c: number } {
    const a = c * Math.sin(angle);
    const b = Math.sqrt(c * c - a * a);
    if (angle_at < 0) {
      if (angle_at > -270 && angle_at < -90) {
        return { x: c + b, y: a, c: b };
      }
      return { x: c - b, y: a, c: b };
    }
    if (angle_at > 90 && angle_at < 270) {
      return { x: c + b, y: a, c: b };
    }
    return { x: c - b, y: a, c: b };
  }

  /**
   * createPrismGeometry (canvas.view3d:422-440) — Geometry-class
   * ExtrudeGeometry subclass (r110-only API, §42; faces[] consumed by the
   * hood face-paint loop). [NEG]#10: the legacy leaks `PrismGeometry` as a
   * window global — kept module-local here.
   */
  createPrismGeometry(): any {
    const PrismGeometry = function (this: any, vertices: any[], depth: number, curved?: boolean) {
      const Shape = new THREE.Shape();
      const getMaxY = (y1: number, y2: number) => (y1 > y2 ? y1 : y2) * (y1 > y2 ? 0.5 : 2);
      (function f(ctx: any) {
        ctx.moveTo(vertices[0].x, vertices[0].y);
        for (let i = 1; i < vertices.length; i++) {
          if (i == 2 && curved)
            ctx.bezierCurveTo(
              vertices[1].x,
              vertices[1].y,
              vertices[2].x - vertices[1].x,
              getMaxY(vertices[1].y, vertices[2].y),
              vertices[2].x,
              vertices[2].y
            );
          else ctx.lineTo(vertices[i].x, vertices[i].y);
        }
        ctx.lineTo(vertices[0].x, vertices[0].y);
      })(Shape);
      const settings: any = {};
      settings.depth = depth;
      settings.bevelEnabled = false;
      (THREE.ExtrudeGeometry as any).call(this, Shape, settings);
    };
    PrismGeometry.prototype = Object.create((THREE.ExtrudeGeometry as any).prototype);
    return PrismGeometry;
  }

  // =========================================================================
  // G12-G21 — ACCESSORY DISPATCH + SHAPES
  // =========================================================================

  /**
   * drawAccessory (canvas.view3d:1324-1354) — THE dispatch: scales inches →
   * world units, applies the floor-construction Y lift for wall planes 3-6
   * (:1328-1331, `prev_y` memo), then routes through shapesFunctionality by
   * accessoryShapesType[name]; ends with the error/normal recolor pass (G34).
   */
  drawAccessory({
    accessory,
    section,
    width,
    height,
    depth,
    name,
    color = sectionMaterialColor,
    mounting,
    isAlredyRendered,
    viewport,
    int_wall_model,
  }: any): void {
    width = this.coef * width;
    height = this.coef * height;
    depth = this.coef * depth;
    if (mounting && mounting.mounting_plane_id > 2 && mounting.mounting_plane_id < 7) {
      if (mounting.prev_y === undefined) mounting.prev_y = mounting.y;
      mounting.y = mounting.prev_y + this.tunnelStartYByFloorConstr[section.tunnel_id];
    }
    if (isAlredyRendered) {
      this.shapesFunctionality[this.accessoryShapesType[name]]?.update.call(this, {
        accessory,
        section,
        width,
        height,
        depth,
        mounting,
        airflow_direction_id: section.tunnel.get('airflow_direction_id'),
        viewport,
        name,
        int_wall_model,
      });
      if (this.accessoriesWithError[accessory.id])
        this.changeAccessoryColor(
          this.accessoryCanvasObjectsObj[accessory.id],
          this.accessoriesWithError[accessory.id]
        );
      else this.changeAccessoryColor(this.accessoryCanvasObjectsObj[accessory.id]);
      return;
    }
    if (this.accessoryCanvasObjectsObj[accessory.id]) this.destroyAccessory(accessory.id);
    this.shapesFunctionality[this.accessoryShapesType[name]]?.draw(
      {
        width,
        height,
        depth,
        name,
        color,
        section,
        accessory,
        mounting,
        viewport,
        int_wall_model,
      },
      this.shapesFunctionality[this.accessoryShapesType[name]]?.complete.bind(this)
    );
    if (this.accessoriesWithError[accessory.id])
      this.changeAccessoryColor(
        this.accessoryCanvasObjectsObj[accessory.id],
        this.accessoriesWithError[accessory.id]
      );
    else if (this.accessoryCanvasObjectsObj[accessory.id])
      this.changeAccessoryColor(this.accessoryCanvasObjectsObj[accessory.id]);
  }

  /**
   * isCube (canvas.view3d:1474-1489) — extra mount depth through the casing:
   * ACCESS_DOOR default 2·coef; floor w/ under-insulation → that thickness;
   * roof → cubeH − casingH; exposed up/downstream walls → (cubeW − casingW)
   * (halved unless the section is both first AND last); left/right walls →
   * (cubeD − casingD)/2.
   */
  isCube({ name, mounting, section_on_scene }: any): number {
    if (!section_on_scene) return 0;
    const defaultVal = name === 'ACCESS_DOOR' ? 2 * this.coef : 0;
    const airflow_direction_id = section_on_scene.section.tunnel.get('airflow_direction_id');
    const downstream =
      mounting.mounting_plane_id === 3 &&
      ((section_on_scene.last && airflow_direction_id === 1) ||
        (section_on_scene.order === 1 && airflow_direction_id == 2));
    const upstream =
      mounting.mounting_plane_id === 4 &&
      ((section_on_scene.order === 1 && airflow_direction_id === 1) ||
        (section_on_scene.last && airflow_direction_id == 2));
    if (
      mounting.mounting_plane_id === 1 &&
      section_on_scene.section.tunnel.floor.get('under_insulation_label')
    )
      return section_on_scene.section.tunnel.get('under_insulation_thick') * this.coef;
    else if (!section_on_scene.casing) return defaultVal;
    else if (
      mounting.mounting_plane_id === 2 &&
      section_on_scene.cube.geometry.parameters.height -
        section_on_scene.casing.cube.geometry.parameters.height
    ) {
      return (
        section_on_scene.cube.geometry.parameters.height -
        section_on_scene.casing.cube.geometry.parameters.height
      );
    } else if (downstream || upstream)
      return (
        (section_on_scene.cube.geometry.parameters.width -
          section_on_scene.casing.cube.geometry.parameters.width) /
        (section_on_scene.order === 1 && section_on_scene.last ? 2 : 1)
      );
    else if (
      (mounting.mounting_plane_id === 5 || mounting.mounting_plane_id === 6) &&
      section_on_scene.cube.geometry.parameters.depth -
        section_on_scene.casing.cube.geometry.parameters.depth
    ) {
      return (
        (section_on_scene.cube.geometry.parameters.depth -
          section_on_scene.casing.cube.geometry.parameters.depth) /
        2
      );
    }
    return defaultVal;
  }

  /**
   * G12 drawRectangle (canvas.view3d:1491-1526) — OPENING / ACCESS_DOOR:
   * BB-through-wall when isCube else PB; OPENING fill forced opacity 0
   * (:1517); ACCESS_DOOR gets the inner double-line wireframe; both
   * wireframes share ONE LineDashedMaterial (gap 0).
   */
  drawRectangle(
    { accessory, section, width, height, name, color = sectionMaterialColor, mounting, viewport, int_wall_model }: any,
    callback?: (r: any) => void
  ): void {
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const isCube = this.isCube({ name, mounting, section_on_scene });
    const rectangleGeometry = isCube
      ? new THREE.BoxBufferGeometry(width, height, isCube)
      : new THREE.PlaneBufferGeometry(width, height);
    const rectangleMaterial = new THREE.MeshBasicMaterial({
      color: accessoriesColors[name] || color,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.6,
    });
    const rectangle: any = new THREE.Mesh(rectangleGeometry, rectangleMaterial);
    const edges = new THREE.EdgesGeometry(rectangleGeometry as any);
    const rectangleGeometryDouble = new THREE.PlaneBufferGeometry(width - 0.6, height - 0.6);
    const edgesDouble = new THREE.EdgesGeometry(rectangleGeometryDouble);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    const wireframeDouble = new THREE.LineSegments(edgesDouble, wireframeMaterial);
    wireframe.computeLineDistances();
    wireframeDouble.computeLineDistances();
    wireframe.scale.set(1.001, 1.001, 1.001);
    // [NEG]#97: legacy stamps `rectangle.depthFunc = THREE.AlwaysDepth` (:1513)
    // — depthFunc is a Material property; the object assign is a no-op. Dropped.
    rectangle.renderOrder = 0;
    rectangle.accessory = accessory;
    if (name === 'OPENING') {
      rectangle.material.opacity = 0;
    }
    rectangle.name = name;
    // Viewport (G13)
    const cube = section_on_scene.cube;
    const viewport_data = viewport ? this.createViewport(viewport, cube) : null;
    if (callback) {
      callback.call(this, {
        accessory,
        section,
        wireframe,
        wireframeDouble,
        mounting,
        shape: rectangle,
        viewport_data,
        viewport,
        name,
        int_wall_model,
        isCube,
      });
    }
  }

  /**
   * drawRectangleComplete (canvas.view3d:2619-2670): registry upsert,
   * int-wall lookup by mounting_plane_id (>6 = wall id; `mounting_plane`
   * stamped with the wall type when absent), position + per-plane rotation,
   * viewport draw, parented into the wall rectangle or the section cube
   * (ACCESS_DOOR also adds the double wireframe).
   */
  drawRectangleComplete({
    accessory,
    section,
    wireframe,
    wireframeDouble,
    mounting,
    shape,
    viewport_data,
    viewport,
    name,
    int_wall_model,
    isCube,
  }: any): void {
    shape.layers.set(0);
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const cube = section_on_scene.cube;
    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    shape.shape_type = 'accessory';
    const data = {
      accessory,
      section,
      wireframe,
      mounting,
      cube,
      wireframeDouble,
      shape,
      viewport_data,
      int_wall_model,
      viewport,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
    const unit = -2 * section.tunnel.get('airflow_direction_id') + 3;
    const int_wall =
      this.int_wall.horizontal[mounting.mounting_plane_id] ||
      this.int_wall.vertical[mounting.mounting_plane_id];
    if (int_wall && !mounting.mounting_plane) mounting.mounting_plane = int_wall.type;
    const positions = this.calculateRectanglePositions(
      accessory,
      int_wall?.rectangle || cube,
      mounting,
      section.tunnel.get('airflow_direction_id'),
      int_wall,
      name,
      section_on_scene,
      isCube
    );
    shape.position.copy(positions);
    wireframe.position.copy(positions);
    wireframeDouble.position.copy(positions);
    shape.parent_cube = cube;
    shape.name = name;
    shape.casing_mount_id = accessory.casing_mount_id;
    this.getRectanglePositionDependOnMounting({
      mounting_plane_id: mounting.mounting_plane_id,
      mounting_plane: mounting.mounting_plane,
      shape,
      wireframe,
      wireframeDouble,
      int_wall,
      unit,
      airflow_direction_id: section.tunnel.get('airflow_direction_id'),
    });
    if (viewport_data) {
      this.drawViewport(viewport_data, shape.rotation, shape, mounting, section.tunnel.get('airflow_direction_id'));
    }
    if (int_wall?.rectangle) {
      int_wall.rectangle.add(shape);
      int_wall.rectangle.add(wireframe);
      if (name === 'ACCESS_DOOR') {
        int_wall.rectangle.add(wireframeDouble);
      }
    } else {
      cube.add(shape);
      cube.add(wireframe);
      if (name === 'ACCESS_DOOR') {
        cube.add(wireframeDouble);
      }
    }
  }

  /**
   * G13 createViewport (canvas.view3d:2071-2095): outer/inner edge frames +
   * the pane cube tinted with the SECTION fill color; registered on
   * cube.viewports[] so G34 recolors panes with the section.
   */
  createViewport(viewport: any, cube: any): any {
    const size = viewport.size.split('x');
    const width = Number(size[0]) * this.coef;
    const height = Number(size[1]) * this.coef;
    const geometry = new THREE.PlaneBufferGeometry(width, height);
    const geometry_m = new THREE.BoxBufferGeometry(width - 0.3, height - 0.3, 0.005);
    const material = new THREE.MeshBasicMaterial({
      transparent: true,
      side: THREE.DoubleSide,
      color: cube.material.color,
    });
    const edges = new THREE.EdgesGeometry(geometry);
    const geometryDouble = new THREE.PlaneBufferGeometry(width - 0.3, height - 0.3);
    const edgesDouble = new THREE.EdgesGeometry(geometryDouble);
    const wireframeMaterial = new THREE.LineBasicMaterial({ color: wireframeMaterialColorAccessory });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    const wireframeDouble = new THREE.LineSegments(edgesDouble, wireframeMaterial);
    const shape: any = new THREE.Mesh(geometry_m, material);
    // [NEG]#97: legacy `shape.depthFunc = THREE.AlwaysDepth` (:2085) — no-op, dropped.
    shape.material.opacity = 0.4;
    shape.renderOrder = 0;
    if (cube.viewports) cube.viewports.push(shape);
    else cube.viewports = [shape];
    return {
      shape,
      wireframe,
      wireframeDouble,
    };
  }

  /**
   * drawViewport (canvas.view3d:2096-2108). [NEG]#98 RESOLVED at the r110
   * level: `viewport_data.*.rotation = rotation` assigns to Object3D.rotation
   * which is defined NON-WRITABLE in r110 — in the legacy's sloppy-mode AMD
   * module the assignment is a silent NO-OP, and the viewport pieces inherit
   * the door's rotation by being its children. The assignments are therefore
   * dropped here (strict ESM would throw); the observable result is identical.
   */
  drawViewport(viewport_data: any, _rotation: any, shape: any, mounting: any, airflow_direction_id: number): void {
    const positions = this.calculateViewportPositions(shape, viewport_data, mounting, airflow_direction_id);
    viewport_data.shape.position.copy(positions);
    viewport_data.wireframe.position.copy(positions);
    viewport_data.wireframeDouble.position.copy(positions);
    viewport_data.shape.isViewport = true;
    shape.add(viewport_data.wireframe);
    shape.add(viewport_data.wireframeDouble);
    shape.add(viewport_data.shape);
  }

  /**
   * G18 drawRectForVFD (canvas.view3d:2109-2132) — the two dashed face rects
   * on the VFD cube (outer 2/3·w × 1/5·h, inner nested).
   */
  drawRectForVFD({ width, height, shape, mounting_plane_id, unit, accessory }: any): void {
    const default_width = accessory.default_width * this.coef;
    const default_height = accessory.default_height * this.coef;
    const large_width = (2 * default_width) / 3;
    const large_height = default_height / 5;
    const small_width = (2 * large_width) / 3;
    const small_height = large_height / 5;
    const rectangleGeometry = new THREE.PlaneBufferGeometry(large_width, large_height);
    const rectangleGeometrySmall = new THREE.PlaneBufferGeometry(small_width, small_height);
    const edges = new THREE.EdgesGeometry(rectangleGeometry);
    const edgesSmall = new THREE.EdgesGeometry(rectangleGeometrySmall);
    const wireframeMaterial = new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    const wireframeSmall = new THREE.LineSegments(edgesSmall, wireframeMaterial);
    let x = -shape.geometry.parameters.width / 2 - 0.02;
    if (mounting_plane_id == 3 || mounting_plane_id == 5) x = shape.geometry.parameters.width / 2 + 0.02;
    wireframe.rotation.y = Math.PI / 2;
    wireframe.position.x = x * unit;
    wireframe.position.z =
      (unit * (width - default_width)) / 2 * (mounting_plane_id == 3 || mounting_plane_id == 5 ? 1 : -1);
    wireframe.position.y = (height - large_height) / 2 - large_height + (height - default_height) / 10;
    wireframeSmall.position.y = (large_height - small_height) / 2 - small_height;
    shape.add(wireframe);
    wireframe.add(wireframeSmall);
  }

  /**
   * getCubeMesh (canvas.view3d:2133-2169) — hood/through-wall two-material
   * set. Legacy wraps in THREE.MeshFaceMaterial (r110 legacy alias that
   * resolves to a plain material array) — ported as the array directly;
   * face.materialIndex routing is identical.
   */
  getCubeMesh(
    mounting: any,
    color: any,
    airflow_direction_id: number,
    hood: boolean,
    isCube: any,
    casing_mount_id?: any
  ): any {
    let cubeMaterial: any;
    const opening = new THREE.MeshBasicMaterial({
      side: THREE.DoubleSide,
      vertexColors: true as unknown as THREE.Colors,
      opacity: 0,
      transparent: true,
    });
    const transparent = new THREE.MeshBasicMaterial({
      opacity: hood ? 0.8 : 0,
      transparent: true,
      color,
      side: THREE.DoubleSide,
    });
    if (hood) {
      cubeMaterial = [transparent, isCube ? transparent : opening];
    } else if (
      ((mounting.mounting_plane_id === 3 || mounting.mounting_plane_id === 5) &&
        airflow_direction_id + casing_mount_id === 3) ||
      ((mounting.mounting_plane_id === 4 || mounting.mounting_plane_id === 6) &&
        airflow_direction_id - casing_mount_id === 0) ||
      (mounting.mounting_plane_id > 6 && casing_mount_id === 1) ||
      (mounting.mounting_plane_id === 2 && casing_mount_id === 2)
    ) {
      cubeMaterial = [opening, transparent];
    } else {
      cubeMaterial = [transparent, opening];
    }
    return cubeMaterial;
  }

  /**
   * G15 drawAccessoriesCube (canvas.view3d:2170-2227): LIGHT_LINEAR
   * orientation swaps, DAMPER/LOUVER frame-vs-airway diff (crossed test
   * 0.1), legacy Geometry-class BoxGeometry (§42 — r110-only, do NOT
   * modernize), invisible fill for unknown colors.
   */
  drawAccessoriesCube(
    { accessory, section, width, height, depth, name, color = sectionMaterialColor, mounting, int_wall_model }: any,
    callback?: (r: any) => void
  ): void {
    let diff_h = 0;
    let diff_w = 0;
    // COMBO LOUVER — A2.1 (canvas.view3d.js:2181). A combo's own `depth` is
    // null (it sources from master_louver.frame_depth, and master_louver is
    // only set for a single non-combo louver), so the master-louver placeholder
    // box would be BoxGeometry(0, h, w); fall back to the enclosure's depth.
    //
    // ★ R6 — DELIBERATELY SCOPED TO LOUVER (legacy applies it to EVERY cube
    // accessory). `enclosure_depth` exists only on the louver serializer, so
    // legacy's unscoped form hands `undefined * 0.2 = NaN` to any other cube
    // accessory that arrives with a falsy depth (DAMPER without
    // master_damper.frame_depth, LIGHT_LINEAR, DISCONNECT, JBOX, DOG_HOUSE) —
    // pre-combo those kept a finite 0. Scoping keeps the pre-combo behaviour
    // for them and is parity-exact for a PLAIN louver too (its
    // `enclosure_depth` is null → `null * coef` → 0, i.e. the same 0 legacy
    // computes). Reported as inventory R6 → Vicken (legacy-side fix).
    if (!depth && name === 'LOUVER') depth = accessory.enclosure_depth * this.coef;
    if (name === 'LIGHT_LINEAR') {
      if (mounting.mounting_plane_id != 2) {
        if (accessory.orientation_id == 1 || accessory.orientation_id == 2)
          [width, depth, height] = [depth, height, width];
        if (accessory.orientation_id == 3) [height, depth] = [depth, height];
      } else {
        if (accessory.orientation_id == 1) [height, depth] = [depth, height];
        if (accessory.orientation_id == 2) [width, depth, height] = [depth, height, width];
      }
    } else if (name === 'DAMPER' || name === 'LOUVER') {
      const width_diff = accessory.width_calc - accessory.aw_width;
      const height_diff = accessory.height_calc - accessory.aw_height;
      if (Math.abs(width_diff - height_diff) > 0.1) {
        diff_h = this.coef * (accessory.height_calc - accessory.aw_width);
        diff_w = this.coef * (accessory.width_calc - accessory.aw_height);
      } else {
        diff_h = this.coef * (accessory.width_calc - accessory.aw_width);
        diff_w = this.coef * (accessory.height_calc - accessory.aw_height);
      }
      // A2.1 — the legacy NaN guard (canvas.view3d.js:2200-2201 is
      // `height -= (diff_h || 0)`; this port carried the bare subtraction).
      // It matters precisely in the combo case: a combo has no `aw_width`/
      // `aw_height` (per-face instead), so `width_calc - aw_width` can be NaN
      // and would poison the placeholder box's geometry, its casing child and
      // the casing_x/casing_y halves in calculateAccessoriesCubePositions.
      // Also covers a DAMPER with no frame depth.
      height -= diff_h || 0;
      width -= diff_w || 0;
    }
    const cubeGeometry = new THREE.BoxGeometry(depth, height, width);
    let opacity = 1;
    if (!accessoriesColors[name]) opacity = 0;
    else if (name === 'DOG_HOUSE') {
      opacity = 0.6;
    }

    const cubeMaterial = new THREE.MeshBasicMaterial({ color: accessoriesColors[name] || color, opacity });
    if (cubeMaterial.opacity != 1) cubeMaterial.transparent = true;
    const cube: any = new THREE.Mesh(cubeGeometry, cubeMaterial);
    const edges = new THREE.EdgesGeometry(cubeGeometry as any);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.computeLineDistances();
    wireframe.scale.set(1.001, 1.001, 1.001);
    // [NEG]#97: legacy `cube.depthFunc = THREE.AlwaysDepth` (:2216) — no-op, dropped.
    cube.accessory = accessory;
    cube.renderOrder = 0;
    cube.diff_h = diff_h;
    cube.diff_w = diff_w;
    wireframe.renderOrder = 0;
    const response = { accessory, section, width, height, depth, name, shape: cube, wireframe, mounting, int_wall_model };
    if (callback) {
      callback.call(this, response);
    }
  }

  /**
   * getOpeningPosition (canvas.view3d:2228-2242). The legacy's third fallback
   * (`positionObj[mounting.mounting_plane]`) references an undeclared
   * identifier — a ReferenceError if ever reached; unreachable live because
   * openings only exist for HOOD or planes 1-6 (isCube gate). Dropped.
   */
  getOpeningPosition({ shape, name, mounting, width: _width, height: _height, depth, airflow_direction_id, casing_mount_id }: any): any {
    const unit = (-2 * airflow_direction_id + 3) * (casing_mount_id == 1 ? -1 : 1);
    const casing_mount_id_unit = casing_mount_id == 1 ? 1 : -1;
    const y = (shape.geometry.parameters.width + depth) / 2;
    const position: any = {
      HOOD: new THREE.Vector3(-depth / 2, _height / 2, _width / 2),
      1: new THREE.Vector3(-y, 0, 0), // Floor
      2: new THREE.Vector3(-y * casing_mount_id_unit, 0, 0), // Roof
      3: new THREE.Vector3(y * unit, 0, 0), // wall_downstream
      4: new THREE.Vector3(-y * unit, 0, 0), // wall_upstream
      5: new THREE.Vector3(y * unit, 0, 0), // wall_left
      6: new THREE.Vector3(-y * unit, 0, 0), // wall_right
    };
    return position[name] || position[mounting.mounting_plane_id];
  }

  /**
   * drawOpeningForAccessory (canvas.view3d:2243-2268) — the dashed
   * through-wall opening box (gap 0.5 / dash 0.3, scale (1,1,0.8),
   * renderOrder 100) for DAMPER/LOUVER/HOOD mounted through a casing depth.
   */
  drawOpeningForAccessory({ name, width, height, depth, shape, mounting, airflow_direction_id, casing_mount_id }: any): void {
    const rectangleGeometry = new THREE.BoxBufferGeometry(width, height, depth);
    const rectangleMaterial = new THREE.MeshBasicMaterial({ side: THREE.DoubleSide, transparent: true, opacity: 0 });
    const rectangle: any = new THREE.Mesh(rectangleGeometry, rectangleMaterial);
    const edges = new THREE.EdgesGeometry(rectangleGeometry);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0.5,
      dashSize: 0.3,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.computeLineDistances();
    wireframe.scale.set(1, 1, 0.8);
    // [NEG]#97: legacy `rectangle.depthFunc = THREE.AlwaysDepth` (:2259) — no-op, dropped.
    shape.add(wireframe);
    shape.add(rectangle);
    const position = this.getOpeningPosition({
      shape,
      name,
      mounting,
      width,
      height,
      depth,
      airflow_direction_id,
      casing_mount_id,
    });
    rectangle.position.copy(position);
    wireframe.position.copy(position);
    wireframe.rotation.y = Math.PI / 2;
    rectangle.rotation.y = Math.PI / 2;
    wireframe.renderOrder = 100;
  }

  /**
   * drawAccessoriesCubeComplete (canvas.view3d:2701-2764): registry upsert,
   * int-wall parenting, per-plane rotation, then the type extras —
   * DAMPER/LOUVER casing + blades + through-wall opening; VFD face rects;
   * DOG_HOUSE base + insulation casing. (The legacy's DOG_HOUSE
   * `casing_insulation_thick` is an implicit global — scoped here, [NEG]#10
   * family.)
   */
  drawAccessoriesCubeComplete({ accessory, section, width, height, depth, name, shape, wireframe, mounting, int_wall_model }: any): void {
    shape.layers.set(0);
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const cube = section_on_scene.cube;
    shape.shape_type = 'accessory';
    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    // COMBO LOUVER — A2.1 orphan sweep (canvas.view3d.js:2769-2784, verbatim
    // and UNCONDITIONAL). A combo's enclosure and its face-louver children live
    // in `extraShapes`, not under shape/wireframe, so if this accessory
    // previously drew an enclosure and is now redrawing WITHOUT one (is_combo
    // reset, or the enclosure guard bailing on a 0-size box), the shape/
    // wireframe replacement below never touches them and they leak on the
    // canvas. Dispose any leftovers before rebuilding, whichever redraw path
    // ran. (Harmless for VFD: that handler is drawVFDComplete, not this one.)
    (this.accessoryCanvasObjectsObj[accessory.id]?.extraShapes || []).forEach(
      ({ shape: exShape, wireframe: exWireframe }: any) => {
        this.disposeChilds(exWireframe);
        this.disposeChilds(exShape);
        this.disposeDetails(exWireframe);
        this.disposeDetails(exShape);
        exWireframe.parent?.remove(exWireframe);
        exShape.parent?.remove(exShape);
      }
    );
    const data: any = {
      accessory,
      section,
      cube,
      width,
      height,
      depth,
      shape,
      wireframe,
      mounting,
      int_wall_model,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
    const unit = -2 * section.tunnel.get('airflow_direction_id') + 3;
    const int_wall =
      this.int_wall.horizontal[mounting.mounting_plane_id] ||
      this.int_wall.vertical[mounting.mounting_plane_id];
    if (int_wall && !mounting.mounting_plane) mounting.mounting_plane = int_wall.type;
    const positions = this.calculateAccessoriesCubePositions(
      section_on_scene,
      shape,
      accessory,
      name,
      cube,
      mounting,
      section.tunnel.get('airflow_direction_id'),
      int_wall
    );
    shape.position.copy(positions);
    wireframe.position.copy(positions);
    // COMBO LOUVER — the only detection site (canvas.view3d.js:2807).
    const isComboLouver = name == 'LOUVER' && accessory.is_combo;

    if (int_wall) {
      int_wall.rectangle.add(shape);
      int_wall.rectangle.add(wireframe);
    } else {
      cube.add(shape);
      cube.add(wireframe);
    }
    // BRANCH 1 (canvas.view3d.js:2815-2829) — hide the phantom master box. A
    // combo louver has NO single louver on the cabinet wall; its blades live on
    // the enclosure's faces. This placeholder (the LOUVER record's own box,
    // positioned off accessory.depth/casing_mount_id — both meaningless in
    // combo mode) must STAY in the scene graph, because destroyAccessory
    // resolves its parent via `shape.parent`, but has to be hidden explicitly:
    // shape's fill is already invisible (LOUVER has no accessoriesColors entry
    // → opacity 0), while wireframe's LineDashedMaterial has no opacity/
    // transparent, so without this it renders as a fully opaque stray box
    // wherever that meaningless position lands (KA-5250).
    if (isComboLouver) {
      shape.visible = false;
      wireframe.visible = false;
    }
    shape.name = name;
    shape.casing_mount_id = accessory.casing_mount_id;
    shape.parent_cube = cube;
    this.getCubePositionDependOnMounting({
      mounting_plane_id: mounting.mounting_plane_id,
      mounting_plane: mounting.mounting_plane,
      shape,
      wireframe,
      unit,
      int_wall,
      airflow_direction_id: section.tunnel.get('airflow_direction_id'),
    });

    if (name == 'DAMPER' || name == 'LOUVER') {
      depth = accessory.blade_width * this.coef;
      this.drawAccessoriesCubeCasing(shape, accessory);
      // BRANCH 2 (canvas.view3d.js:2838-2844) — skip the master-louver blades
      // for a combo so they don't render as a phantom louver on the tunnel end
      // wall (KA-5249); the real blades are drawn per enclosure face.
      if (!isComboLouver) {
        this.drawAccessoriesCubeBlades(accessory, section, width, height, depth, shape, wireframe, mounting, cube, false);
      }
      const isCube = this.isCube({ mounting, section_on_scene });
      // BRANCH 3 (canvas.view3d.js:2845-2853) — skip the master wall opening
      // for a combo: this shape is hidden and sits at a meaningless position,
      // and three.js skips children of an invisible parent, so the opening
      // would never show. drawComboLouverEnclosure punches the real one against
      // the correctly-positioned enclosure box instead.
      if (isCube && !isComboLouver) {
        this.drawOpeningForAccessory({
          material: wireframe.material,
          name,
          depth: isCube,
          width,
          height,
          shape,
          mounting,
          airflow_direction_id: section.tunnel.get('airflow_direction_id'),
          casing_mount_id: accessory.casing_mount_id,
        });
      }
      // BRANCH 4 (canvas.view3d.js:2854-2863) — overlay the 3-sided sheet-metal
      // enclosure + its 1-3 face louvers, protruding from the wall by
      // enclosure_depth. Mirrors cad/cadahu/combo_louver.py. Positioned
      // independently (like a real ENCLOSURE accessory) rather than offset from
      // the placeholder above, and tracked in data.extraShapes — the same
      // mechanism drawVFDComplete uses for its own enclosure box — so
      // destroyAccessory (and the sweep at the top of this function) removes it
      // whenever the record is redrawn or reset.
      if (isComboLouver) {
        data.extraShapes = this.drawComboLouverEnclosure({
          accessory,
          mounting,
          section_on_scene,
          cube,
          int_wall,
          airflow_direction_id: section.tunnel.get('airflow_direction_id'),
        });
      }
      // dev-sync (canvas.view3d delta @2808): the inline VFD branch moved to
      // the dedicated drawVFDComplete handler below.
    } else if (name === 'DOG_HOUSE') {
      if (accessory.base_height) {
        this.drawDogHouseBase(accessory, shape);
      }
      if (accessory.casing_insulation_thick) {
        const casing_insulation_thick = 2 * this.coef * accessory.casing_insulation_thick;
        shape.diff_w = -2 * casing_insulation_thick;
        shape.diff_h = -casing_insulation_thick;
        this.drawAccessoriesCubeCasing(shape, accessory, casing_insulation_thick);
      }
    }
  }

  /**
   * COMBO LOUVER (KA-5249/KA-5250) — drawComboLouverEnclosure
   * (canvas.view3d.js:3106-3305). A sheet-metal enclosure that surface-mounts
   * on the cabinet wall and carries up to three real louver assemblies
   * (front/left/right). Mirrors cad/cadahu/combo_louver.py.
   *
   * Reached ONLY from drawAccessoriesCubeComplete's branch 4 (:2861-2863) — it
   * is in no dispatch map. Returns exactly ONE {shape, wireframe} pair (the
   * enclosure box + its edge wireframe); everything else is parented under it.
   *
   * The enclosure is positioned/rotated exactly like a standalone ENCLOSURE
   * accessory — the SAME calculateAccessoriesCubePositions +
   * getCubePositionDependOnMounting pipeline a real 'ENCLOSURE' record uses
   * (mirroring how drawVFDComplete places its own housed_in_enclosure box) —
   * rather than being offset from the master-louver placeholder shape. That
   * indirection (the placeholder's own position depends on the master louver's
   * unrelated casing_mount_id/depth) was the source of previous position bugs.
   * casing_mount is forced to Surface (1) since a combo enclosure is always
   * 100 % surface-mounted regardless of what the master louver record carries.
   *
   * Passing the name `'ENCLOSURE'` into calculateAccessoriesCubePositions
   * matters twice: casing_x/casing_y stay zero (only LOUVER/DAMPER apply
   * shape.diff_h/diff_w, and encBox has neither → passing 'LOUVER' would inject
   * NaN), and 'ENCLOSURE' is in the insulation-zeroing list.
   *
   * Every mesh returned is a top-level SIBLING of the placeholder shape (added
   * to `cube`/`int_wall.rectangle`, not nested under it) and handed back as
   * extraShapes — the mechanism drawVFDComplete already uses — so
   * destroyAccessory's extraShapes cleanup removes every mesh whenever the
   * accessory is redrawn or its combo config is reset/nulled.
   */
  drawComboLouverEnclosure({
    accessory,
    mounting,
    section_on_scene,
    cube,
    int_wall,
    airflow_direction_id,
  }: any): Array<{ shape: any; wireframe: any }> {
    // Number(...) is deliberate (:3107-3110): a reset PATCH can send these as
    // the string "0.00" rather than null/0 — a non-empty string is truthy in
    // JS, so a plain falsy check let a degenerate 0-size enclosure through.
    if (
      !Number(accessory.enclosure_width) ||
      !Number(accessory.enclosure_height) ||
      !Number(accessory.enclosure_depth)
    )
      return [];
    const coef = this.coef;
    const encD = accessory.enclosure_depth * coef; // local X (protrusion)
    const encH = accessory.enclosure_height * coef; // local Y
    const encW = accessory.enclosure_width * coef; // local Z (along cabinet)
    const pid = mounting.mounting_plane_id;
    const unit = -2 * airflow_direction_id + 3;
    const planeSign = pid === 3 || pid === 5 ? 1 : -1;
    const outward = planeSign * unit >= 0 ? 1 : -1;
    const extraShapes: Array<{ shape: any; wireframe: any }> = [];

    // --- Enclosure shell ---------------------------------------------------
    const encGeometry = new THREE.BoxGeometry(encD, encH, encW);
    const encBox: any = new THREE.Mesh(
      encGeometry,
      new THREE.MeshBasicMaterial({
        color: accessoriesColors['ENCLOSURE'],
        transparent: true,
        opacity: 0,
      })
    );
    // [NEG]#97: legacy `encBox.depthFunc = THREE.AlwaysDepth` (:3124) — Mesh has
    // no depthFunc (it is a Material field), so the assignment is a no-op;
    // dropped like every other occurrence in this file.
    encBox.renderOrder = 0;
    const encWireframe: any = new THREE.LineSegments(
      new THREE.EdgesGeometry(encGeometry as any),
      new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory, gapSize: 0 })
    );
    encWireframe.computeLineDistances();
    encWireframe.scale.set(1.001, 1.001, 1.001);
    encWireframe.renderOrder = 0;
    // casing_mount_id forces the Surface push branch in
    // calculateAccessoriesCubePositions; that branch reads accessory.depth (NOT
    // shape.geometry.parameters) for the push distance, so depth must be
    // overridden to the enclosure's own depth too — the combo record's own
    // accessory.depth sources from master_louver.frame_depth, which is null in
    // combo mode (master_louver is only set for a single, non-combo louver).
    const encAccessory = { ...accessory, casing_mount_id: 1, depth: accessory.enclosure_depth };
    const encPosition = this.calculateAccessoriesCubePositions(
      section_on_scene,
      encBox,
      encAccessory,
      'ENCLOSURE',
      cube,
      mounting,
      airflow_direction_id,
      int_wall
    );
    encBox.position.copy(encPosition);
    encWireframe.position.copy(encPosition);
    encBox.name = 'combo_enclosure_box';
    encBox.shape_type = 'accessory';
    encBox.accessory = accessory;
    encBox.casing_mount_id = 1;
    encBox.parent_cube = cube;
    encWireframe.shape_type = 'accessory';
    encWireframe.accessory = accessory;
    this.getCubePositionDependOnMounting({
      mounting_plane_id: pid,
      mounting_plane: mounting.mounting_plane,
      shape: encBox,
      wireframe: encWireframe,
      unit,
      int_wall,
      airflow_direction_id,
    });
    if (int_wall) {
      int_wall.rectangle.add(encBox);
      int_wall.rectangle.add(encWireframe);
    } else {
      cube.add(encBox);
      cube.add(encWireframe);
    }
    extraShapes.push({ shape: encBox, wireframe: encWireframe });

    // Invisible solid hit-box (a mesh, not just wireframe lines) spanning the
    // enclosure so a click anywhere on it resolves to this accessory and opens
    // the combo louver dialog (:3156-3163). getAccessoryShapes folds
    // extraShapes[].shape into the raycast set; every combo mesh carries
    // shape_type='accessory' + .accessory so all resolve to the right id.
    const hit: any = new THREE.Mesh(
      encGeometry.clone(),
      new THREE.MeshBasicMaterial({ transparent: true, opacity: 0, depthWrite: false })
    );
    hit.name = 'combo_enclosure_hit';
    hit.shape_type = 'accessory';
    hit.accessory = accessory;
    encBox.add(hit);

    // --- Opening through the section wall ----------------------------------
    // The enclosure is surface-mounted flush against the cabinet wall and there
    // can never be solid casing between the tunnel interior and the enclosure's
    // plenum — air has to pass through. Mirrors the drawOpeningForAccessory a
    // plain LOUVER/DAMPER uses, sized to the enclosure's own footprint
    // (encW x encH, not the combo record's meaningless master-louver
    // width/height) and anchored to encBox — which, unlike the hidden
    // placeholder, is correctly positioned — so the opening tracks whatever
    // wall/mounting the enclosure is actually on. isCube returns the section's
    // casing/insulation thickness at this plane, i.e. the depth of wall the hole
    // must punch through, 0 if the section has no casing (KA-5250).
    const openingCasingDepth = this.isCube({ mounting, section_on_scene });
    if (openingCasingDepth) {
      this.drawOpeningForAccessory({
        material: encWireframe.material,
        name: 'LOUVER',
        depth: openingCasingDepth,
        width: encW,
        height: encH,
        shape: encBox,
        mounting,
        airflow_direction_id,
        casing_mount_id: 1,
      });
    }

    // --- Top / bottom walls ------------------------------------------------
    // Unlike front/left/right (louver or open), the enclosure's floor and top
    // cap are ALWAYS solid sheet metal ("a 3-sided sheet-metal enclosure … plus
    // a floor + top cap"; mirrors the Perimeter pad in
    // cad/cadahu/combo_louver.py's enclosure_shell()). encBox itself is fully
    // invisible (opacity 0, kept only for the wireframe/hit-box/positioning), so
    // without these the floor and top cap don't read as solid in the preview.
    // Translucent panels spanning the enclosure's depth (local X) and width
    // (local Z), added as encBox children so they inherit its placement and are
    // swept up by destroyAccessory with it.
    const wallGeometry = new THREE.PlaneGeometry(encD, encW);
    const wallMaterial = new THREE.MeshBasicMaterial({
      color: accessoriesColors['ENCLOSURE'],
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.4,
    });
    [1, -1].forEach((sign) => {
      const wall: any = new THREE.Mesh(wallGeometry.clone(), wallMaterial.clone());
      wall.rotation.x = -Math.PI / 2;
      wall.position.set(0, (sign * encH) / 2, 0);
      wall.name = sign > 0 ? 'combo_enclosure_top_wall' : 'combo_enclosure_bottom_wall';
      wall.shape_type = 'accessory';
      wall.accessory = accessory;
      const wallEdges = new THREE.EdgesGeometry(wallGeometry as any);
      const wallWireframe: any = new THREE.LineSegments(
        wallEdges,
        new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory, gapSize: 0 })
      );
      wallWireframe.computeLineDistances();
      wallWireframe.rotation.copy(wall.rotation);
      wallWireframe.position.copy(wall.position);
      wallWireframe.shape_type = 'accessory';
      wallWireframe.accessory = accessory;
      encBox.add(wall);
      encBox.add(wallWireframe);
    });

    // --- Face louvers ------------------------------------------------------
    // Real louver assemblies — a frame box with the blade profile recessed
    // inside it via drawAccessoriesCubeBlades, the SAME helper a regular
    // (non-combo) louver uses — rather than a flat decorative panel. Each is a
    // child of the (now correctly positioned) encBox, so it inherits the
    // enclosure's placement and is swept up by destroyAccessory's extraShapes
    // cleanup along with it. Blade pitch uses the CAD's fixed 3 in convention
    // (cadahu/combo_louver.py's _build_louver / :321): combo faces share the
    // master louver's blade profile but have no per-face blade_width of their
    // own to read (master_louver is only set for a single, non-combo louver).
    const pitch = 3 * coef;
    const loverDepth = Math.min(encD, 4 * coef); // sic — legacy spelling
    let comboFaces = accessory.combo_faces;
    // Inventory DEAD-3: the canvas always receives the serializer's ARRAY (the
    // only stringification is accessory_view.js:2064's canvasData snapshot,
    // which never reaches here). Kept verbatim as the same cheap crash guard.
    if (typeof comboFaces === 'string') {
      try {
        comboFaces = JSON.parse(comboFaces);
      } catch {
        comboFaces = null;
      }
    }
    (comboFaces || []).forEach((f: any) => {
      // ★ The face gate (:3244-3245) filters on `active`, NOT `checked`.
      // `active` is derived server-side as `checked and aw_width and aw_height`,
      // so auto-size removing the front louver (sides over-provide the area)
      // leaves it checked:true / active:false — and it must NOT draw.
      if (
        !f.active ||
        !Number(f.aw_width) ||
        !Number(f.aw_height) ||
        !['front', 'left', 'right'].includes(f.face)
      )
        return;
      const aw = f.aw_width * coef;
      const ah = f.aw_height * coef;
      // Face louver depth = MasterAHULouver.frame_depth: the per-face value,
      // falling back to the combo's shared frame_depth, then the default
      // (KA-5253) so the face louver is modelled at its real depth.
      // (Inventory DEAD-2: `f.frame_depth` is never present — the face
      // serializer has no such field — so the first term is always
      // Number(undefined)*coef = NaN → falsy → falls through. Ported verbatim.)
      const d =
        Number(f.frame_depth) * coef ||
        Number(f.depth) * coef ||
        Number(accessory.frame_depth) * coef ||
        loverDepth;
      // force Surface if missing, like the enclosure itself
      const casing_mount_id = accessory.casing_mount_id || 1;
      const louverGeometry = new THREE.BoxGeometry(d, ah, aw);
      const louverBox: any = new THREE.Mesh(
        louverGeometry,
        new THREE.MeshBasicMaterial({ color: 0x8aa0c8, transparent: true, opacity: 0.5 })
      );
      // [NEG]#97: legacy `louverBox.depthFunc = THREE.AlwaysDepth` (:3255) — a
      // Mesh has no depthFunc; no-op, dropped (see encBox above).
      louverBox.renderOrder = 998;
      const louverWireframe: any = new THREE.LineSegments(
        new THREE.EdgesGeometry(louverGeometry as any),
        new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory, gapSize: 0 })
      );
      louverWireframe.computeLineDistances();
      let faceMounting = mounting;
      if (f.face === 'front') {
        // Flush on the enclosure's outward face, protruding further by its own
        // depth. `outward` is driven by the TUNNEL's airflow_direction_id (not
        // the accessory's air_direction_id) XNOR'd with planeSign, and it drives
        // the front face ONLY.
        louverBox.position.set(outward * (encD / 2 + d / 2), 0, 0);
        if (casing_mount_id == 2) {
          louverBox.position.x -= d * outward;
        }
      } else {
        // Rotated 90° relative to the enclosure (a purely local,
        // enclosure-relative turn — independent of the outer wall's own
        // mounting_plane_id) so the blade/depth axis points sideways, flush on
        // the enclosure's side face and protruding past it. Treated as if
        // mounted on that side wall (pid 5/6) so the shared blade-tilt logic in
        // drawAccessoriesCubeBlades picks a sensible angle.
        //
        // ★ R2 — THE LEFT/RIGHT MIRROR (:3272-3278), ported verbatim.
        // Truth table (pid ≡ mounting.mounting_plane_id):
        //   pid 3 downstream / 5 left : keep on Air In (1), swap otherwise
        //   pid 4 upstream   / 6 right: swap on Air In (1), keep otherwise
        //   pid 1 floor / 2 roof / >6 internal wall: ALWAYS swap
        // i.e. for pid ∈ {3,4,5,6}: keep ⟺ XNOR(air_direction_id == 1,
        // planeSign == 1); the {1,2,>6} always-swap is an undocumented
        // asymmetry, and combo IS reachable there (louver_viewset.py:65-69
        // recentres combos on floor/roof) even though the dialog offers only
        // the four wall planes.
        //
        // ⚠ PARKED FOR THE OWNER (inventory R2): the canvas and the backend
        // DISAGREE. models_accessory_louver.py:1533 uses the unconditional
        // `swap = self.air_direction_id != 1` — no planeSign term — so the two
        // pick different faces for pid ∈ {4,6} under Air In and pid ∈ {3,5}
        // under Air out, and the model property feeds CAD/3-D serialization
        // (cad_serializers.py:1300-1336). This is a frontend, parity-only
        // migration: React's canvas must draw what LEGACY'S CANVAS draws, so
        // this matches canvas.view3d.js:3273 exactly and deliberately does NOT
        // "correct" itself toward the backend. Whoever is right is Vicken's
        // call; do not change either side here.
        let face: string;
        if (accessory.air_direction_id == 1) {
          face = pid == 3 || pid == 5 ? f.face : f.face === 'left' ? 'right' : 'left';
        } else {
          face = pid == 4 || pid == 6 ? f.face : f.face === 'left' ? 'right' : 'left';
        }
        const sideSign = face === 'left' ? 1 : -1;
        louverBox.rotation.y = (-sideSign * Math.PI) / 2;
        louverWireframe.rotation.y = (-sideSign * Math.PI) / 2;
        louverBox.position.set(0, 0, sideSign * (encW / 2 + d / 2));
        // ⚠ The legacy comment at :3282-3286 is STALE: it describes a
        // planeSign-conditional 3:4 vs 5:6 pid pair (added by aa12df40ad) but
        // the code is the FIXED 5:6 version (that change was reverted by
        // 49790ee374). Trust the code — the fixed 5:6 pair is what ships and is
        // what this port reproduces. By the stale comment's own reasoning the
        // side-face blade TILT would be wrong for a base wall of pid 3 or 5;
        // that is inventory R5, parked for the owner, NOT fixed here.
        faceMounting = { ...mounting, mounting_plane_id: face === 'left' ? 5 : 6 };
        if (casing_mount_id == 2) {
          louverBox.position.z -= d * sideSign;
        }
      }
      louverWireframe.position.copy(louverBox.position);
      louverBox.name = 'combo_face_louver';
      louverBox.shape_type = 'accessory';
      louverBox.accessory = accessory;
      louverWireframe.shape_type = 'accessory';
      louverWireframe.accessory = accessory;
      encBox.add(louverBox);
      encBox.add(louverWireframe);
      // Legacy MUTATES the accessory (:3299-3300) to override the master
      // louver's blade_direction_id for the combo face; drawAccessoriesCubeBlades
      // reads it back off `accessory`. Kept verbatim, mutation included.
      const blade_orientation_id = JSON.parse(accessory.louver_params)?.blade_orientation_id;
      accessory.blade_direction_id = blade_orientation_id === 2 ? 2 : 1;
      this.drawAccessoriesCubeBlades(
        accessory,
        section_on_scene.section,
        aw,
        ah,
        pitch,
        louverBox,
        louverWireframe,
        faceMounting,
        cube,
        false
      );
    });

    return extraShapes;
  }

  /**
   * updateVFD (dev-sync canvas.view3d delta @2823) — destroy + full redraw:
   * the bank/enclosure extraShapes can change COUNT, so an in-place position
   * update is not enough.
   */
  updateVFD({ accessory, section, width, height, depth, mounting, name, int_wall_model }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawAccessoriesCube(
      { accessory, section, width, height, depth, name, mounting, int_wall_model },
      this.drawVFDComplete.bind(this)
    );
  }

  /**
   * drawVFDComplete (dev-sync canvas.view3d delta @2825-2925) — the VFD bank +
   * enclosure completer. `shape`/`wireframe` are the already-built box for the
   * FIRST VFD (created by drawAccessoriesCube, reused so the legacy single-VFD
   * case is pixel-identical). Additional bank members and the enclosure frame
   * are built here and tracked in `extraShapes`, mirroring cad/cadahu/vfd.py's
   * per-VFD placement plus a separate enclosure box (VFD._create_bank).
   */
  drawVFDComplete({ accessory, section, width, height, depth, name, shape, wireframe, mounting, int_wall_model }: any): void {
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const cube = section_on_scene.cube;
    const airflow_direction_id = section.tunnel.get('airflow_direction_id');
    const unit = -2 * airflow_direction_id + 3;
    const int_wall =
      this.int_wall.horizontal[mounting.mounting_plane_id] ||
      this.int_wall.vertical[mounting.mounting_plane_id];
    if (int_wall && !mounting.mounting_plane) mounting.mounting_plane = int_wall.type;

    const bankPositions =
      accessory.vfd_bank_positions && accessory.vfd_bank_positions.length
        ? accessory.vfd_bank_positions
        : null;
    const width_calc = accessory.width_calc ?? accessory.width;
    const height_calc = accessory.height_calc ?? accessory.height;
    let positions: Array<{ x: number; y: number }> = bankPositions || [
      { x: mounting.x, y: mounting.y },
    ];
    // Single VFD with an oversized enclosure (no bank): center the VFD inside
    // it, matching the enc_x/enc_z centering in cad/cadahu/vfd.py's vfd() for
    // the single-enclosed case.
    if (!bankPositions && accessory.housed_in_enclosure && accessory.enc_width && accessory.enc_height) {
      const w = accessory.vfd_qty === 1 ? accessory.width : width_calc;
      const h = accessory.vfd_qty === 1 ? accessory.height : height_calc;
      positions = [
        {
          x: mounting.x + (accessory.enc_width - w) / 2,
          y: mounting.y + (accessory.enc_height - h) / 2,
        },
      ];
    }
    // mounting.y already has the multi-tunnel floor-construction offset baked
    // in (drawAccessory :1328-1331 / here :5025-5027, `prev_y` memo);
    // vfd_bank_positions are RAW section-space coordinates from the API, so
    // re-apply that same offset.
    const floorOffset =
      bankPositions && mounting.prev_y !== undefined ? mounting.y - mounting.prev_y : 0;
    // aw_width is the section's usable width along the mounting wall;
    // reconstructing it once yields a correct distance_to_right_wall for every
    // individual VFD/enclosure box below.
    const aw_width =
      (+accessory.distance_to_right_wall || 0) + (+mounting.x || 0) + (+width_calc || 0);

    const placeBox = (
      boxShape: any,
      boxWireframe: any,
      mountingOverride: any,
      accessoryOverride: any,
      addControlsPanel: boolean
    ) => {
      const position = this.calculateAccessoriesCubePositions(
        section_on_scene,
        boxShape,
        accessoryOverride,
        name,
        cube,
        mountingOverride,
        airflow_direction_id,
        int_wall
      );
      boxShape.position.copy(position);
      boxWireframe.position.copy(position);
      boxShape.layers.set(0);
      boxShape.shape_type = 'accessory';
      boxShape.name = name;
      boxShape.accessory = accessory;
      boxShape.casing_mount_id = accessory.casing_mount_id;
      boxShape.parent_cube = cube;
      this.getCubePositionDependOnMounting({
        mounting_plane_id: mounting.mounting_plane_id,
        mounting_plane: mounting.mounting_plane,
        shape: boxShape,
        wireframe: boxWireframe,
        unit,
        int_wall,
        airflow_direction_id,
      });
      if (int_wall) {
        int_wall.rectangle.add(boxShape);
        int_wall.rectangle.add(boxWireframe);
      } else {
        cube.add(boxShape);
        cube.add(boxWireframe);
      }
      if (addControlsPanel)
        this.drawRectForVFD({
          width,
          height,
          shape: boxShape,
          mounting_plane_id: mounting.mounting_plane_id,
          unit,
          accessory,
        });
    };

    const extraShapes: Array<{ shape: any; wireframe: any }> = [];
    positions.forEach((pos: { x: number; y: number }, i: number) => {
      const mountingOverride = { ...mounting, x: pos.x, y: pos.y + floorOffset };
      const accessoryOverride = {
        ...accessory,
        distance_to_right_wall: aw_width - (+pos.x || 0) - (+accessory.width || 0),
      };
      if (i === 0) {
        placeBox(shape, wireframe, mountingOverride, accessoryOverride, true);
        return;
      }
      const boxGeometry = new THREE.BoxGeometry(depth, height, width);
      const box: any = new THREE.Mesh(
        boxGeometry,
        new THREE.MeshBasicMaterial({ color: accessoriesColors[name] || sectionMaterialColor })
      );
      // [NEG]#97: legacy `box.depthFunc = THREE.AlwaysDepth` — no-op, dropped.
      box.renderOrder = 0;
      const boxWireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(boxGeometry as any),
        new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory, gapSize: 0 })
      );
      boxWireframe.computeLineDistances();
      boxWireframe.scale.set(1.001, 1.001, 1.001);
      boxWireframe.renderOrder = 0;
      placeBox(box, boxWireframe, mountingOverride, accessoryOverride, true);
      extraShapes.push({ shape: box, wireframe: boxWireframe });
    });

    if (accessory.housed_in_enclosure && accessory.enc_width && accessory.enc_height) {
      const encDepth = depth + 2 * this.coef;
      const encGeometry = new THREE.BoxGeometry(
        encDepth,
        accessory.enc_height * this.coef,
        accessory.enc_width * this.coef
      );
      const encBox: any = new THREE.Mesh(
        encGeometry,
        new THREE.MeshBasicMaterial({
          color: accessoriesColors[name] || sectionMaterialColor,
          transparent: true,
          opacity: 0,
        })
      );
      // [NEG]#97: legacy `encBox.depthFunc = THREE.AlwaysDepth` — no-op, dropped.
      encBox.renderOrder = 0;
      const encWireframe = new THREE.LineSegments(
        new THREE.EdgesGeometry(encGeometry as any),
        new THREE.LineDashedMaterial({ color: wireframeMaterialColorAccessory, gapSize: 0 })
      );
      encWireframe.computeLineDistances();
      encWireframe.scale.set(1.001, 1.001, 1.001);
      encWireframe.renderOrder = 0;
      // Surface-mounted enclosures protrude an extra 2in beyond a bare VFD
      // box, matching the -2.0*metric_k local shift applied to surface-mounted
      // enclosure boxes in cad/cadahu/vfd.py.
      const encDistanceToRightWall =
        (+accessory.distance_to_right_wall || 0) + width_calc - accessory.enc_width;
      const encAccessoryOverride = {
        ...accessory,
        distance_to_right_wall: encDistanceToRightWall,
        depth: +accessory.depth + 2,
      };
      placeBox(encBox, encWireframe, mounting, encAccessoryOverride, false);
      extraShapes.push({ shape: encBox, wireframe: encWireframe });
    }

    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    const data = {
      accessory,
      section,
      cube,
      width,
      height,
      depth,
      shape,
      wireframe,
      mounting,
      int_wall_model,
      extraShapes,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
  }

  /**
   * G16 drawAccessoriesCubeBlades (canvas.view3d:2798-2813): blade count from
   * the airway extent / blade_width; opposed dampers
   * (damper_params.blade_orientation_id == 2) alternate twist sign per odd
   * index.
   */
  drawAccessoriesCubeBlades(
    accessory: any,
    section: any,
    width: number,
    height: number,
    depth: number,
    shape: any,
    wireframe: any,
    mounting: any,
    cube: any,
    update: boolean
  ): void {
    const on_top_or_bottom =
      (mounting.mounting_plane_id == 1 || mounting.mounting_plane_id == 2) && accessory.blade_direction_id === 1;
    const blades_count =
      (accessory.blade_direction_id !== 2 && !on_top_or_bottom ? height : width) / depth - 1;
    let blade_orientation_id = null;
    if (accessory.damper_params) {
      blade_orientation_id = JSON.parse(accessory.damper_params)?.blade_orientation_id;
    }
    for (let i = 0; i < blades_count; i++) {
      const bladeGeometry = new THREE.PlaneGeometry(
        accessory.blade_direction_id === 2 || on_top_or_bottom ? height : width,
        depth
      );
      const bladeMaterial = new THREE.MeshBasicMaterial({ color: 0x828282, side: THREE.DoubleSide });
      const blade = new THREE.Mesh(bladeGeometry, bladeMaterial);
      const bladeEdges = new THREE.EdgesGeometry(bladeGeometry as any);
      const bladeWireframeMaterial = new THREE.LineBasicMaterial({ color: 0x828282 });
      const bladeWireframe = new THREE.LineSegments(bladeEdges, bladeWireframeMaterial);
      this.drawAccessoriesCubeBladesComplete(
        blades_count,
        accessory,
        section,
        width,
        height,
        depth,
        shape,
        wireframe,
        bladeWireframe,
        mounting,
        blade,
        i,
        cube,
        update,
        on_top_or_bottom,
        blade_orientation_id == 2 && i % 2 > 0 ? -1 : 1
      );
    }
  }

  /**
   * drawAccessoriesCubeBladesComplete (canvas.view3d:2815-2848): the ±135°
   * (LOUVER direction-2: 45°) twist via direct Geometry vertex mutation +
   * the wireframe position-array slots 5/14/17/23 (§42 r110 Geometry API).
   */
  drawAccessoriesCubeBladesComplete(
    blades_count: number,
    accessory: any,
    section: any,
    _width: number,
    _height: number,
    depth: number,
    shape: any,
    _wireframe: any,
    bladeWireframe: any,
    mounting: any,
    blade: any,
    i: number,
    _cube: any,
    _update: boolean,
    on_top_or_bottom: boolean,
    isOpposed: number
  ): void {
    const airflow_direction_id = section.tunnel.get('airflow_direction_id');
    let degree =
      (accessory.blade_direction_id === 3 || accessory.blade_direction_id === 1) &&
      ((airflow_direction_id === 2 && mounting.mounting_plane_id === 5) ||
        (airflow_direction_id === 1 &&
          (mounting.mounting_plane_id === 6 || mounting.mounting_plane_id === 4 || mounting.mounting_plane_id === 3)))
        ? -135
        : 135;
    if (accessory.name === 'LOUVER') {
      degree =
        accessory.blade_direction_id === 2
          ? 45
          : (accessory.blade_direction_id === 3 || accessory.blade_direction_id === 1) &&
            ((airflow_direction_id === 2 &&
              (mounting.mounting_plane_id === 6 || mounting.mounting_plane_id === 4)) ||
              (airflow_direction_id === 1 &&
                (mounting.mounting_plane_id === 3 || mounting.mounting_plane_id === 5)))
          ? -135
          : 135;
    }
    const angle = THREE.Math.degToRad(isOpposed * degree);
    const new_height = Math.tan(angle) * (depth / 2);
    const bladePosition = this.calculateAccessoriesCubeBladePositions(
      blades_count,
      blade,
      shape,
      mounting,
      i,
      accessory.blade_direction_id,
      new_height,
      on_top_or_bottom
    );
    blade.rotation.y = Math.PI / 2;
    bladeWireframe.rotation.y = Math.PI / 2;
    if (accessory.blade_direction_id === 2 || on_top_or_bottom) {
      blade.rotation.z = Math.PI / 2;
      bladeWireframe.rotation.z = Math.PI / 2;
    }
    (blade.geometry as any).vertices[2].z = new_height;
    (blade.geometry as any).vertices[3].z = new_height;
    bladeWireframe.geometry.attributes.position.array[5] = new_height;
    bladeWireframe.geometry.attributes.position.array[14] = new_height;
    bladeWireframe.geometry.attributes.position.array[17] = new_height;
    bladeWireframe.geometry.attributes.position.array[23] = new_height;
    blade.position.copy(bladePosition);
    bladeWireframe.position.copy(bladePosition);
    (blade as any).shape_type = 'accessory';
    (blade as any).accessory = accessory;
    shape.add(blade);
    shape.add(bladeWireframe);
  }

  /**
   * G17 drawAccessoriesCubeCasing (canvas.view3d:2850-2868). [NEG]#100:
   * opacity 0.3 without `transparent` → renders opaque black; replicate.
   */
  drawAccessoriesCubeCasing(shape: any, accessory: any, minus_from_depth = 0): void {
    const width = shape.geometry.parameters.depth + shape.diff_w / 2;
    const height = shape.geometry.parameters.height + shape.diff_h / 2;
    const depth = shape.geometry.parameters.width - minus_from_depth;
    const cubeGeometry = new THREE.BoxGeometry(depth, height, width);
    const edges = new THREE.EdgesGeometry(cubeGeometry as any);
    const wireframeMaterial = new THREE.LineBasicMaterial({
      color: wireframeMaterialColorAccessory,
      opacity: 0.3,
    });
    const wireframe: any = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.computeLineDistances();
    shape.add(wireframe);
    wireframe.name = 'cube_casing';
    wireframe.shape_type = 'accessory';
    wireframe.accessory = accessory;
    if (accessory.name === 'DOG_HOUSE') {
      wireframe.position.y = shape.diff_h / 4;
    }
  }

  /**
   * G14 drawCylinder (canvas.view3d:1962-1983) — LIGHT / ADP_GAGE: CB(w/2,
   * w/2, depth, 10 segs), opaque fill, dashed-capable black wireframe.
   */
  drawCylinder(
    { accessory, section, width, depth, name, color = sectionMaterialColor, mounting }: any,
    callback?: (r: any) => void
  ): void {
    const cylinderGeometry = new THREE.CylinderBufferGeometry(width / 2, width / 2, depth, 10);
    const cylinderMaterial = new THREE.MeshBasicMaterial({ color: accessoriesColors[name] || color, opacity: 1 });
    const cylinder: any = new THREE.Mesh(cylinderGeometry, cylinderMaterial);
    const edges = new THREE.EdgesGeometry(cylinderGeometry);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.computeLineDistances();
    wireframe.scale.set(1.001, 1.001, 1.001);
    wireframe.renderOrder = 1;
    cylinder.name = name;
    cylinder.accessory = accessory;
    if (callback) {
      callback.call(this, { accessory, section, wireframe, mounting, shape: cylinder });
    }
  }

  /** drawCylinderComplete (canvas.view3d:2671-2700). */
  drawCylinderComplete({ accessory, section, wireframe, mounting, shape, name }: any): void {
    shape.layers.set(0);
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const cube = section_on_scene.cube;
    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    shape.shape_type = 'accessory';
    const data = {
      accessory,
      section,
      wireframe,
      mounting,
      cube,
      shape,
      name,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
    const positions = this.calculateCylinderPositions(
      section_on_scene,
      accessory,
      shape,
      cube,
      mounting,
      section.tunnel.get('airflow_direction_id')
    );
    shape.position.copy(positions);
    wireframe.position.copy(positions);
    shape.casing_mount_id = accessory.casing_mount_id;
    shape.parent_cube = cube;
    this.getCylinderPositionDependOnMounting({
      shape,
      wireframe,
      mounting,
      airflow_direction_id: section.tunnel.get('airflow_direction_id'),
    });
    cube.add(shape);
    cube.add(wireframe);
  }

  // --- G20 hood trig helpers (canvas.view3d:2269-2291). [NEG]#99: π
  // approximated as 3.14 in all three — keep, the vertex positions are
  // observable. ---
  getEndPoint(startX: number, startY: number, angleInDegrees: any, distance: number): THREE.Vector2 {
    const angleInRadians = (angleInDegrees * 3.14) / 180;
    const endX = startX + distance * Math.sin(angleInRadians);
    const endY =
      angleInDegrees == 90
        ? startY
        : angleInDegrees < 90
        ? startY - distance * Math.cos(angleInRadians)
        : startY + distance * Math.cos(angleInRadians);
    return new THREE.Vector2(endX, endY);
  }

  getEndPointUp(startX: number, startY: number, angleInDegrees: any, distance: number): THREE.Vector2 {
    const angleInRadians = (angleInDegrees * 3.14) / 180;
    const endX = startX + distance * Math.sin(angleInRadians);
    const endY = angleInDegrees == 90 ? startY : startY + distance * Math.cos(angleInRadians);
    return new THREE.Vector2(endX, endY);
  }

  getEndPointMiddle(startX: number, startY: number, angleInDegrees: any, distance: number): THREE.Vector2 {
    const angleInRadians = (angleInDegrees * 3.14) / 180;
    const endX = angleInDegrees == 90 ? startX : startX + distance * Math.sin(angleInRadians);
    const endY =
      angleInDegrees == 90
        ? distance
        : angleInDegrees < 90
        ? startY + distance * Math.cos(angleInRadians)
        : startY - distance * Math.cos(angleInRadians);
    return new THREE.Vector2(endX, endY);
  }

  /**
   * G20 drawAccessoriesHood (canvas.view3d:2292-2344): style → vertex set
   * (2-sided straight/curved, 3-sided, 4-sided), PrismGeometry extrusion,
   * face paint loop skipping the mouth (last-4/-3 faces), two-material set
   * via getCubeMesh, through-wall opening when isCube.
   */
  drawAccessoriesHood(
    { accessory, section, width, height, depth, name, color = sectionMaterialColor, mounting }: any,
    callback?: (r: any) => void
  ): void {
    const PrismGeometry = this.createPrismGeometry();
    const angle = 180 - +accessory.angle_2;
    const A = new THREE.Vector2(0, 0);
    const B = new THREE.Vector2(0, height);
    const C = this.getEndPoint(0, height, +accessory.angle_1, accessory.face_dim_2 * this.coef);
    const C_D = this.getEndPoint(0, height, +accessory.angle_1, accessory.face_dim_3 * this.coef);
    const C_E = this.getEndPoint(0, height, +accessory.angle_1, accessory.face_dim_4 * this.coef);
    const D = this.getEndPointUp(0, 0, +accessory.angle_2, accessory.face_dim_1 * this.coef);
    const E = this.getEndPointMiddle(D.x, D.y, +accessory.angle_3 - angle, accessory.face_dim_2 * this.coef);

    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    const isCube = this.isCube({ mounting, section_on_scene });
    const hoodMaterial = this.getCubeMesh(mounting, color, section.tunnel.get('airflow_direction_id'), true, isCube);
    const hood_depth = width;
    const hood_sides: Record<string, THREE.Vector2[]> = {
      hood_2_sided_straight: [A, B, C],
      hood_2_sided_curved: [A, B, C],
      hood_3_sided: [A, B, C_D, D],
      hood_4_sided: [A, B, C_E, E, D],
    };
    const hoodVerticesArray = hood_sides[accessory.style];
    const hoodGeometry: any = new (PrismGeometry as any)(
      hoodVerticesArray,
      hood_depth,
      accessory.style === 'hood_2_sided_curved'
    );
    for (let i = 0; i < hoodGeometry.faces.length; i++) {
      if (i != hoodGeometry.faces.length - 4 && i != hoodGeometry.faces.length - 3) {
        hoodGeometry.faces[i].color.setHex(color);
        hoodGeometry.faces[i].materialIndex = 0;
      }
    }
    const hood: any = new THREE.Mesh(hoodGeometry, hoodMaterial);
    const edges = new THREE.EdgesGeometry(hoodGeometry);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.scale.set(1.001, 1.001, 1.001);
    wireframe.computeLineDistances();
    wireframe.renderOrder = 1;
    hood.accessory = accessory;
    if (isCube) {
      this.drawOpeningForAccessory({
        material: wireframe.material,
        name,
        depth: isCube,
        width,
        height,
        shape: hood,
        mounting,
        airflow_direction_id: section.tunnel.get('airflow_direction_id'),
      });
    }
    const response = { accessory, section, width, height, depth, name, shape: hood, wireframe, mounting };
    if (callback) {
      callback.call(this, response);
    }
  }

  /** drawAccessoriesHoodComplete (canvas.view3d:2765-2797). */
  drawAccessoriesHoodComplete({ accessory, section, width, height, depth, shape, wireframe, mounting }: any): void {
    shape.layers.set(0);
    shape.shape_type = 'accessory';
    const section_on_scene = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    );
    if (!section_on_scene) return;
    const cube = section_on_scene.cube;
    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    const data = {
      accessory,
      section,
      cube,
      width,
      height,
      depth,
      shape,
      wireframe,
      mounting,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
    const positions = this.calculateAccessoriesHoodPositions(
      section_on_scene,
      accessory,
      cube,
      mounting,
      section.tunnel.get('airflow_direction_id')
    );
    const airflow_direction_id = section.tunnel.get('airflow_direction_id');
    shape.position.copy(positions);
    wireframe.position.copy(positions);
    shape.name = 'HOOD';
    shape.casing_mount_id = accessory.casing_mount_id;
    shape.parent_cube = cube;
    this.getHoodPositionDependOnMounting({ mounting, shape, wireframe, airflow_direction_id });
    cube.add(shape);
    cube.add(wireframe);
  }

  /**
   * G21 drawCylinderForDrainPan (canvas.view3d:2345-2362) — the pipe nipple,
   * positioned by slope-direction × airflow × conn_hand.
   */
  drawCylinderForDrainPan({ width, depth, shape, accessory, airflow_direction_id, tunnel_width }: any): void {
    const wireframe = this.drawPipe(depth, width, wireframeMaterialColorAccessory, 10);
    shape.add(wireframe);
    wireframe.rotateX(Math.PI / 2);
    const position = new THREE.Vector3(0, 0, 0);
    position.x = width + Number(accessory.dim9_shallow_side_depth) * this.coef;
    position.y = width;
    if (
      (+accessory.dim3_slope_direction_id === 2 && airflow_direction_id == 1) ||
      (+accessory.dim3_slope_direction_id === 1 && airflow_direction_id == 2)
    ) {
      position.y = accessory.dim1_length * this.coef - width;
    }
    const casing = (tunnel_width - accessory.width * this.coef) / 2;
    if (
      (accessory.conn_hand_id == 2 && airflow_direction_id == 2) ||
      (accessory.conn_hand_id == 1 && airflow_direction_id == 1)
    ) {
      position.z = depth / 2 - accessory.conn_protrusion_from_base * this.coef - casing;
    } else {
      position.z = accessory.width * this.coef - depth / 2 + accessory.conn_protrusion_from_base * this.coef + casing;
    }
    wireframe.position.copy(position);
  }

  /**
   * G21 drawDrainPan (canvas.view3d:2363-2396): prism profile (shallow vs
   * slope side by direction×airflow), invisible fill, dashed wireframe
   * gap/dash 0.3. `pipe_nipple_spn.length` is the STRING length — legacy
   * quirk, keep (G21 inventory note).
   */
  drawDrainPan(
    { accessory, section, width, name, color = sectionMaterialColor }: any,
    callback?: (r: any) => void
  ): void {
    const PrismGeometry = this.createPrismGeometry();
    const shallow_side = Number(accessory.dim9_shallow_side_depth) * this.coef;
    const slope_side = Number(accessory.dim4_slope_percent) * this.coef;
    const length = accessory.dim1_length * this.coef;
    const right =
      (+accessory.dim3_slope_direction_id === 2 && section.tunnel.get('airflow_direction_id') == 2) ||
      (+accessory.dim3_slope_direction_id === 1 && section.tunnel.get('airflow_direction_id') == 1);
    const A = new THREE.Vector2(0, 0);
    const B = new THREE.Vector2(0, length);
    const C = new THREE.Vector2(!right ? shallow_side : slope_side, 0);
    const D = new THREE.Vector2(right ? shallow_side : slope_side, length);
    const material = new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0 });
    const verticesArray = [A, B, D, C];
    const geometry: any = new (PrismGeometry as any)(verticesArray, accessory.width * this.coef);
    const drain_pan: any = new THREE.Mesh(geometry, material);
    const edges = new THREE.EdgesGeometry(geometry);
    const wireframeMaterial = new THREE.LineDashedMaterial({
      color: wireframeMaterialColorAccessory,
      gapSize: 0.3,
      dashSize: 0.3,
    });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    wireframe.scale.set(1.001, 1.001, 1.001);
    wireframe.computeLineDistances();
    wireframe.renderOrder = 1;
    this.drawCylinderForDrainPan({
      width: (accessory.pipe_nipple_dia * this.coef) / 2,
      depth: accessory.pipe_nipple_spn.length * this.coef,
      shape: drain_pan,
      accessory,
      airflow_direction_id: section.tunnel.get('airflow_direction_id'),
      tunnel_width: section.tunnel.get('width') * this.coef,
    });
    const response = { accessory, section, width, height: length, name, shape: drain_pan, wireframe };
    if (callback) {
      callback.call(this, response);
    }
  }

  /** drawDrainPanComplete (canvas.view3d:2870-2906) — floor parking + the `Drain Pan : <tag>` label rewrite. */
  drawDrainPanComplete({ accessory, section, width, height = null, depth, shape, wireframe }: any): void {
    shape.layers.set(0);
    const cube = this.sectionCanvasObjects.find(
      (el: any) => el.section.get('id') === section.get('id')
    )?.cube;
    if (!cube) return;
    const accessoryIndex = this.accessoryCanvasObjects.findIndex(
      (obj: any) => obj.accessory.id === accessory.id
    );
    shape.shape_type = 'accessory';
    accessory.label = `Drain Pan${accessory.tag ? ` : ${accessory.tag}` : ''}`;
    shape.accessory = accessory;
    const data = {
      accessory,
      section,
      cube,
      width,
      height,
      depth,
      shape,
      wireframe,
    };
    this.accessoryCanvasObjectsObj[accessory.id] = data;
    if (accessoryIndex > -1) {
      this.accessoryCanvasObjects[accessoryIndex] = data;
    } else this.accessoryCanvasObjects.push(data);
    const z_diff = (cube.geometry.parameters.depth - width) / 2;
    const tunnel_depth = 0;
    const x = accessory.distance_from_section_ups * this.coef + tunnel_depth;
    // sync K (canvas.view3d delta @3331, dev b6fb1d224f): "Above Floor" pans sit
    // lifted off the floor reference by the pan depth; "In Floor" pans stay recessed
    // at the floor line. `vert_pos` = cms_drain_pan.vert_pos.value from
    // drain_pan_serializers.py ('above_floor' / 'in_floor'), riding on the detail
    // row in both the boot feed and the live m.toJSON() draw payload.
    const floor_offset =
      accessory.vert_pos === 'above_floor' ? Number(accessory.depth || 0) * this.coef : 0;
    const start_y = this.tunnelStartYByFloorConstr[section.tunnel.id] * this.coef + floor_offset;
    const position = new THREE.Vector3(
      x - cube.geometry.parameters.width / 2,
      -cube.geometry.parameters.height / 2 + start_y,
      -cube.geometry.parameters.depth / 2 + z_diff
    );
    if (
      section.tunnel.get('airflow_direction_id') == 2 ||
      (+accessory.dim3_slope_direction_id === 2 && section.tunnel.get('airflow_direction_id') != 1) ||
      (section.tunnel.get('airflow_direction_id') == 2 && +accessory.dim3_slope_direction_id === 1)
    ) {
      position.x = cube.geometry.parameters.width / 2 - height - x;
    }
    wireframe.rotateZ(-Math.PI / 2);
    shape.rotateZ(-Math.PI / 2);
    shape.position.copy(position);
    wireframe.position.copy(position);
    cube.add(wireframe);
    cube.add(shape);
  }

  // --- update (destroy + redraw) wrappers (canvas.view3d:2397-2416) ---
  updateRectangle({ accessory, section, width, height, mounting, airflow_direction_id, name, viewport, int_wall_model }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawRectangle(
      { accessory, section, width, height, mounting, airflow_direction_id, name, viewport, int_wall_model },
      this.drawRectangleComplete
    );
  }

  updateCylinder({ accessory, section, width, height, mounting, airflow_direction_id, name, depth }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawCylinder(
      { accessory, section, width, height, mounting, airflow_direction_id, name, depth },
      this.drawCylinderComplete
    );
  }

  updateAccessoriesCube({ accessory, section, width, height, depth, mounting, name, int_wall_model }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawAccessoriesCube(
      { accessory, section, width, height, depth, name, mounting, int_wall_model },
      this.drawAccessoriesCubeComplete
    );
  }

  updateAccessoriesHood({ accessory, section, width, height, depth, mounting, name }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawAccessoriesHood(
      { accessory, section, width, height, depth, name, mounting },
      this.drawAccessoriesHoodComplete
    );
  }

  updateDrainPan({ accessory, section, width, name }: any): void {
    this.destroyAccessory(accessory.id);
    this.drawDrainPan({ accessory, section, width, name }, this.drawDrainPanComplete);
  }

  /** dev-sync (canvas.view3d delta @3210): raycast target set incl. the VFD
   * bank/enclosure extraShapes — every member box must hover/pick. */
  getAccessoryShapes(): any[] {
    return this.accessoryCanvasObjects.flatMap((obj: any) => [
      obj.shape,
      ...(obj.extraShapes || []).map((el: any) => el.shape),
    ]);
  }

  /** destroyAccessory (canvas.view3d:3046-3060 + dev-sync extraShapes delta @3226). */
  destroyAccessory(id: number | string): void {
    const accessoryIndex = this.accessoryCanvasObjects.findIndex((obj: any) => obj.accessory.id === id);
    if (!this.accessoryCanvasObjects[accessoryIndex]) return;
    delete this.accessoryCanvasObjectsObj[id];
    const cube =
      this.accessoryCanvasObjects[accessoryIndex].shape.parent ||
      this.accessoryCanvasObjects[accessoryIndex].wireframe.parent;
    this.disposeChilds(this.accessoryCanvasObjects[accessoryIndex].wireframe);
    this.disposeChilds(this.accessoryCanvasObjects[accessoryIndex].shape);
    cube.remove(this.accessoryCanvasObjects[accessoryIndex].wireframe);
    cube.remove(this.accessoryCanvasObjects[accessoryIndex].shape);
    if (this.accessoryCanvasObjects[accessoryIndex].wireframeDouble) {
      this.disposeChilds(this.accessoryCanvasObjects[accessoryIndex].wireframeDouble);
      cube.remove(this.accessoryCanvasObjects[accessoryIndex].wireframeDouble);
    }
    (this.accessoryCanvasObjects[accessoryIndex].extraShapes || []).forEach(
      ({ shape, wireframe }: any) => {
        this.disposeChilds(wireframe);
        this.disposeChilds(shape);
        this.disposeDetails(wireframe);
        this.disposeDetails(shape);
        cube.remove(wireframe);
        cube.remove(shape);
      }
    );
    this.accessoryCanvasObjects.splice(accessoryIndex, 1);
  }

  // =========================================================================
  // G36 — ACCESSORY POSITION TABLES (canvas.view3d:3731-3920)
  // =========================================================================

  /**
   * calculateRectanglePositions (canvas.view3d:3731-3758). The
   * (order,last)-keyed insulation_2 ladder is verbatim incl. the no-op first
   * branch (legacy self-assignment :3739).
   */
  calculateRectanglePositions(
    accessory: any,
    cube: any,
    mounting: any,
    airflow_direction_id: number,
    _int_wall: any,
    _name: string,
    section: any,
    isCube: any
  ): THREE.Vector3 {
    const unit = -2 * airflow_direction_id + 3;
    const width = (accessory.width || accessory.width_calc) * this.coef;
    const height = (accessory.height || accessory.height_calc) * this.coef;
    const depth = isCube ? isCube / 2 : 0;
    const insulation =
      (cube.geometry.parameters.depth - ((+accessory.distance_to_right_wall + mounting.x) * this.coef + width)) / 2;
    let insulation_2 =
      cube.geometry.parameters.width -
      (+accessory.distance_to_downstream_wall + mounting.x + accessory.width_calc) * this.coef;
    const insulation_3 =
      (cube.geometry.parameters.depth - ((+accessory.distance_to_right_wall + mounting.y) * this.coef + width)) / 2;
    if ((section.order === 1 && !section.last) || (section.order !== 1 && section.last)) {
      // :3739 — full insulation_2 kept (legacy self-assignment no-op).
    } else if (section.order === 1 && section.last) insulation_2 = insulation_2 / 2;
    else insulation_2 = insulation_2 / 2;
    const x = (cube.geometry.parameters.width - width) / 2;
    const x_h = (cube.geometry.parameters.width - height) / 2;
    const z_d = (cube.geometry.parameters.depth - width) / 2;
    const y = cube.geometry.parameters.height / 2;
    const z = cube.geometry.parameters.depth / 2;
    const y_h = (cube.geometry.parameters.height - height) / 2;
    const positionObj: any = {
      1: new THREE.Vector3(
        unit * (-x_h + insulation_2 + mounting.x * this.coef),
        -y + 0.01 - depth,
        unit * (-z_d + insulation_3 + mounting.y * this.coef)
      ), // Floor
      2: new THREE.Vector3(
        unit * (-x_h + insulation_2 + mounting.x * this.coef),
        y + 0.01 - depth,
        unit * (-z_d + insulation_3 + mounting.y * this.coef)
      ), // Roof
      3: new THREE.Vector3(
        unit * (cube.geometry.parameters.width / 2 + 0.01 - depth),
        -y_h + mounting.y * this.coef,
        unit * (-z_d + insulation + mounting.x * this.coef)
      ), // wall_downstream
      4: new THREE.Vector3(
        unit * (-cube.geometry.parameters.width / 2 - 0.01 + depth),
        -y_h + mounting.y * this.coef,
        unit * (-z_d + insulation + mounting.x * this.coef)
      ), // wall_upstream
      5: new THREE.Vector3(
        unit * (-x + insulation_2 + mounting.x * this.coef),
        -y_h + mounting.y * this.coef,
        unit * (-z - 0.01 + depth)
      ), // wall_left
      6: new THREE.Vector3(
        unit * (-x + insulation_2 + mounting.x * this.coef),
        -y_h + mounting.y * this.coef,
        unit * (z + 0.01 - depth)
      ), // wall_right
      horizontal_internal_wall: new THREE.Vector3(
        unit * (mounting.x * this.coef - x_h),
        unit * (-y + width / 2 + mounting.y * this.coef),
        0
      ),
      vertical_internal_wall: new THREE.Vector3(
        unit * -((-cube.geometry.parameters.width + width) / 2 + mounting.x * this.coef),
        -y_h + mounting.y * this.coef,
        0
      ),
    };
    return positionObj[mounting.mounting_plane_id] || positionObj[mounting.mounting_plane];
  }

  /**
   * calculateCylinderPositions (canvas.view3d:3760-3803) — incl. the
   * casing_mount 1 outset and the ADP_GAGE side-insulation zeroing.
   */
  calculateCylinderPositions(
    section: any,
    accessory: any,
    shape: any,
    cube: any,
    mounting: any,
    airflow_direction_id: number
  ): THREE.Vector3 {
    const unit = -2 * airflow_direction_id + 3;
    const width = shape.geometry.parameters.radiusTop * 2;
    const height = shape.geometry.parameters.height;
    const x = (cube.geometry.parameters.width - width) / 2;
    let x_h = (cube.geometry.parameters.width - height) / 2;
    const y_w = (cube.geometry.parameters.height - width) / 2;
    const z_w = (cube.geometry.parameters.depth - width) / 2;
    const y_h = (cube.geometry.parameters.height - height) / 2;
    let z_h = (cube.geometry.parameters.depth - height) / 2;
    let insulation_left = section.casing?.cube?.left ? section.casing?.cube.casing_thick : 0;
    let insulation_right = section.casing?.cube?.right ? section.casing?.cube.casing_thick : 0;
    let insulation =
      (cube.geometry.parameters.depth -
        (+accessory.distance_to_right_wall + mounting.x + accessory.width_calc) * this.coef) /
      2;
    const insulation_3 =
      (cube.geometry.parameters.depth -
        (+accessory.distance_to_right_wall + mounting.y + accessory.height_calc) * this.coef) /
      2;
    let insulation_4 = section.casing?.cube?.geometry?.parameters.depth
      ? (cube.geometry.parameters.depth - section.casing.cube.geometry.parameters.depth) / 2
      : 0;
    let insulation_5 = section.casing?.cube?.geometry?.parameters.height
      ? cube.geometry.parameters.height - section.casing.cube.geometry.parameters.height
      : 0;

    if (accessory.casing_mount_id == 1) {
      x_h = x_h + accessory.depth * this.coef;
      z_h = z_h + accessory.depth * this.coef;
      insulation_4 = 0;
      insulation_5 = 0;
      if (mounting.mounting_plane_id === 3 || mounting.mounting_plane_id === 4) {
        insulation_left = 0;
        insulation_right = 0;
      }
    } else if (accessory.name === 'ADP_GAGE') {
      if (mounting.mounting_plane_id === 3 || mounting.mounting_plane_id === 4) {
        insulation_left = 0;
        insulation_right = 0;
      } else {
        insulation = 0;
        insulation_4 = 0;
      }
    }
    const positionObj: any = {
      2: new THREE.Vector3(
        (-x + insulation_left + mounting.x * this.coef) * unit,
        y_h + 0.01 - insulation_5,
        (-z_w + insulation_3 + mounting.y * this.coef) * unit
      ), // Roof
      3: new THREE.Vector3(
        (x_h - insulation_right) * unit,
        -y_w + mounting.y * this.coef,
        (-z_w + insulation + mounting.x * this.coef) * unit
      ), // wall_downstream
      4: new THREE.Vector3(
        (-x_h + insulation_left) * unit,
        -y_w + mounting.y * this.coef,
        (-z_w + insulation + mounting.x * this.coef) * unit
      ), // wall_upstream
      5: new THREE.Vector3(
        (-x + insulation_left + mounting.x * this.coef) * unit,
        -y_w + mounting.y * this.coef,
        (-z_h + insulation_4) * unit
      ), // wall_left
      6: new THREE.Vector3(
        (-x + insulation_left + mounting.x * this.coef) * unit,
        -y_w + mounting.y * this.coef,
        (z_h - insulation_4) * unit
      ), // wall_right
    };
    return positionObj[mounting.mounting_plane_id];
  }

  /**
   * calculateAccessoriesCubePositions (canvas.view3d:3805-3864) — the full
   * plane table incl. the casing-mount-1 outset block with its 6-type
   * insulation zero list and the DAMPER/LOUVER casing_x/casing_y halves.
   * (Legacy declares x/x_w/…/z_h as implicit globals — scoped here, [NEG]#10
   * family.)
   */
  calculateAccessoriesCubePositions(
    section: any,
    shape: any,
    accessory: any,
    name: string,
    section_layer: any,
    mounting: any,
    airflow_direction_id: number,
    int_wall: any,
    withRect?: boolean
  ): THREE.Vector3 {
    const unit = -2 * airflow_direction_id + 3;
    const casing_y = name === 'LOUVER' || name === 'DAMPER' ? shape.diff_h / 2 : 0;
    const casing_x = name === 'LOUVER' || name === 'DAMPER' ? shape.diff_w / 2 : 0;
    let positionObj: any = {};
    let x = (section_layer.geometry.parameters.width - shape.geometry.parameters.width) / 2;
    let x_w = (int_wall?.rectangle?.geometry?.parameters?.width - shape.geometry.parameters.width) / 2;
    const x_d = (section_layer.geometry.parameters.width - shape.geometry.parameters.depth - casing_x) / 2;
    const y = (section_layer.geometry.parameters.height - shape.geometry.parameters.height - casing_y) / 2;
    let y_d = (section_layer.geometry.parameters.height - shape.geometry.parameters.width) / 2;
    let y_i = (section_layer.geometry.parameters.height - shape.geometry.parameters.width) / 2;
    const z = (section_layer.geometry.parameters.depth - shape.geometry.parameters.depth) / 2;
    let z_d = (section_layer.geometry.parameters.depth - shape.geometry.parameters.width) / 2;
    const z_h = (section_layer.geometry.parameters.depth - shape.geometry.parameters.height) / 2;
    const w_y = (int_wall?.rectangle?.geometry?.parameters.height - shape.geometry.parameters.height) / 2;
    const w_x = (int_wall?.rectangle?.geometry?.parameters.width - shape.geometry.parameters.depth) / 2;
    let w_z = shape.geometry.parameters.width / 2;
    let insulation_left = section.casing?.cube?.left ? section.casing?.cube.casing_thick : 0;
    let insulation_right = section.casing?.cube?.right ? section.casing?.cube.casing_thick : 0;
    const insulation =
      (section_layer.geometry.parameters.depth -
        ((+accessory.distance_to_right_wall + mounting.x) * this.coef + shape.geometry.parameters.depth)) /
      2;
    const insulation_3 =
      (section_layer.geometry.parameters.depth -
        ((+accessory.distance_to_right_wall + mounting.y) * this.coef + shape.geometry.parameters.height)) /
      2;
    let insulation_4 = section.casing?.cube?.geometry?.parameters.depth
      ? (section_layer.geometry.parameters.depth - section.casing.cube.geometry.parameters.depth) / 2
      : 0;
    let insulation_5 = section.casing?.cube?.geometry?.parameters.height
      ? section_layer.geometry.parameters.height - section.casing.cube.geometry.parameters.height
      : 0;
    if (withRect) {
      positionObj = {
        3: new THREE.Vector3(x * unit, -y + mounting.y * this.coef, (-z + mounting.x * this.coef) * unit), // wall_downstream
        4: new THREE.Vector3(-x * unit, -y + mounting.y * this.coef, (-z + mounting.x * this.coef) * unit), // wall_upstream
        5: new THREE.Vector3(0, 0, 0), // wall_left
        6: new THREE.Vector3(0 * unit, 0, 0), // wall_right
      };
    } else {
      if (accessory.casing_mount_id == 1 && mounting.mounting_plane_id != 1) {
        x = x + accessory.depth * this.coef;
        x_w = x_w - accessory.depth * this.coef;
        y_d = y_d + accessory.depth * this.coef;
        y_i = y_i + shape.geometry.parameters.height;
        z_d = z_d + accessory.depth * this.coef;
        w_z = w_z - accessory.depth * this.coef;
        insulation_4 = 0;
        insulation_5 = 0;
        const list = ['DISCONNECT', 'JBOX', 'ENCLOSURE', 'VFD', 'ADP_GAGE', 'DOG_HOUSE'];
        if (mounting.mounting_plane_id === 3 || mounting.mounting_plane_id === 4 || list.includes(name)) {
          insulation_left = 0;
          insulation_right = 0;
        }
      }
      positionObj = {
        1: new THREE.Vector3(
          unit * (-x_d + insulation_left + mounting.x * this.coef),
          -y_d + unit * 0.01,
          unit * (-z_h + insulation_3 + mounting.y * this.coef)
        ), // Floor
        2: new THREE.Vector3(
          unit * (-x_d + insulation_left + mounting.x * this.coef),
          y_d + 0.01 - insulation_5,
          unit * (-z_h + insulation_3 + mounting.y * this.coef)
        ), // Roof
        3: new THREE.Vector3(
          (x - 0.01 - insulation_right) * unit,
          -y + mounting.y * this.coef,
          (-z + insulation + mounting.x * this.coef) * unit
        ), // wall_downstream
        4: new THREE.Vector3(
          (-x + 0.01 + insulation_left) * unit,
          -y + mounting.y * this.coef,
          (-z + +insulation + mounting.x * this.coef) * unit
        ), // wall_upstream
        5: new THREE.Vector3(
          (-x_d + insulation_left + mounting.x * this.coef) * unit + 0.01 * unit,
          -y + mounting.y * this.coef,
          (-z_d + insulation_4 + 0.01) * unit
        ), // wall_left
        6: new THREE.Vector3(
          (-x_d + insulation_left + mounting.x * this.coef) * unit - 0.01 * unit,
          -y + mounting.y * this.coef,
          (z_d - insulation_4 - 0.01) * unit
        ), // wall_right
        horizontal_internal_wall: new THREE.Vector3(
          unit * (-w_x + mounting.x * this.coef),
          unit * (-w_y + mounting.y * this.coef),
          -w_z
        ),
        vertical_internal_wall: new THREE.Vector3(
          unit * (w_x - mounting.x * this.coef),
          -w_y + mounting.y * this.coef,
          -w_z
        ),
      };
    }
    void y_i; // stamped exactly like legacy (computed, only the cm-1 block mutates it; no reader)
    return positionObj[mounting.mounting_plane_id] || positionObj[mounting.mounting_plane];
  }

  /** calculateAccessoriesCubeBladePositions (canvas.view3d:3866-3877). */
  calculateAccessoriesCubeBladePositions(
    count: number,
    blade: any,
    shape: any,
    _mounting: any,
    i: number,
    blade_direction_id: any,
    new_height: number,
    on_top_or_bottom: boolean
  ): THREE.Vector3 {
    let y = 0,
      z = 0;
    const x = -shape.geometry.parameters.width / 2 - (new_height - shape.geometry.parameters.width) / 2;
    if (blade_direction_id === 2 || on_top_or_bottom) {
      const extra_height =
        (shape.geometry.parameters.depth - Math.ceil(count) * blade.geometry.parameters.height) / 2;
      z =
        (shape.geometry.parameters.depth + blade.geometry.parameters.height) / 2 -
        (i + 1) * blade.geometry.parameters.height -
        extra_height;
    } else {
      const extra_height =
        (shape.geometry.parameters.height - Math.ceil(count) * blade.geometry.parameters.height) / 2;
      y =
        -(shape.geometry.parameters.height - blade.geometry.parameters.height) / 2 +
        blade.geometry.parameters.height * i +
        extra_height;
    }
    return new THREE.Vector3(x, y, z);
  }

  /**
   * calculateAccessoriesHoodPositions (canvas.view3d:3878-3906) — planes 3-6
   * only (hood hangs OUTSIDE the face); insulation_2 ladder as G12.
   */
  calculateAccessoriesHoodPositions(
    section: any,
    accessory: any,
    cube: any,
    mounting: any,
    airflow_direction_id: number,
    withRect?: boolean
  ): THREE.Vector3 {
    let positionObj: any = {};
    const hood_x = cube.geometry.parameters.width / 2;
    const hood_y = cube.geometry.parameters.height / 2;
    const hood_z = cube.geometry.parameters.depth / 2;

    const unit = -2 * airflow_direction_id + 3;
    const insulation =
      (cube.geometry.parameters.depth -
        (+accessory.distance_to_right_wall + mounting.x + +accessory.width) * this.coef) /
      2;
    let insulation_2 =
      cube.geometry.parameters.width -
      (+accessory.distance_to_downstream_wall + mounting.x + +accessory.width) * this.coef;
    if ((section.order === 1 && !section.last) || (section.order !== 1 && section.last)) {
      // :3894 — full insulation_2 kept (legacy self-assignment no-op).
    } else if (section.order === 1 && section.last) insulation_2 = insulation_2 / 2;
    else insulation_2 = insulation_2 / 2;
    if (!withRect) {
      positionObj = {
        3: new THREE.Vector3(
          (hood_x + 0.01) * unit,
          -hood_y + mounting.y * this.coef,
          unit * (-hood_z + insulation + mounting.x * this.coef)
        ), // wall_downstream
        4: new THREE.Vector3(
          (-hood_x - 0.01) * unit,
          -hood_y + mounting.y * this.coef,
          unit * (-hood_z + insulation + mounting.x * this.coef + accessory.width * this.coef)
        ), // wall_upstream
        5: new THREE.Vector3(
          unit * (-hood_x + insulation_2 + mounting.x * this.coef),
          -hood_y + mounting.y * this.coef,
          (-hood_z - 0.01) * unit
        ), // wall_left
        6: new THREE.Vector3(
          unit * (-hood_x + insulation_2 + mounting.x * this.coef + accessory.width * this.coef),
          -hood_y + mounting.y * this.coef,
          (hood_z + 0.01) * unit
        ), // wall_right
      };
    }
    return positionObj[mounting.mounting_plane_id];
  }

  /** calculateViewportPositions (canvas.view3d:3907-3921). */
  calculateViewportPositions(shape: any, viewport_data: any, mounting: any, airflow_direction_id: number): THREE.Vector3 {
    const unit = -2 * airflow_direction_id + 3;
    const height = (shape.geometry.parameters.height - viewport_data.shape.geometry.parameters.height) / 2;
    const y = height / 2.5;
    const depth = this.coef;
    const positionObj: any = {
      3: new THREE.Vector3(unit * 0.01, height - y, depth), // wall_downstream
      4: new THREE.Vector3(-unit * 0.01, height - y, -depth), // wall_upstream
      5: new THREE.Vector3(0, height - y, -unit * depth), // wall_left
      6: new THREE.Vector3(0, height - y, unit * depth), // wall_right
      horizontal_internal_wall: new THREE.Vector3(0, height - y, unit * 0.01),
      vertical_internal_wall: new THREE.Vector3(0, height - y, -unit * depth),
    };
    return positionObj[mounting.mounting_plane_id] || positionObj[mounting.mounting_plane];
  }

  // --- per-plane rotation/dash routers (canvas.view3d:2907-2982) ---

  /** getRectanglePositionDependOnMounting (canvas.view3d:2907-2922). */
  getRectanglePositionDependOnMounting({
    mounting_plane_id,
    mounting_plane,
    shape,
    wireframe,
    wireframeDouble,
    airflow_direction_id,
  }: any): void {
    this.makeLineDashed({ mounting_plane_id }, wireframe.material, this.text_view_position, airflow_direction_id, shape);
    if (mounting_plane_id == 3 || mounting_plane_id == 4) {
      shape.rotation.y = Math.PI / 2;
      wireframe.rotation.y = Math.PI / 2;
      wireframeDouble.rotation.y = Math.PI / 2;
    } else if (mounting_plane_id == 2 || mounting_plane_id == 1) {
      shape.rotation.x = Math.PI / 2;
      shape.rotation.z = Math.PI / 2;
      wireframe.rotation.x = Math.PI / 2;
      wireframe.rotation.z = Math.PI / 2;
    } else if (mounting_plane === 'horizontal_internal_wall') {
      shape.rotation.z = Math.PI / 2;
      wireframe.rotation.z = Math.PI / 2;
    }
  }

  /**
   * getCubePositionDependOnMounting (canvas.view3d:2923-2944). [NEG]#98
   * resolved as in drawViewport: the legacy `shape.rotation = int_wall…`
   * aliasing is a sloppy-mode silent NO-OP against r110's non-writable
   * Object3D.rotation — the `+=`/`=` mutations land on the shape's OWN Euler
   * (which then composes with the wall parent's rotation through the scene
   * graph). The assignments are dropped; the mutations are kept verbatim.
   */
  getCubePositionDependOnMounting({ mounting_plane_id, mounting_plane, shape, wireframe, int_wall: _int_wall, airflow_direction_id }: any): void {
    this.makeLineDashed({ mounting_plane_id }, wireframe.material, this.text_view_position, airflow_direction_id, shape);
    if (mounting_plane_id == 5 || mounting_plane_id == 6) {
      shape.rotation.y = Math.PI / 2;
      wireframe.rotation.y = Math.PI / 2;
    } else if (mounting_plane_id == 2 || mounting_plane_id == 1) {
      shape.rotation.z = Math.PI / 2;
      wireframe.rotation.z = Math.PI / 2;
      shape.rotation.y = Math.PI / 2;
      wireframe.rotation.y = Math.PI / 2;
    } else if (mounting_plane === 'vertical_internal_wall') {
      shape.rotation.y += Math.PI / 2;
      wireframe.rotation.y += Math.PI / 2;
    } else if (mounting_plane === 'horizontal_internal_wall') {
      shape.rotation.y += Math.PI / 2;
      wireframe.rotation.y = Math.PI / 2;
    }
  }

  /** getCylinderPositionDependOnMounting (canvas.view3d:2945-2954). */
  getCylinderPositionDependOnMounting({ mounting, shape, wireframe, airflow_direction_id }: any): void {
    this.makeLineDashed(mounting, wireframe.material, this.text_view_position, airflow_direction_id, shape);
    if (mounting.mounting_plane_id == 3 || mounting.mounting_plane_id == 4) {
      shape.rotation.z = Math.PI / 2;
      wireframe.rotation.z = Math.PI / 2;
    } else if (mounting.mounting_plane_id == 5 || mounting.mounting_plane_id == 6) {
      shape.rotation.x = Math.PI / 2;
      wireframe.rotation.x = Math.PI / 2;
    }
  }

  /** getHoodPositionDependOnMounting (canvas.view3d:2955-2982). */
  getHoodPositionDependOnMounting({ mounting, shape, wireframe, airflow_direction_id }: any): void {
    this.makeLineDashed(mounting, wireframe.material, this.text_view_position, airflow_direction_id, shape);
    if (mounting.mounting_plane_id == 4) {
      shape.rotation.y = -Math.PI;
      wireframe.rotation.y = -Math.PI;
      if (airflow_direction_id == 2) {
        shape.rotation.y = 2 * Math.PI;
        wireframe.rotation.y = 2 * Math.PI;
      }
    } else if (mounting.mounting_plane_id == 5) {
      shape.rotation.y = Math.PI / 2;
      wireframe.rotation.y = Math.PI / 2;
      if (airflow_direction_id == 2) {
        shape.rotation.y = -Math.PI / 2;
        wireframe.rotation.y = -Math.PI / 2;
      }
    } else if (mounting.mounting_plane_id == 6) {
      shape.rotation.y = -Math.PI / 2;
      wireframe.rotation.y = -Math.PI / 2;
      if (airflow_direction_id == 2) {
        shape.rotation.y = Math.PI / 2;
        wireframe.rotation.y = Math.PI / 2;
      }
    } else if (mounting.mounting_plane_id == 3 && airflow_direction_id == 2) {
      shape.rotation.y = -Math.PI;
      wireframe.rotation.y = -Math.PI;
    }
  }

  // =========================================================================
  // G31/G32 — COIL + EVAP-PACK CONNECTIONS
  // =========================================================================

  /**
   * G31 drawCoilConnections (canvas.view3d:5077-5128) — §3.4 #20 receiver +
   * the drawSectionComplete cache replay: invisible wrapper sized depth ×
   * (fh+flanges)·qty × fl; per stacked row an invisible row cube carrying 2
   * header pipes (+ protrusions; outlet omitted for steam type_id 2);
   * mounting angle via rotation.z with corner-delta Y/X compensation.
   */
  drawCoilConnections(coil: any, section_id: number, qty: number, duty: any, h_from_floor: any, rot_angle: any): void {
    if (!coil.model) return;
    this.need_update = true;
    const section = this.sectionCanvasObjects.find((el: any) => el.section.get('id') === section_id);
    if (!section) return;
    const start_y = this.tunnelStartYByFloorConstr[section.tunnel.id] * this.coef;
    const insulation_left = section.casing?.cube?.left ? section.casing?.cube.casing_thick : 0;
    const insulation_right = section.casing?.cube?.right ? section.casing?.cube.casing_thick : 0;
    const colors_by_duty: Record<string, string> = { 1: '#3867af', 2: 'red', 3: '#6f4e37', 4: 'red', 5: 'red' };
    const conn_hand_unit = ({ 2: 1, 1: -1 } as any)[coil.conn_hand_id];
    const airflow_direction_unit = ({ 1: 1, 2: -1 } as any)[section.tunnel.get('airflow_direction_id')];
    const angle = section.tunnel.get('airflow_direction_id') == 1 ? rot_angle * -1 : rot_angle;
    // `settings` rides the rating_mode_coils GET (coil_serializers.py:242-262);
    // legacy (canvas.view3d.js:5275-5277) defaults each MISSING KEY with
    // `|| 0` / `|| 1` — extend the same defaults to a missing settings object
    // (a replayed/partial coil payload must not throw and abort the caller).
    const start = (
      {
        1:
          -(section.cube.geometry.parameters.width / 2 - (coil.depth * this.coef) / 2) +
          (coil.settings?.clear_ups_in_section || 0) * this.coef +
          insulation_left,
        2:
          section.cube.geometry.parameters.width / 2 -
          (coil.depth * this.coef) / 2 -
          (coil.settings?.clear_downs_in_section || 0) * this.coef +
          insulation_right,
      } as any
    )[coil.settings?.in_section_clear_selection_id || 1];
    const { cube: group, wireframe } = this.drawCubeTemplate({
      width: coil.depth * this.coef,
      height: (coil.fh + Number(coil.casing_flange_top) + Number(coil.casing_flange_btm)) * this.coef * qty,
      length: coil.fl * this.coef,
      cubeOpacity: 0,
      cubeColor: colors_by_duty[duty],
    });
    const rotation_angle = this.getRotationAngle(angle);
    for (let i = 0; i < qty; i++) {
      const { wireframe: w, cube: c } = this.createCube(coil, colors_by_duty[duty], 0);
      (w.material as any).visible = false;
      wireframe.add(w);
      const verticalOffsetC = this.calculateVerticalOffset(group, c, i, coil);
      w.position.y = verticalOffsetC + start_y;
      const { cylinder_inlet, cylinder_outlet } = this.drawVerticalPipes(
        coil,
        colors_by_duty[duty],
        airflow_direction_unit,
        conn_hand_unit
      );
      w.add(cylinder_inlet);
      if (coil.type_id != '2') w.add(cylinder_outlet);
      this.drawHorizontalPipes(cylinder_inlet, coil, colors_by_duty[duty], airflow_direction_unit, conn_hand_unit, 'inlet');
      this.drawHorizontalPipes(cylinder_outlet, coil, colors_by_duty[duty], airflow_direction_unit, conn_hand_unit, 'outlet');
    }
    wireframe.rotation.z = rotation_angle;
    const top = this.getRotatedPosition(group, rotation_angle);
    const unit = angle > 0 ? -1 : 1;
    (wireframe.material as any).visible = false;
    wireframe.position.y =
      -(section.cube.geometry.parameters.height - group.geometry.parameters.height) / 2 +
      top[angle > 0 ? 0 : 1].y +
      h_from_floor * this.coef;
    wireframe.position.x =
      airflow_direction_unit * start +
      top[0].x * unit * (section.tunnel.get('airflow_direction_id') == 1 ? 1 : -1);
    section.cube.add(wireframe);
    if (this.coilConnections[section_id]) {
      this.disposeChilds(this.coilConnections[section_id].group);
      this.disposeChilds(this.coilConnections[section_id].wireframe);
      section.cube.remove(this.coilConnections[section_id].group);
      section.cube.remove(this.coilConnections[section_id].wireframe);
    }
    this.coilConnections[section_id] = { coil, qty, duty, group, h_from_floor, angle: rot_angle, wireframe };
  }

  /**
   * drawPipe (canvas.view3d:5130-5141) — concentric EdgesGeometry cylinders
   * (dia & dia−0.05) under one dashed-capable material (G21/G31/G32 shared).
   */
  drawPipe(height: number, diameter: number, color: any, coefficient = 100): any {
    const cylinderGeometry = new THREE.CylinderBufferGeometry(diameter, diameter, height, coefficient);
    const cylinderGeometry_2 = new THREE.CylinderBufferGeometry(diameter - 0.05, diameter - 0.05, height, coefficient);
    const wireframeMaterial = new THREE.LineDashedMaterial({ color });

    const edges = new THREE.EdgesGeometry(cylinderGeometry);
    const edges_2 = new THREE.EdgesGeometry(cylinderGeometry_2);
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    const wireframe_2 = new THREE.LineSegments(edges_2, wireframeMaterial);
    wireframe.add(wireframe_2);
    return wireframe;
  }

  /** createCube (canvas.view3d:5143-5151) — the per-row invisible coil cube. */
  createCube(coil: any, cubeColor: any, fl_id_to_header_id = 0): { wireframe: any; cube: any } {
    return this.drawCubeTemplate({
      width: (fl_id_to_header_id + coil.depth) * this.coef,
      height:
        (fl_id_to_header_id + coil.fh + Number(coil.casing_flange_top) + Number(coil.casing_flange_btm)) * this.coef,
      length: (fl_id_to_header_id + coil.fl) * this.coef,
      cubeOpacity: 0,
      cubeColor,
    });
  }

  /** calculateVerticalOffset (canvas.view3d:5153-5158) — stacked-row Y. */
  calculateVerticalOffset(section: any, cube: any, index: number, coil: any): number {
    const coil_to_coil_vertical_clear = Number(coil.coil_to_coil_vertical_clear) || 0;
    return (
      -(section.geometry.parameters.height - cube.geometry.parameters.height) / 2 +
      index * cube.geometry.parameters.height +
      (index + 1) * coil_to_coil_vertical_clear
    );
  }

  /** drawVerticalPipes (canvas.view3d:5160-5170) — the two header pipes. */
  drawVerticalPipes(coil: any, color: any, airflow_direction_unit: number, conn_hand_unit: number): { cylinder_inlet: any; cylinder_outlet: any } {
    const height = coil.fh * this.coef;
    const cylinder_inlet = this.drawPipe(height, coil.inlet_header_dia * this.coef, color);
    const cylinder_outlet = this.drawPipe(height, coil.outlet_header_dia * this.coef, color);

    cylinder_outlet.position.x = -(coil.depth / 2 - coil.outlet_header_dia - 0.5) * this.coef * airflow_direction_unit;
    cylinder_outlet.position.z =
      airflow_direction_unit * conn_hand_unit * (coil.fl / 2 + coil.outlet_header_dia + coil.header_cl_to_casing_edge) * this.coef;
    cylinder_inlet.position.x = (coil.depth / 2 - coil.inlet_header_dia - 0.5) * this.coef * airflow_direction_unit;
    cylinder_inlet.position.z =
      airflow_direction_unit * conn_hand_unit * (coil.fl / 2 + coil.inlet_header_dia + coil.header_cl_to_casing_edge) * this.coef;
    return { cylinder_inlet, cylinder_outlet };
  }

  /** drawHorizontalPipes (canvas.view3d:5172-5185) — the protrusion pipes at the conn clearances. */
  drawHorizontalPipes(cylinder: any, coil: any, color: any, airflow_direction_unit: number, conn_hand_unit: number, type: 'inlet' | 'outlet'): void {
    const is_inlet = type === 'inlet';
    const diameter = is_inlet ? coil.inlet_header_dia : coil.outlet_header_dia;
    const conn_cl_to_casing = is_inlet ? coil.inlet_conn_cl_to_btm_of_casing : coil.outlet_conn_cl_to_top_of_casing;
    const height = coil.fh * this.coef;

    const pipe = this.drawPipe(coil.conn_protrusion_from_header * this.coef, diameter * this.coef, color);
    cylinder.add(pipe);
    pipe.rotation.x = Math.PI / 2;
    pipe.position.z = (airflow_direction_unit * conn_hand_unit * coil.conn_protrusion_from_header * this.coef) / 2;
    pipe.position.y = is_inlet
      ? -(height - diameter * this.coef) / 2 + conn_cl_to_casing * this.coef
      : (height - diameter * this.coef) / 2 - conn_cl_to_casing * this.coef;
  }

  /** getRotatedPosition (canvas.view3d:5186-5212) — corner deltas of the rotated wrapper. */
  getRotatedPosition(cube: any, angle: number): Array<{ x: number; y: number }> {
    const width = cube.geometry.parameters.width;
    const height = cube.geometry.parameters.height;
    const halfWidth = width / 2;
    const halfHeight = height / 2;
    const corners = [
      { x: -halfWidth, y: -halfHeight }, // Top-left
      { x: halfWidth, y: -halfHeight }, // Top-right
      { x: halfWidth, y: halfHeight }, // Bottom-right
      { x: -halfWidth, y: halfHeight }, // Bottom-left
    ];
    const rotatedCorners = corners.map((corner) => {
      const rotatedX = corner.x * Math.cos(angle) - corner.y * Math.sin(angle);
      const rotatedY = corner.x * Math.sin(angle) + corner.y * Math.cos(angle);
      const rotatedX_0 = corner.x * Math.cos(0) - corner.y * Math.sin(0);
      const rotatedY_0 = corner.x * Math.sin(0) + corner.y * Math.cos(0);
      return { x: rotatedX_0 - rotatedX, y: rotatedY_0 - rotatedY };
    });
    return rotatedCorners;
  }

  /** drawCubeTemplate (canvas.view3d:5213-5229) — invisible cube + invisible-line wireframe pair. */
  drawCubeTemplate({ width, height, length, cubeColor, cubeOpacity }: any): { wireframe: any; cube: any } {
    const cubeGeometry = new THREE.BoxBufferGeometry(width, height, length);
    const cubeMaterial = new THREE.MeshBasicMaterial({ color: cubeColor, transparent: true, opacity: cubeOpacity });
    const cube = new THREE.Mesh(cubeGeometry, cubeMaterial);
    const edges = new THREE.EdgesGeometry(cubeGeometry);
    const wireframeMaterial = new THREE.LineBasicMaterial({ opacity: 0, transparent: true });
    const wireframe = new THREE.LineSegments(edges, wireframeMaterial);
    return { wireframe, cube };
  }

  /**
   * G32 drawEvapPackConnections (canvas.view3d:5231-5247) — §3.4 #21
   * receiver + replay: fill/drain/overflow nipples grouped on the section
   * cube. Legacy removes the prior group WITHOUT disposing (minor leak) —
   * fixed silently per the G32 inventory note.
   */
  drawEvapPackConnections(data: any, section_id: number): void {
    if (this.evapPackConnections[section_id]) {
      this.disposeChilds(this.evapPackConnections[section_id].group); // dedrift: legacy leaks here
      this.evapPackConnections[section_id].cube.remove(this.evapPackConnections[section_id].group);
    }
    const section = this.sectionCanvasObjects.find((el: any) => el.section.get('id') === section_id);
    if (!section) return; // defensive — legacy throws when the section is off-canvas
    const cube = section.cube;
    const airflow_direction_id = section.tunnel.get('airflow_direction_id');
    const start = this.tunnelStartYByFloorConstr[section.tunnel.id] * this.coef;
    const group = new THREE.Group();
    const fill = this.drawAndcalculateEvapPackPipePosition(data.fill, cube, airflow_direction_id, start);
    const drain = this.drawAndcalculateEvapPackPipePosition(data.drain, cube, airflow_direction_id, start);
    const overflow = this.drawAndcalculateEvapPackPipePosition(data.overflow, cube, airflow_direction_id, start);
    group.add(fill);
    group.add(drain);
    group.add(overflow);
    cube.add(group);
    this.evapPackConnections[section_id] = { data, section_id, group, cube };
    this.need_update = true;
  }

  /** drawAndcalculateEvapPackPipePosition (canvas.view3d:5249-5260) [sic name]. */
  drawAndcalculateEvapPackPipePosition(data: any, cube: any, airflow_direction_id: number, start: number): any {
    const length = data.pipe_nipple_length * this.coef;
    const radius = (data.pipe_nipple_od_dia / 2) * this.coef;
    const shape = this.drawPipe(length, radius, 'black', 10);
    const unit_airflow = airflow_direction_id === 2 ? -1 : 1;
    const unit_conn = data.conn_hand === 1 ? -1 : 1;
    shape.rotation.x = Math.PI / 2;
    shape.position.y = -(cube.geometry.parameters.height / 2 - radius) + data.conn_cl_h_sump_floor * this.coef + start;
    shape.position.x = (-cube.geometry.parameters.width / 2 + data.conn_cl_ups_edge_sump * this.coef) * unit_airflow;
    shape.position.z =
      ((cube.geometry.parameters.depth - length) / 2 + data.pipe_nipple_protrusion * this.coef) * unit_conn * unit_airflow;
    return shape;
  }

  /** G19 drawDogHouseBase (canvas.view3d:5261-5271) — createBase part set hung under the dog-house cube. */
  drawDogHouseBase(accessory: any, dog_house: any): void {
    const width = dog_house.geometry.parameters.depth;
    const height = this.coef * accessory.base_height;
    const length = dog_house.geometry.parameters.width;

    const baseData = this.createBase({ width, height, length, baseType: accessory.base_type });
    if (!baseData) return;
    const { group } = baseData;
    group.position.y = -(dog_house.geometry.parameters.height + height) / 2;
    dog_house.add(group);
  }

  // =========================================================================
  // tunnel.js DISPATCHER HANDLERS — accessory draw channels (§3.4 #5/#8)
  // =========================================================================

  /**
   * `draw:accessory:on:canvas` receiver (tunnel.js:252-295): name
   * upper-cased; preview accessory gets the staging/mounting overlay and
   * skips the missing-int-wall pre-draw; otherwise an int-wall payload that
   * is not on canvas yet is drawn first (re-enters §3.4 #17). The `section`
   * payload arg is a legacy Backbone section model — React callers may pass
   * the wrapper, a plain row or an id; resolved against the live wrappers.
   */
  onDrawAccessoryOnCanvas(
    accessory: any,
    label: string,
    sectionArg: any,
    mounting: any,
    isAlredyRendered: boolean,
    viewport?: any,
    int_wall?: any
  ): void {
    const section = this.resolveSectionModel(sectionArg);
    if (!section) return;
    this.need_update = true;
    accessory.name = label.toUpperCase();
    if (this.previewAccessoryId != null && accessory.id === this.previewAccessoryId) {
      const stagingData = this.previewAccessoryStagingData;
      const acc = stagingData ? { ...accessory, ...stagingData, name: label.toUpperCase() } : accessory;
      const mnt = this.previewMounting || mounting || { x: 5, y: 5, mounting_plane_id: 5 };
      this.drawAccessory({
        accessory: acc,
        section,
        width: acc.width || acc.overall_width || acc.width_calc,
        height: acc.height || acc.overall_depth || acc.height_calc,
        depth: acc.depth ? acc.depth : null,
        name: label.toUpperCase(),
        mounting: mnt,
        isAlredyRendered: !!this.accessoryCanvasObjectsObj[accessory.id],
        viewport,
        int_wall_model: int_wall,
      });
      return;
    }
    const existed_int_wall = int_wall
      ? this.int_wall.horizontal[int_wall.id] || this.int_wall.vertical[int_wall.id]
      : null;
    if (int_wall && !existed_int_wall) this.drawInternalWall(int_wall.id, int_wall);
    this.drawAccessory({
      accessory,
      section,
      width: accessory.width || accessory.overall_width || accessory.width_calc,
      height: accessory.height || accessory.overall_depth || accessory.height_calc,
      depth: accessory.depth ? accessory.depth : null,
      name: label.toUpperCase(),
      mounting: mounting || {
        x: 5,
        y: 5,
        mounting_plane_id: 5,
      },
      isAlredyRendered,
      viewport,
      int_wall_model: int_wall,
    });
  }

  /** `remove_accessory_from_canvas` receiver (tunnel.js:172-178) — skips the active preview accessory. */
  onRemoveAccessoryFromCanvas(id: number): void {
    if (this.previewAccessoryId != null && id === this.previewAccessoryId) return;
    this.need_update = true;
    this.destroyAccessory(id);
  }

  /** Bus-payload section → live wrapper (legacy callers pass persistent Backbone models). */
  resolveSectionModel(sectionArg: any): CanvasSectionModel | null {
    if (sectionArg && typeof sectionArg.get === 'function' && sectionArg.tunnel) return sectionArg;
    const id = typeof sectionArg === 'number' ? sectionArg : sectionArg?.id;
    if (id == null) return null;
    for (const tunnel of this.tunnelModels) {
      const found = tunnel.sections.find((s) => s.id === id);
      if (found) return found;
    }
    return null;
  }

  // =========================================================================
  // COLOR TABLES (G34 hooks the 6c1 surfaces need)
  // =========================================================================

  /**
   * getSectionColors (canvas.view3d:5731-5736). [NEG]#2: the first statement
   * hard-returns `colors.default` — the error/warning SECTION-FILL palette
   * below it is unreachable. Replicated (do not "fix"); the live error/warning
   * tints flow through accessories (getAccessoryColors, 6c2).
   */
  getSectionColors(_section_id: number): { passive: number; hover: number; selected: number } {
    return colors.default;
  }

  /**
   * updateSectionsColor (canvas.view3d:4769-4773) — §3.4 #23 receiver: store
   * + redraw.
   *
   * DEDRIFT (deliberate): legacy redraws UNCONDITIONALLY, and the error log
   * re-emits `sectionsWithError` after every save and every section
   * operation — so a validation round trip that changed nothing still cost a
   * full geometry pass a second after the board had settled. The store half
   * stays verbatim; only the redraw is now conditional on the error state
   * actually differing. `updateAccessoriesColor` right below already keeps a
   * `prev` copy for exactly this kind of diff, so the shape is legacy's own.
   */
  updateSectionsColor(sectionsWithError: any, erroeMessages: any): void {
    const unchanged =
      JSON.stringify(this.sectionsWithError ?? {}) === JSON.stringify(sectionsWithError ?? {}) &&
      JSON.stringify(this.erroeMessages ?? {}) === JSON.stringify(erroeMessages ?? {});
    this.sectionsWithError = sectionsWithError;
    // sic — the legacy field name typo is load-bearing for grep parity (§22.5).
    this.erroeMessages = erroeMessages;
    if (unchanged) return;
    this.emitBus('redraw:canvas');
  }

  /**
   * getAccessoryColors (canvas.view3d:5726-5730) — LIVE (unlike the section
   * table, [NEG]#2): error palette for 'do_not_generate', warning for any
   * other truthy entry, both gated on showColor.
   */
  getAccessoryColors(accessory_id: any): { passive?: number; hover: number; selected: number } {
    if (!this.showColor || !this.accessoriesWithError[accessory_id]) return acc_colors.default;
    if (this.accessoriesWithError[accessory_id] === 'do_not_generate') return acc_colors.error;
    return acc_colors.warning;
  }

  // =========================================================================
  // G34 — HOVER / SELECTION / ERROR TINT STATE MACHINE
  // =========================================================================

  /**
   * selectDeselectSection (canvas.view3d:1181-1204) — `force:select:section`
   * / `deselect:section` receiver + the §3.2.10 selection API: stores
   * section_with_error (error rows keep their tint until released), retargets
   * the tunnel-isolation pill, deselects all actives unless ctrl, tints the
   * target, then collapses the open accessory row of a deselected section
   * (panel hook). The `color` arg is accepted and IGNORED — verbatim legacy
   * (updateSectionStyles takes no color; the §22.5 row-click colors only
   * matter through section_with_error).
   */
  selectDeselectSection(
    section_id: number | null | undefined,
    error = false,
    detect_error = false,
    _color?: number | string,
    isCtrlPressed = false
  ): void {
    this.section_with_error = error && !detect_error ? section_id ?? null : null;
    this.need_update = true;
    const selected = this.sectionCanvasObjects.find((el: any) => el.section.id == section_id);
    if (this.activeTunnelIndex && selected?.tunnel?.id && this.activeTunnelIndex != selected?.tunnel?.id) {
      this.activeTunnelIndex = selected?.tunnel?.id;
      this.renderTunnelsIndexes();
    }
    const deselecteds = this.sectionCanvasObjects.filter(
      (el: any) => el.active && (el.section.id == section_id || !section_id || selected)
    );
    if (deselecteds.length > 0 && !isCtrlPressed) {
      deselecteds.forEach((deselected: any) => {
        this.updateSectionStyles(deselected, false);
      });
    }
    if (selected) this.updateSectionStyles(selected, true);
    if (this.selectedAccessory) {
      const deselected = deselecteds[0];
      const accSectionId = this.accessoryCanvasObjectsObj[this.selectedAccessory]?.section.id;
      if (accSectionId && deselected?.section?.id === accSectionId) {
        const open = this.isAccessoryRowCollapsed(this.selectedAccessory as number);
        if (!open) this.onAccessoryActivityToggle(this.selectedAccessory as number);
      }
    }
  }

  /**
   * updateSectionStyles (canvas.view3d:1205-1221) — the single section tint
   * writer: fill + wireframe + viewport panes + the `active` stamp.
   */
  updateSectionStyles(section: any, active: boolean): void {
    this.need_update = true;
    const sectionColors = this.getSectionColors(section.section.id);
    if (!active) {
      section.cube.material.color.set(sectionColors.passive);
      section.wireframe.material.color.set(wireframeMaterialColor);
      section.cube.viewports?.forEach((el: any) => el.material.color.set(sectionColors.passive));
      section.active = false;
      return;
    }
    section.cube.material.color.set(sectionColors.selected);
    section.wireframe.material.color.set(sectionColors.selected);
    section.cube.viewports?.forEach((el: any) => el.material.color.set(sectionColors.selected));
    section.active = true;
  }

  /**
   * deselectAllActiveSections (canvas.view3d:1222-1230) — clears every active
   * cube except the current selection, then refreshes the multi-select list.
   */
  deselectAllActiveSections(): void {
    const current_section_id = this.getCurrentSectionId();
    this.sectionCanvasObjects.forEach((section: any) => {
      if (section.active && section.section.id != current_section_id) {
        this.updateSectionStyles(section, false);
      }
    });
    this.emitBus('update:multiple:sections:list');
  }

  /**
   * hover_scene_section (canvas.view3d:4700-4726) — `hover:section` receiver
   * (error-log rows / panel hover): restores the previous hover target
   * (unless it owns the error tint), tints the new one (+ optional accessory
   * hover pass-through).
   */
  hover_scene_section({ section, accessory }: any = {}): void {
    this.need_update = true;
    const accessory_on_scene = accessory
      ? this.accessoryCanvasObjects.find((el: any) => el.accessory.id == accessory)
      : null;
    if (!section) {
      if (this.hover_section) {
        if (this.section_with_error == this.hover_section.section.id) return;
        const hoverColors = this.getSectionColors(this.hover_section.section.id);
        const color = this.hover_section.active ? hoverColors.selected : hoverColors.hover;
        const w_color = this.hover_section.active ? wireframeMaterialColorActive : wireframeMaterialColor;
        this.hover_section.cube.material.color.set(color);
        this.hover_section.wireframe.material.color.set(w_color);
        this.hover_section = null;
      }
      if (accessory_on_scene) this.changeAccessoryColor(accessory_on_scene, true);
      return;
    }
    if (accessory_on_scene) {
      this.changeAccessoryColor(accessory_on_scene, true, true);
    }
    const hovered_section = this.sectionCanvasObjects.find((el: any) => el.section.id == section.id);
    if (!hovered_section || this.section_with_error == hovered_section.section.id) return;
    const hoverColors = this.getSectionColors(hovered_section.section.id);
    const color = hovered_section.active ? hoverColors.selected : hoverColors.hover;
    hovered_section.cube.material.color.set(color);
    hovered_section.wireframe.material.color.set(color);
    this.hover_section = hovered_section;
  }

  /**
   * hoverAccessory (canvas.view3d:4727-4729). DEAD in legacy — no caller
   * (tunnel.js binds `hover:accessory` to selectDeselectAccessory, :312) and
   * the body reads `this.accessoriesWithError[this.id]` where `this.id` is
   * the view's undefined id (latent slip). Ported for §3.2.10 surface parity;
   * do not wire.
   */
  hoverAccessory(id: any, hover: boolean): void {
    if (id && this.accessoryCanvasObjectsObj[id])
      this.changeAccessoryColor(
        this.accessoryCanvasObjectsObj[id],
        this.accessoriesWithError[(this as any).id],
        hover
      );
  }

  /**
   * selectDeselectAccessory (canvas.view3d:4730-4735) — BOTH the
   * `select:deselect:accessory` AND `hover:accessory` receiver (tunnel.js
   * :311-312 bind the same handler — verbatim quirk; the hover bool lands as
   * `select`).
   */
  selectDeselectAccessory(id: any, select: boolean): void {
    const old_selected = this.selectedAccessory;
    this.selectedAccessory = select ? id : null;
    if (this.accessoryCanvasObjectsObj[id])
      this.changeAccessoryColor(this.accessoryCanvasObjectsObj[id], this.accessoriesWithError[id]);
    if (old_selected && this.accessoryCanvasObjectsObj[old_selected])
      this.changeAccessoryColor(
        this.accessoryCanvasObjectsObj[old_selected],
        this.accessoriesWithError[old_selected],
        false
      );
  }

  /**
   * changeAccessoryColor (canvas.view3d:4736-4768) — the accessory tint
   * writer: DAMPER/LOUVER children fall back 0x828282 when not
   * hover/selected; `cube_casing` child always black; viewports excluded
   * (they follow the SECTION); HOOD maps the color over both materials. The
   * `error` arg is part of the legacy signature but unused (getAccessoryColors
   * reads the error store directly). selectedAccessory side-channel synced
   * via the §3.5 panel hooks (left-panel DOM probes re-homed).
   */
  changeAccessoryColor(accessory_on_scene: any, _error?: any, hover?: boolean, old?: boolean): void {
    if (!accessory_on_scene) return; // defensive — a missing registry entry crashes legacy too
    const accessory_id = accessory_on_scene?.accessory?.id;
    const accColors = this.getAccessoryColors(accessory_id);

    const acc_color = {
      hover: accColors.hover,
      selected: accColors.selected,
      passive:
        accColors.passive ||
        accessoriesColors[accessory_on_scene.name || accessory_on_scene.shape.name] ||
        sectionMaterialColor,
    };

    let color = acc_color.passive;
    let child_color = acc_color.passive;
    const child_casing_color = wireframeMaterialColorAccessory;
    const open =
      this.isAccessoryRowCollapsed(accessory_id) && accessory_on_scene.shape.name !== 'ACCESS_DOOR';
    if (old) this.selectedAccessory = null;
    else if (
      !open &&
      this.isAccessoriesTabActive(accessory_on_scene.section.id) &&
      accessory_on_scene.shape.name !== 'ACCESS_DOOR'
    )
      this.selectedAccessory = accessory_id;
    const isSelected = old ? false : !!(accessory_id == this.selectedAccessory);
    color = hover ? acc_color.hover : isSelected ? acc_color.selected : acc_color.passive;
    child_color = color;
    if (accessory_on_scene.shape.name === 'DAMPER' || accessory_on_scene.shape.name === 'LOUVER') {
      child_color = hover ? acc_color.hover : isSelected ? acc_color.selected : 0x828282;
    }
    accessory_on_scene.shape.material.color?.set(color);
    accessory_on_scene.shape.children?.forEach((child: any) => {
      if (child.name === 'cube_casing') child?.material?.color?.set(child_casing_color);
      else if (!child.isViewport) child?.material?.color?.set(child_color);
    });
    accessory_on_scene.wireframe.material.color?.set(color);
    if (accessory_on_scene.shape.name === 'HOOD') {
      accessory_on_scene.shape.material.map((el: any) => el.color.set(color));
    }
    // dev-sync (canvas.view3d delta @4939): tint the VFD bank/enclosure
    // extras with the same color set (the enclosure fill is opacity-0 —
    // only its wireframe shows the change).
    (accessory_on_scene.extraShapes || []).forEach(({ shape, wireframe }: any) => {
      shape.material.color?.set(color);
      shape.children?.forEach((child: any) => {
        if (!child.isViewport) child?.material?.color?.set(child_color);
      });
      wireframe.material.color?.set(color);
    });
    this.need_update = true;
  }

  /**
   * updateAccessoriesColor (canvas.view3d:4774-4788) — `accessoriesWithError`
   * receiver (§3.4 #22): diffs old/new error maps and recolors only the
   * transitions; releases section_with_error when its error-log row vanished
   * (§3.5 probe re-homed to hasErrorLogRowForSection).
   */
  updateAccessoriesColor(accessoriesWithError: Record<string, any> = {}): void {
    const prevAccessoriesWithError = { ...this.accessoriesWithError };
    this.accessoriesWithError = accessoriesWithError;
    if (this.section_with_error && !this.hasErrorLogRowForSection(this.section_with_error as number)) {
      const section_on_scene = this.sectionCanvasObjects.find(
        (section: any) => section.section.id == this.section_with_error
      );
      this.section_with_error = null;
      if (section_on_scene) this.updateSectionStyles(section_on_scene, section_on_scene.active);
    }
    const allIds = [...new Set([...Object.keys(accessoriesWithError), ...Object.keys(prevAccessoriesWithError)])];
    allIds.map((id) => {
      if (!this.accessoryCanvasObjectsObj[id] || (prevAccessoriesWithError[id] && accessoriesWithError[id])) return;
      else if (prevAccessoriesWithError[id]) this.changeAccessoryColor(this.accessoryCanvasObjectsObj[id]);
      else if (accessoriesWithError[id]) this.changeAccessoryColor(this.accessoryCanvasObjectsObj[id], true);
    });
  }
}

// ---------------------------------------------------------------------------
// R16/D4 — ACCESSORY-PREVIEW MINI-CANVAS FACTORY: moved to
// canvas3dInteraction.ts (createAccessoryPreviewCanvas) so the preview can be
// built as a real Canvas3DInteraction (hover-tooltip + drag-rotate) instead
// of a bare Canvas3DGeometry — see attachHoverInteraction there. Kept out of
// this file to avoid a Canvas3DGeometry <-> Canvas3DInteraction import cycle.
// ---------------------------------------------------------------------------
